import { intlLocale, type AppLocale } from '../stores/locale'
import { useSiteStore } from '../stores/site'

export interface ChangelogFigure {
  /** 0-based index into `items.en` / `items.es` / `items.pt`. */
  item: number
  /** Public URL, e.g. `/assets/changelog/2026-08-26-foil.webp`. */
  src: string
  alt: Record<AppLocale, string>
}

export interface ChangelogRelease {
  id: string
  /** Calendar day of the update, YYYY-MM-DD. */
  date: string
  title: Record<AppLocale, string>
  items: Record<AppLocale, string[]>
  /** Optional screenshots under the matching bullet. */
  figures?: ChangelogFigure[]
  /**
   * Staff-only (admin panel). Left out of Novedades, the desktop dialog,
   * and the pending dot.
   */
  ops?: boolean
  /** Hidden until this expansion reset is marked done. */
  afterReset?: string
}

/**
 * Player-facing notes. Newest first. Keep the wording for duelists, not for
 * the engine: no file names, no commit jargon. Skip admin-panel work (`ops`).
 * Optional `figures` illustrate visual changes (see
 * `.cursor/rules/changelog-news.mdc`).
 */
export const CHANGELOG_RELEASES: ChangelogRelease[] = [
  {
    id: '2026-11-03-event-rewards',
    date: '2026-11-03',
    title: {
      en: 'Event queue pays like ranked',
      es: 'La cola de evento paga como clasificatoria',
      pt: 'A fila de evento paga como o ranqueado',
    },
    items: {
      en: [
        'Each finished event-queue duel pays the same as ranked: **7** coins for a win and **5** for a loss or draw. Surrendering still pays nothing, and your league does not move. When the week ends, Master Packs are one band among players with a win: **20** for the top 1%, **15** for the rest of the top 10%, **8** for the rest of the top 25%, **4** for the rest of the top 50%, and **2** for the rest.',
      ],
      es: [
        'Cada duelo terminado de la cola de evento paga lo mismo que clasificatoria: **7** monedas al ganar y **5** al perder o empatar. Rendirse sigue sin pagar, y tu liga no se mueve. Al cerrar la semana, los Master Packs son una sola banda entre quienes ganaron: **20** el top 1%, **15** el resto del top 10%, **8** el resto del top 25%, **4** el resto del top 50% y **2** el resto.',
      ],
      pt: [
        'Cada duelo terminado da fila de evento paga o mesmo que o ranqueado: **7** moedas na vitória e **5** na derrota ou empate. Desistir continua sem pagar, e sua liga não muda. Quando a semana fecha, os Master Packs são uma faixa só entre quem ganhou: **20** o top 1%, **15** o resto do top 10%, **8** o resto do top 25%, **4** o resto do top 50% e **2** o resto.',
      ],
    },
  },
  {
    id: '2026-11-03-gy-glance',
    date: '2026-11-03',
    title: {
      en: 'Graveyard glance',
      es: 'Mirando el Cementerio',
      pt: 'Olhando o Cemitério',
    },
    items: {
      en: [
        'When your opponent opens a Graveyard, the same gold note as “waiting” says whether they are looking at theirs or yours. It leaves when they close it. Someone watching the duel sees that note next to the player who opened it.',
      ],
      es: [
        'Si tu rival abre un Cementerio, el mismo aviso dorado de cuando esperas dice si está viendo el suyo o el tuyo. Se va cuando lo cierra. Quien mira el duelo lo ve junto a quien lo abrió.',
      ],
      pt: [
        'Se o oponente abre um Cemitério, o mesmo aviso dourado de quando você espera diz se ele está vendo o dele ou o seu. Some quando ele fecha. Quem assiste ao duelo vê esse aviso ao lado de quem abriu.',
      ],
    },
  },
  {
    id: '2026-11-03-spectators',
    date: '2026-11-03',
    title: {
      en: 'Who is watching',
      es: 'Quién está mirando',
      pt: 'Quem está assistindo',
    },
    items: {
      en: [
        'A duel someone is watching shows their names on the card rail. On a phone it shows an eye and the count, off the field.',
      ],
      es: [
        'Si alguien mira tu duelo, sus nombres aparecen en el riel de la carta. En el teléfono se ve un ojo y el número, fuera del campo.',
      ],
      pt: [
        'Se alguém assiste ao seu duelo, os nomes aparecem no trilho da carta. No celular aparece um olho e o número, fora do campo.',
      ],
    },
  },
  {
    id: '2026-11-02-coin-quality',
    date: '2026-11-02',
    title: {
      en: 'Coin animation quality',
      es: 'Calidad de la animación de la moneda',
      pt: 'Qualidade da animação da moeda',
    },
    items: {
      en: [
        'Settings now offers High and Low coin animation, next to Animations. High is the 3D coin. Low is the same coin spinning flat, for a duel the 3D dialog was freezing. If you had the animation off, it stays on Low.',
      ],
      es: [
        'Ajustes ahora ofrece calidad Alta y Baja para la animación de la moneda, junto a Animaciones. Alta es la moneda en 3D. Baja es la misma moneda girando en plano, para un duelo que el diálogo 3D trababa. Si la tenías apagada, queda en Baja.',
      ],
      pt: [
        'Ajustes agora oferece qualidade Alta e Baixa para a animação da moeda, ao lado de Animações. Alta é a moeda em 3D. Baixa é a mesma moeda girando no plano, para um duelo que o diálogo 3D travava. Se você tinha desligado, fica em Baixa.',
      ],
    },
  },
  {
    id: '2026-11-02-event-history',
    date: '2026-11-02',
    title: {
      en: 'Event duels in history',
      es: 'Duelos de evento en el historial',
      pt: 'Duelos de evento no histórico',
    },
    items: {
      en: [
        'An event duel in your history says Event vs the other duelist, including when it has a replay.',
      ],
      es: [
        'Un duelo de evento en tu historial dice Evento contra el otro duelista, también cuando tiene repetición.',
      ],
      pt: [
        'Um duelo de evento no seu histórico diz Evento contra o outro duelista, inclusive quando tem replay.',
      ],
    },
  },
  {
    id: '2026-11-02-replay-controls',
    date: '2026-11-02',
    title: {
      en: 'Replay controls',
      es: 'Controles de la repetición',
      pt: 'Controles do replay',
    },
    items: {
      en: [
        'Replay controls are smaller icons that stay above the board, and you can drag them out of the way like the chat. The arrow button collapses them, and the same button brings them back.',
      ],
      es: [
        'Los controles de la repetición ahora son íconos más pequeños que quedan encima del tablero, y puedes arrastrarlos igual que el chat. El botón de la flecha los recoge, y ese mismo botón los vuelve a extender.',
      ],
      pt: [
        'Os controles do replay agora são ícones menores que ficam acima do tabuleiro, e você pode arrastá-los como o chat. O botão da seta os recolhe, e o mesmo botão os abre de novo.',
      ],
    },
  },
  {
    id: '2026-11-02-trap-master-swords',
    date: '2026-11-02',
    title: {
      en: 'Trap Master after Swords',
      es: 'Trap Master tras Espadas',
      pt: 'Trap Master depois de Swords',
    },
    items: {
      en: [
        'Vs AI no longer freezes when **Swords of Revealing Light** flips the CPU\'s Trap Master and that monster has to choose one of two Set cards.',
      ],
      es: [
        'Contra la IA ya no se congela el duelo cuando **Swords of Revealing Light** voltea el Trap Master del bot y esa carta tiene que elegir una de dos cartas boca abajo.',
      ],
      pt: [
        'Contra a IA o duelo não trava mais quando **Swords of Revealing Light** vira o Trap Master do bot e esse monstro precisa escolher uma de duas cartas baixadas.',
      ],
    },
  },
  {
    id: '2026-11-02-event-queue-race',
    date: '2026-11-02',
    title: {
      en: 'Race in the event queue',
      es: 'Carrera en la cola de evento',
      pt: 'Corrida na fila de evento',
    },
    items: {
      en: [
        'The Dark Magician Girl race now pays coins and daily race missions while you wait in the **event queue**, the same as Quick Match and casual. You do not need to enter a duel for those jumps to count.',
      ],
      es: [
        'La carrera de Dark Magician Girl ahora paga monedas y las misiones diarias de carrera mientras esperas en la **cola de evento**, igual que en clasificatoria y casual. No hace falta entrar a un duelo para que cuenten los saltos.',
      ],
      pt: [
        'A corrida da Dark Magician Girl agora paga moedas e as missões diárias de corrida enquanto você espera na **fila de evento**, igual ao ranqueado e ao casual. Não precisa entrar em um duelo para os pulos contarem.',
      ],
    },
  },
  {
    id: '2026-10-28-event-queue-coins',
    date: '2026-10-28',
    title: {
      en: 'Event queue coins',
      es: 'Monedas en la cola de evento',
      pt: 'Moedas na fila de evento',
    },
    items: {
      en: [
        'A finished event-queue duel now pays coins: **4** for a win, **2** for a loss or draw. XP is the same as ranked. Surrendering still pays nothing. The weekly Master Packs at the end of the week stay the same.',
      ],
      es: [
        'Un duelo terminado de la cola de evento ahora paga monedas: **4** al ganar, **2** al perder o empatar. La XP es la misma que en clasificatoria. Rendirse sigue sin pagar. Los Master Packs de fin de semana no cambian.',
      ],
      pt: [
        'Um duelo terminado da fila de evento agora paga moedas: **4** na vitória, **2** na derrota ou empate. O XP é o mesmo do ranqueado. Desistir continua sem pagar. Os Master Packs do fim da semana não mudam.',
      ],
    },
  },
  {
    id: '2026-10-27-ghost-chase',
    date: '2026-10-27',
    title: {
      en: 'Ghost Rare odds',
      es: 'Probabilidad de Ghost Rare',
      pt: 'Chance de Ghost Rare',
    },
    items: {
      en: [
        '**Ghost Rare** now lands as often as Helios, Nightglass, and Stormwake. It is no longer as common as Ultra.',
      ],
      es: [
        '**Ghost Rare** ahora sale con la misma frecuencia que Helios, Nightglass y Stormwake. Ya no es tan común como Ultra.',
      ],
      pt: [
        '**Ghost Rare** agora sai com a mesma frequência que Helios, Nightglass e Stormwake. Não é mais tão comum quanto Ultra.',
      ],
    },
  },
  {
    id: '2026-10-27-event-queues',
    date: '2026-10-27',
    title: {
      en: 'Weekly event queues',
      es: 'Colas de evento semanales',
      pt: 'Filas de evento semanais',
    },
    items: {
      en: [
        'A rotating event queue can open for a week. One mode at a time. **Wins** go on a public board in Rank, under Event. Ranked and casual do not change. When the week ends, everyone with a win gets Master Packs: **10** for the top 5, **5** for 6th–10th, **1** from 11th on.',
        '**Random deck:** each duel gives you a new Standard list. You do not pick a deck.',
        '**Unlimited:** your cards, three copies of any name, no house banlist. 40–60 in the Main.',
        '**60 · one copy:** your cards, at least 60 in the Main, and only one copy of each name.',
        'While an event is live, a wide card sits at the top of Home with the mode and the countdown.',
      ],
      es: [
        'Una cola de evento rotativa puede abrirse por una semana. Un modo a la vez. Las **victorias** van al ranking en Mejores jugadores, en Evento. Clasificatoria y casual no cambian. Al cerrar la semana, quien haya ganado un duelo recibe Master Packs: **10** para el top 5, **5** del 6.º al 10.º, **1** del 11.º en adelante.',
        '**Baraja aleatoria:** cada duelo te da una lista Standard nueva. No eliges baraja.',
        '**Unlimited:** tus cartas, tres copias de cualquier nombre, sin banlist de casa. 40–60 en el Main.',
        '**60 · una copia:** tus cartas, al menos 60 en el Main, y solo una copia de cada nombre.',
        'Mientras hay un evento en vivo, una tarjeta ancha va primero en Inicio, con el modo y la cuenta atrás.',
      ],
      pt: [
        'Uma fila de evento rotativa pode abrir por uma semana. Um modo por vez. As **vitórias** entram no ranking em Melhores jogadores, em Evento. Ranqueado e casual não mudam. Quando a semana fecha, quem ganhou um duelo recebe Master Packs: **10** para o top 5, **5** do 6º ao 10º, **1** do 11º em diante.',
        '**Baralho aleatório:** cada duelo te dá uma lista Standard nova. Você não escolhe o baralho.',
        '**Unlimited:** suas cartas, três cópias de qualquer nome, sem banlist da casa. 40–60 no Main.',
        '**60 · uma cópia:** suas cartas, pelo menos 60 no Main, e só uma cópia de cada nome.',
        'Enquanto um evento está ao vivo, um cartão largo fica no topo de Início, com o modo e a contagem.',
      ],
    },
  },
  {
    id: '2026-10-27-cpu-change-of-heart',
    date: '2026-10-27',
    title: {
      en: 'CPU attacks with Change of Heart',
      es: 'La CPU ataca con Change of Heart',
      pt: 'A CPU ataca com Change of Heart',
    },
    items: {
      en: [
        'When the CPU takes a monster with **Change of Heart**, it attacks with that body instead of switching it to Defense. A stolen Summoned Skull now swings.',
      ],
      es: [
        'Cuando la CPU toma un monstruo con **Change of Heart**, ataca con ese cuerpo en vez de pasarlo a Defensa. Un Summoned Skull robado ahora golpea.',
      ],
      pt: [
        'Quando a CPU toma um monstro com **Change of Heart**, ela ataca com esse corpo em vez de passá-lo para Defesa. Um Summoned Skull roubado agora golpeia.',
      ],
    },
  },
  {
    id: '2026-10-27-robbin-goblin-set',
    date: '2026-10-27',
    title: {
      en: 'Robbin\' Goblin on a Set Defense',
      es: 'Robbin\' Goblin en una Defensa colocada',
      pt: 'Robbin\' Goblin numa Defesa baixada',
    },
    items: {
      en: [
        'In **Minimal** and **Auto**, a Set **Robbin\' Goblin** asks when the opponent attacks a face-down Defense monster you control, if that battle would deal damage back. Activate it then — before the Damage Step — and they still discard after the rebound. **All** already offered it on that attack.',
      ],
      es: [
        'En **Mínimo** y **Auto**, un **Robbin\' Goblin** colocado pregunta cuando el rival ataca un monstruo tuyo boca abajo en Defensa, si esa batalla devolvería daño. Actívalo ahí — antes del Damage Step — y el descarte sigue después del rebote. **Todo** ya lo ofrecía en ese ataque.',
      ],
      pt: [
        'Em **Mínimo** e **Auto**, um **Robbin\' Goblin** baixado pergunta quando o oponente ataca um monstro seu virado para baixo em Defesa, se essa batalha devolveria dano. Ative aí — antes do Damage Step — e o descarte continua depois do ricochete. **Tudo** já oferecia nesse ataque.',
      ],
    },
  },
  {
    id: '2026-10-26-picker-rail',
    date: '2026-10-26',
    title: {
      en: 'Card lists no longer hide under the side panel',
      es: 'Las listas de cartas ya no quedan bajo el panel',
      pt: 'Listas de cards não ficam mais sob o painel',
    },
    items: {
      en: [
        'Monster Reborn, Big Eye, and other pick lists now hide the right-hand card panel while they are open, so every card stays tappable. On a phone you can also tuck that panel away yourself.',
      ],
      es: [
        'Monster Reborn, Big Eye y las demás listas ahora ocultan el panel de la derecha mientras están abiertas, para que puedas tocar cada carta. En el teléfono también puedes guardar ese panel tú mismo.',
      ],
      pt: [
        'Monster Reborn, Big Eye e as outras listas agora escondem o painel da direita enquanto estão abertas, para você tocar em cada card. No celular você também pode guardar esse painel.',
      ],
    },
  },
  {
    id: '2026-10-26-big-eye-picker',
    date: '2026-10-26',
    title: {
      en: 'Big Eye lets you finish the 5th card',
      es: 'Big Eye ya deja elegir la quinta carta',
      pt: 'Big Eye deixa você escolher o 5º card',
    },
    items: {
      en: [
        'After Big Eye flips, you can order all five cards on a phone. The last one fills in once four are chosen, and Confirm stays on screen.',
      ],
      es: [
        'Cuando Big Eye se voltea, puedes ordenar las cinco cartas en el teléfono. La última se completa al elegir cuatro, y Confirmar se queda a la vista.',
      ],
      pt: [
        'Quando Big Eye vira, você consegue ordenar os cinco cards no celular. O último completa sozinho depois de quatro, e Confirmar fica visível.',
      ],
    },
  },
  {
    id: '2026-10-25-tournament-podium',
    date: '2026-10-25',
    title: {
      en: 'A podium of tournament champions',
      es: 'Un podio de campeones de torneo',
      pt: 'Um pódio de campeões de torneio',
    },
    items: {
      en: [
        'The tournament history page now shows the three duelists with the most tournament wins, with their portrait, frame, and name.',
      ],
      es: [
        'La página de historial de torneos ahora muestra a los tres duelistas con más torneos ganados, con su retrato, marco y nombre.',
      ],
      pt: [
        'A página de histórico de torneios agora mostra os três duelistas com mais torneios ganhos, com o retrato, a moldura e o nome.',
      ],
    },
  },
  {
    id: '2026-10-25-stern-mystic-peek',
    date: '2026-10-25',
    title: {
      en: 'The Stern Mystic no longer traps the board',
      es: 'The Stern Mystic ya no traba el tablero',
      pt: 'The Stern Mystic não trava mais o campo',
    },
    items: {
      en: [
        'After The Stern Mystic shows the Set cards, tap a card, the dark background, or Close to keep playing. Close stays on screen even when the list is long.',
      ],
      es: [
        'Cuando The Stern Mystic muestra las cartas Set, toca una carta, el fondo oscuro o Cerrar para seguir jugando. Cerrar se queda a la vista aunque la lista sea larga.',
      ],
      pt: [
        'Quando The Stern Mystic mostra os cards Set, toque em um card, no fundo escuro ou em Fechar para continuar. Fechar fica visível mesmo se a lista for longa.',
      ],
    },
  },
  {
    id: '2026-10-25-tournament-bell-off',
    date: '2026-10-25',
    title: {
      en: 'Turn tournament notices off from the bell',
      es: 'Apaga los avisos del torneo desde la campana',
      pt: 'Desligue os avisos do torneio pelo sino',
    },
    items: {
      en: [
        'The bell on the tournament card stays visible after you allow notices. Tap the slashed bell to stop them on this device. You can turn them back on the same way, or from Settings.',
      ],
      es: [
        'La campana de la tarjeta del torneo sigue visible cuando ya permitiste los avisos. Toca la campana tachada para apagarlos en este dispositivo. Puedes volver a activarlos igual, o desde Ajustes.',
      ],
      pt: [
        'O sino do cartão do torneio continua visível depois que você permite os avisos. Toque no sino riscado para desligá-los neste aparelho. Você pode ligá-los de novo do mesmo jeito, ou em Ajustes.',
      ],
    },
  },
  {
    id: '2026-10-25-pack-complete',
    date: '2026-10-25',
    title: {
      en: 'Packs show how complete they are',
      es: 'Los sobres muestran cuánto llevas',
      pt: 'Os pacotes mostram o quanto você já tem',
    },
    items: {
      en: [
        'Each pack now shows the percent of its cards you already have, plus how many you own out of the list. Open Cards to see which ones are still missing.',
      ],
      es: [
        'Cada sobre ahora muestra el porcentaje de sus cartas que ya tienes, y cuántas llevas de la lista. Abre Cartas para ver cuáles te faltan.',
      ],
      pt: [
        'Cada pacote agora mostra a porcentagem dos cards que você já tem, e quantos você tem da lista. Abra Cartas para ver quais ainda faltam.',
      ],
    },
  },
  {
    id: '2026-10-25-tab-board',
    date: '2026-10-25',
    title: {
      en: 'The board comes back when you leave the tab',
      es: 'El campo vuelve si sales de la pestaña',
      pt: 'O campo volta se você sair da aba',
    },
    items: {
      en: [
        'Leaving the duel to check another tab or window no longer blanks the field. Cards stay clickable when you come back.',
      ],
      es: [
        'Salir del duelo a otra pestaña o ventana ya no deja el campo en blanco. Al volver puedes elegir cartas otra vez.',
      ],
      pt: [
        'Sair do duelo para outra aba ou janela não deixa mais o campo em branco. Ao voltar, as cartas continuam clicáveis.',
      ],
    },
  },
  {
    id: '2026-10-25-dice-shorter',
    date: '2026-10-25',
    title: {
      en: 'The dice roll is shorter',
      es: 'La tirada de dados es más corta',
      pt: 'A rolagem de dados é mais curta',
    },
    items: {
      en: [
        'Before a duel the dice roll on their own. There is no roll button. The first-turn card stays a moment longer; tap it to skip.',
      ],
      es: [
        'Antes del duelo los dados se tiran solos. Ya no hay botón de tirar. La tarjeta de quién empieza se queda un momento más; tócala para saltarla.',
      ],
      pt: [
        'Antes do duelo os dados rolam sozinhos. Não tem mais botão de rolar. O cartão de quem começa fica um pouco mais; toque para pular.',
      ],
    },
  },
  {
    id: '2026-10-24-keep-progress',
    date: '2026-10-24',
    title: {
      en: 'Your cards stay',
      es: 'Tus cartas se quedan',
      pt: 'Suas cartas ficam',
    },
    items: {
      en: [
        'There will be no more resets of cards, coins, or packs. Everything stays. The only thing that resets is the ranking each season.',
        'The next set is **Spell Ruler**. We are focused on it, and it should be ready in about 3 weeks.',
      ],
      es: [
        'No va a haber más resets de cartas, monedas ni sobres. Se conserva todo. Lo único que se reinicia son los rankings de cada temporada.',
        'El siguiente set es **Spell Ruler**. Estamos enfocados trabajando en eso, y aproximadamente en 3 semanas estará listo.',
      ],
      pt: [
        'Não vai ter mais reset de cartas, moedas nem pacotes. Tudo fica. A única coisa que reinicia é o ranking de cada temporada.',
        'O próximo set é **Spell Ruler**. Estamos focados nisso, e em cerca de 3 semanas deve estar pronto.',
      ],
    },
  },
  {
    id: '2026-10-23-tournament-faq',
    date: '2026-10-23',
    title: {
      en: 'How to sign up for a tournament',
      es: 'Cómo inscribirse al torneo',
      pt: 'Como se inscrever no torneio',
    },
    items: {
      en: [
        'The FAQ now answers how automatic tournaments work, starting with how to sign up. Until the button appears, the tournament card says you can sign up in the last 30 minutes.',
      ],
      es: [
        'El FAQ ahora responde cómo funcionan los torneos automáticos, empezando por cómo inscribirse. Hasta que aparezca el botón, la tarjeta del torneo dice que podrás inscribirte en los últimos 30 minutos.',
      ],
      pt: [
        'O FAQ agora responde como funcionam os torneios automáticos, começando por como se inscrever. Até o botão aparecer, o cartão do torneio diz que você poderá se inscrever nos últimos 30 minutos.',
      ],
    },
  },
  {
    id: '2026-10-23-coin-toss',
    date: '2026-10-23',
    title: {
      en: 'The coin toss can stay quiet',
      es: 'La moneda puede quedarse quieta',
      pt: 'A moeda pode ficar quieta',
    },
    items: {
      en: [
        'Settings has a coin toss animation switch, on by default, next to Animations. Turn it off and a coin card shows the result and the Life Point change without the 3D dialog.',
      ],
      es: [
        'Ajustes tiene un interruptor de la animación de la moneda, encendido por defecto, junto a Animaciones. Apágalo y una carta de moneda muestra el resultado y el cambio de Life Points sin el diálogo 3D.',
      ],
      pt: [
        'Ajustes tem um interruptor da animação da moeda, ligado por padrão, ao lado de Animações. Desligue e um card de moeda mostra o resultado e a mudança de Life Points sem o diálogo 3D.',
      ],
    },
  },
  {
    id: '2026-10-23-tournament-bell',
    date: '2026-10-23',
    title: {
      en: 'A bell on the tournament card',
      es: 'Una campana en la tarjeta del torneo',
      pt: 'Um sino no cartão do torneio',
    },
    items: {
      en: [
        'The tournament card has a bell. Tap it to allow tournament notices. On a computer, confirm the permission in the address bar. The bell goes away once this device allows them.',
      ],
      es: [
        'La tarjeta del torneo tiene una campana. Tócala para permitir los avisos del torneo. En la computadora, confirma el permiso en la barra de direcciones. La campana desaparece cuando este dispositivo ya los permite.',
      ],
      pt: [
        'O cartão do torneio tem um sino. Toque nele para permitir os avisos do torneio. No computador, confirme a permissão na barra de endereço. O sino some quando este aparelho já os permite.',
      ],
    },
  },
  {
    id: '2026-10-23-banlist-exodia-soul',
    date: '2026-10-23',
    title: {
      en: 'Exodia and Soul Exchange go to 3',
      es: 'Exodia y Soul Exchange vuelven a 3',
      pt: 'Exodia e Soul Exchange voltam a 3',
    },
    items: {
      en: [
        'Soul Exchange and the Exodia pieces leave the banlist. You can play 3 copies of each.',
      ],
      es: [
        'Soul Exchange y las piezas de Exodia salen de la banlist. Puedes llevar 3 copias de cada una.',
      ],
      pt: [
        'Soul Exchange e as peças de Exodia saem da banlist. Você pode levar 3 cópias de cada uma.',
      ],
    },
  },
  {
    id: '2026-10-23-banlist-semi-to-one',
    date: '2026-10-23',
    title: {
      en: 'Eight cards move to 1',
      es: 'Ocho cartas pasan a 1',
      pt: 'Oito cards passam para 1',
    },
    items: {
      en: [
        'Man-Eater Bug, Wall of Illusion, Fissure, La Jinn the Mystical Genie of the Lamp, Solemn Judgment, Seven Tools of the Bandit, Magician of Faith, and Jirai Gumo move from 2 to 1. Cut the extra copies before you start a duel.',
      ],
      es: [
        'Man-Eater Bug, Wall of Illusion, Fissure, La Jinn the Mystical Genie of the Lamp, Solemn Judgment, Seven Tools of the Bandit, Magician of Faith y Jirai Gumo pasan de 2 a 1. Baja las copias de más antes de empezar un duelo.',
      ],
      pt: [
        'Man-Eater Bug, Wall of Illusion, Fissure, La Jinn the Mystical Genie of the Lamp, Solemn Judgment, Seven Tools of the Bandit, Magician of Faith e Jirai Gumo passam de 2 para 1. Reduza as cópias a mais antes de começar um duelo.',
      ],
    },
  },
  {
    id: '2026-10-23-forbidden-cards-poll',
    date: '2026-10-23',
    title: {
      en: 'A poll on forbidden cards',
      es: 'Una encuesta sobre cartas prohibidas',
      pt: 'Uma enquete sobre cards proibidos',
    },
    items: {
      en: [
        'Home asks whether you want forbidden cards now, or only limited cards for the time being. A forbidden card cannot be played: 0 copies in the deck. Each option shows a bar with the votes so far. The tournament poll is gone. You can still leave a comment.',
      ],
      es: [
        'Inicio pregunta si quieres cartas prohibidas ya, o si por ahora seguimos solo con cartas limitadas. Una carta prohibida no se puede jugar: 0 copias en la baraja. Cada opción muestra una barra con los votos de ahora. La encuesta de los torneos ya no está. Puedes dejar un comentario.',
      ],
      pt: [
        'O Início pergunta se você quer cards proibidos agora, ou se por enquanto seguimos só com cards limitados. Um card proibido não pode ser jogado: 0 cópias no baralho. Cada opção mostra uma barra com os votos de agora. A enquete dos torneios saiu. Você ainda pode deixar um comentário.',
      ],
    },
  },
  {
    id: '2026-10-23-set-pulse',
    date: '2026-10-23',
    title: {
      en: 'Set cards pulse again on a phone',
      es: 'Las Colocadas vuelven a destellar en el teléfono',
      pt: 'As Colocadas voltam a piscar no celular',
    },
    items: {
      en: [
        'Your Set cards pulse on a phone again, so you can tell them apart. They no longer stay face-up.',
      ],
      es: [
        'Tus cartas Colocadas vuelven a destellar en el teléfono, para que distingas cuál es cuál. Ya no se quedan boca arriba.',
      ],
      pt: [
        'Suas cartas Colocadas voltam a piscar no celular, para você distinguir qual é qual. Elas não ficam mais viradas para cima.',
      ],
    },
  },
  {
    id: '2026-10-23-phone-sleeves',
    date: '2026-10-23',
    title: {
      en: 'Card backs stay visible on a phone',
      es: 'Los dorsos se ven en el teléfono',
      pt: 'Os dorsos ficam visíveis no celular',
    },
    items: {
      en: [
        'On a phone, the opponent\'s hand and the Graveyard and Fusion piles stay visible while a sleeve image is still loading.',
      ],
      es: [
        'En el teléfono, la mano del rival y las pilas de Cementerio y Fusión se ven aunque el dorso tarde.',
      ],
      pt: [
        'No celular, a mão do oponente e as pilhas do Cemitério e da Fusão continuam visíveis mesmo se o dorso demorar.',
      ],
    },
  },
  {
    id: '2026-10-23-tournament-seats',
    date: '2026-10-23',
    title: {
      en: 'Tournaments fill eight seats',
      es: 'Los torneos son de ocho plazas',
      pt: 'Os torneios são de oito vagas',
    },
    items: {
      en: [
        'A tournament has eight seats. It starts as soon as those seats are full, or at the published time if they are not. Fewer than four players and it is called off.',
      ],
      es: [
        'Un torneo tiene ocho plazas. Arranca apenas se llenan, o a la hora publicada si no se llenaron. Con menos de cuatro duelistas se cancela.',
      ],
      pt: [
        'Um torneio tem oito vagas. Começa assim que elas lotam, ou na hora publicada se não lotarem. Com menos de quatro duelistas, é cancelado.',
      ],
    },
  },
  {
    id: '2026-10-23-tournament-push',
    date: '2026-10-23',
    title: {
      en: 'Tournament notices also arrive with the site closed',
      es: 'Los avisos del torneo también llegan con el sitio cerrado',
      pt: 'Os avisos do torneio também chegam com o site fechado',
    },
    items: {
      en: [
        'If you allow notifications, the same tournament notices arrive even when Dueling Chaos is closed: 2 hours, 1 hour, when sign-up opens, 5 minutes, 1 minute, and when your duel is ready.',
      ],
      es: [
        'Si aceptas los avisos, también te llegan con el sitio cerrado: a las 2 horas, a la 1 hora, cuando abre la inscripción, a los 5 minutos, al minuto, y cuando tu duelo está listo.',
      ],
      pt: [
        'Se você aceitar os avisos, eles também chegam com o site fechado: em 2 horas, em 1 hora, quando a inscrição abre, em 5 minutos, em 1 minuto, e quando sua partida está pronta.',
      ],
    },
  },
  {
    id: '2026-10-23-tournament-countdown',
    date: '2026-10-23',
    title: {
      en: 'The site counts down to the tournament',
      es: 'El sitio avisa cuánto falta para el torneo',
      pt: 'O site avisa quanto falta para o torneio',
    },
    items: {
      en: [
        'A notice appears when a tournament is 2 hours away, 1 hour away, 5 minutes away, and 1 minute away. Sign-up still opens with its own notice at 30 minutes. At 5 minutes and at 1 minute, that notice opens sign-up if you are not in yet.',
      ],
      es: [
        'Aparece un aviso cuando faltan 2 horas, 1 hora, 5 minutos y 1 minuto para un torneo. La inscripción sigue abriendo con su propio aviso a los 30 minutos. A los 5 minutos y al minuto, el aviso te inscribe si todavía no entraste.',
      ],
      pt: [
        'Aparece um aviso quando faltam 2 horas, 1 hora, 5 minutos e 1 minuto para um torneio. A inscrição continua abrindo com o próprio aviso aos 30 minutos. Aos 5 minutos e no último minuto, o aviso te inscreve se você ainda não entrou.',
      ],
    },
  },
  {
    id: '2026-10-23-bracket-marks',
    date: '2026-10-23',
    title: {
      en: 'The bracket marks how a match was won',
      es: 'El cuadro marca cómo se ganó',
      pt: 'A chave marca como a partida foi vencida',
    },
    items: {
      en: [
        'Next to the winner, a green trophy means the duel was played. A green clock means they won because the other player missed the 5-minute check-in. A red clock sits on anyone who did not enter the room. A played duel does not get a clock. A bye seat shows a red exit.',
      ],
      es: [
        'Junto al ganador, una copa verde significa que se jugó el duelo. Un reloj verde significa que ganó porque el otro no entró en los 5 minutos. El reloj rojo queda en quien no entró a la sala. Un duelo jugado no lleva reloj. El puesto que descansa muestra una salida roja.',
      ],
      pt: [
        'Ao lado de quem venceu, um troféu verde significa que o duelo foi jogado. Um relógio verde significa que venceu porque o outro não entrou nos 5 minutos. O relógio vermelho fica em quem não entrou na sala. Um duelo jogado não leva relógio. A vaga de folga mostra uma saída vermelha.',
      ],
    },
  },
  {
    id: '2026-10-23-pack-buy-fail',
    date: '2026-10-23',
    title: {
      en: 'The shop says why a pack did not sell',
      es: 'La tienda dice por qué no se vendió el sobre',
      pt: 'A loja diz por que o pacote não foi vendido',
    },
    items: {
      en: [
        'If a pack cannot be bought, a notice says so. Not enough coins, too many purchases, or a failed try each get their own line. A purchase that goes through uses the same notice.',
      ],
      es: [
        'Si un sobre no se puede comprar, aparece un aviso. Faltan monedas, hay demasiadas compras o el intento falló: cada caso tiene su propia línea. Una compra que sí entra usa el mismo aviso.',
      ],
      pt: [
        'Se um pacote não puder ser comprado, aparece um aviso. Moedas insuficientes, compras demais ou uma tentativa que falhou: cada caso tem a própria linha. Uma compra que entra usa o mesmo aviso.',
      ],
    },
  },
  {
    id: '2026-10-23-bracket-advance',
    date: '2026-10-23',
    title: {
      en: 'The bracket shows who moved on',
      es: 'El cuadro muestra quién pasó',
      pt: 'A chave mostra quem passou',
    },
    items: {
      en: [
        'Whoever wins a tournament match is drawn in the next round, including the final, even when the other match has no winner. That seat says bye instead of staying blank.',
      ],
      es: [
        'Quien gana un partido del torneo queda dibujado en la ronda siguiente, incluida la final, aunque el otro partido no tenga ganador. Ese puesto dice que descansa, en vez de quedar en blanco.',
      ],
      pt: [
        'Quem ganha uma partida do torneio aparece na rodada seguinte, inclusive na final, mesmo quando a outra partida não tem vencedor. Essa vaga fica como folga, em vez de ficar em branco.',
      ],
    },
  },
  {
    id: '2026-10-23-picker-copy',
    date: '2026-10-23',
    title: {
      en: 'Those lists say what to pick',
      es: 'Esas listas dicen qué elegir',
      pt: 'Essas listas dizem o que escolher',
    },
    items: {
      en: [
        'Tribute to the Doomed now asks you to discard 1 card. The Cheerful Coffin, Magic Jammer, Soul Release, and a few other lists name the choice too. That line used to be an empty key.',
      ],
      es: [
        'Tribute to the Doomed ahora te pide descartar 1 carta. The Cheerful Coffin, Magic Jammer, Soul Release y otras listas también dicen qué elegir. Antes esa línea era una clave vacía.',
      ],
      pt: [
        'Tribute to the Doomed agora pede para descartar 1 card. The Cheerful Coffin, Magic Jammer, Soul Release e outras listas também dizem o que escolher. Antes essa linha era uma chave vazia.',
      ],
    },
  },
  {
    id: '2026-10-22-tournament-format-poll',
    date: '2026-10-22',
    title: {
      en: 'A poll on tournament matches',
      es: 'Una encuesta sobre los torneos',
      pt: 'Uma enquete sobre os torneios',
    },
    items: {
      en: [
        'Home asks whether tournaments should be best of 3, stay a single duel, or wait until the card pool is larger. The 60-card deck poll is gone. You can still leave a comment.',
      ],
      es: [
        'Inicio pregunta si los torneos deberían ser al mejor de 3, si seguimos con un solo duelo, o si esperamos a que el pool de cartas sea más grande. La encuesta de las barajas de 60 ya no está. Puedes dejar un comentario.',
      ],
      pt: [
        'O Início pergunta se os torneios devem ser melhor de 3, se seguimos com um duelo só, ou se esperamos o pool de cartas ficar maior. A enquete dos baralhos de 60 saiu. Você ainda pode deixar um comentário.',
      ],
    },
  },
  {
    id: '2026-10-22-robbin-goblin-discard',
    date: '2026-10-22',
    title: {
      en: 'Robbin\' Goblin discards after the hit',
      es: 'Robbin\' Goblin descarta después del golpe',
      pt: 'Robbin\' Goblin descarta depois do golpe',
    },
    items: {
      en: [
        'When **Robbin\' Goblin** or **White Magical Hat** makes the opponent discard, that card stays in the hand through the attack and leaves after the damage.',
      ],
      es: [
        'Cuando **Robbin\' Goblin** o **White Magical Hat** hacen descartar al rival, esa carta sigue en la mano durante el ataque y se va después del daño.',
      ],
      pt: [
        'Quando **Robbin\' Goblin** ou **White Magical Hat** fazem o oponente descartar, essa carta fica na mão durante o ataque e sai depois do dano.',
      ],
    },
  },
  {
    id: '2026-10-22-tournament-deck-order',
    date: '2026-10-22',
    title: {
      en: 'Tournament decks, in order',
      es: 'Barajas del torneo, en orden',
      pt: 'Baralhos do torneio, em ordem',
    },
    items: {
      en: [
        'In the tournament history, a deck is shown sorted even if it was saved unsorted: monsters by level, then spells, then traps.',
      ],
      es: [
        'En el historial del torneo, la baraja se muestra ordenada aunque se haya guardado sin orden: monstruos por nivel, luego mágicas y luego trampas.',
      ],
      pt: [
        'No histórico do torneio, o baralho aparece ordenado mesmo se foi salvo sem ordem: monstros por nível, depois magias e depois armadilhas.',
      ],
    },
  },
  {
    id: '2026-10-22-tournament-deck',
    date: '2026-10-22',
    title: {
      en: 'Your tournament deck stays put',
      es: 'La baraja del torneo se queda',
      pt: 'O baralho do torneio fica',
    },
    items: {
      en: [
        'Signing up asks which deck you will play. That deck cannot be edited until the tournament ends or you leave sign-up. Recycling stays closed while you are signed up. You can still use that deck in other duels. Sign-up closes 30 seconds before the start. If the list is still open then, you enter with the deck you use in duels.',
      ],
      es: [
        'Al inscribirte eliges la baraja con la que vas a jugar. Esa baraja no se puede editar hasta que termine el torneo o salgas de la inscripción. Reciclar queda cerrado mientras estés inscrito. Puedes seguir usándola en otros duelos. La inscripción cierra 30 segundos antes de empezar. Si la lista sigue abierta en ese momento, entras con la baraja que usas en duelos.',
      ],
      pt: [
        'Na inscrição você escolhe o baralho com que vai jogar. Esse baralho não pode ser editado até o torneio terminar ou você sair da inscrição. Reciclar fica fechado enquanto você estiver inscrito. Dá para continuar usando em outros duelos. A inscrição fecha 30 segundos antes de começar. Se a lista ainda estiver aberta nesse momento, você entra com o baralho que usa nos duelos.',
      ],
    },
  },
  {
    id: '2026-10-22-tournament-prizes',
    date: '2026-10-22',
    title: {
      en: 'Tournament prizes',
      es: 'Premios del torneo',
      pt: 'Prêmios do torneio',
    },
    items: {
      en: [
        'When a tournament duel ends, the dialog no longer offers another match. The prizes arrive in your mailbox when the tournament ends. The button takes you home.',
      ],
      es: [
        'Al terminar un duelo de torneo, el diálogo ya no ofrece jugar de nuevo. Los premios llegan a tu buzón cuando el torneo termina. El botón te lleva al inicio.',
      ],
      pt: [
        'Quando um duelo de torneio termina, o diálogo não oferece jogar de novo. Os prêmios chegam no correio quando o torneio acaba. O botão leva você ao início.',
      ],
    },
  },
  {
    id: '2026-10-22-barrel-target',
    date: '2026-10-22',
    title: {
      en: 'Barrel Dragon',
      es: 'Barrel Dragon',
      pt: 'Barrel Dragon',
    },
    items: {
      en: [
        'Barrel Dragon asks you to choose the opposing monster first. The three coins are tossed after that choice. Two or three heads destroys that monster.',
      ],
      es: [
        'Barrel Dragon te pide elegir el monstruo del rival primero. Las tres monedas se lanzan después de esa elección. Dos o tres caras destruyen ese monstruo.',
      ],
      pt: [
        'Barrel Dragon pede para escolher o monstro do oponente primeiro. As três moedas são lançadas depois dessa escolha. Duas ou três caras destroem esse monstro.',
      ],
    },
  },
  {
    id: '2026-10-22-sleeves-sharp',
    date: '2026-10-22',
    title: {
      en: 'Sharper sleeves',
      es: 'Fundas más nítidas',
      pt: 'Capas mais nítidas',
    },
    items: {
      en: [
        'Card backs are as sharp as the faces. Image quality and foil shine start at the maximum. You can lower either one if the phone needs it. Refresh the page to see a change.',
      ],
      es: [
        'El reverso de las cartas se ve tan nítido como la cara. La calidad de imagen y el brillo del foil empiezan al máximo. Si el teléfono lo necesita, puedes bajarlos. Actualiza la página para ver un cambio.',
      ],
      pt: [
        'O verso dos cards fica tão nítido quanto a face. A qualidade da imagem e o brilho do foil começam no máximo. Se o celular precisar, você pode baixar os dois. Atualize a página para ver uma mudança.',
      ],
    },
  },
  {
    id: '2026-10-22-auto-deck',
    date: '2026-10-22',
    title: {
      en: 'Auto-build follows the banlist',
      es: 'El armado automático sigue la banlist',
      pt: 'A montagem automática segue a banlist',
    },
    items: {
      en: [
        'Auto-build stays inside the banlist and fills the deck with cards that win more often this season.',
      ],
      es: [
        'Armar automático se queda dentro de la banlist y llena la baraja con las cartas que ganan más seguido esta temporada.',
      ],
      pt: [
        'Montar sozinho fica dentro da banlist e completa o baralho com os cards que ganham mais nesta temporada.',
      ],
    },
  },
  {
    id: '2026-10-22-board-detail',
    date: '2026-10-22',
    title: {
      en: 'Sharper cards and ATK / DEF',
      es: 'Cartas y ATK / DEF más nítidos',
      pt: 'Cards e ATK / DEF mais nítidos',
    },
    items: {
      en: [
        'Cards and the ATK and DEF numbers on the board are sharper. Settings has Image quality: Sharp is the starting choice. Standard asks less of a phone and applies on the next duel.',
      ],
      es: [
        'Las cartas y los números de ATK y DEF del tablero se ven más nítidos. En Ajustes está Calidad de imagen: Nítida es la que viene elegida. Estándar pide menos al teléfono y se aplica en el siguiente duelo.',
      ],
      pt: [
        'Os cards e os números de ATK e DEF do campo ficam mais nítidos. Em Ajustes está Qualidade da imagem: Nítida é a que já vem escolhida. Padrão pede menos do celular e vale no próximo duelo.',
      ],
    },
  },
  {
    id: '2026-10-22-friend-notices',
    date: '2026-10-22',
    title: {
      en: 'Friend requests and private chats',
      es: 'Solicitudes de amistad y chats privados',
      pt: 'Pedidos de amizade e chats privados',
    },
    items: {
      en: [
        'A friend request shows a notice, and so does an acceptance. The count stays on your name in the bar, and on the menu on a phone.',
        'A private message opens its own chat tab. The number on the chat button matches a tab you can open.',
      ],
      es: [
        'Una solicitud de amistad muestra un aviso, y también cuando la aceptan. El número queda en tu nombre, en la barra, y en el menú del teléfono.',
        'Un mensaje privado abre su pestaña en el chat. El número del botón de chat corresponde a una pestaña que puedes abrir.',
      ],
      pt: [
        'Um pedido de amizade mostra um aviso, e também quando aceitam. O número fica no seu nome, na barra, e no menu do telefone.',
        'Uma mensagem privada abre a própria aba no chat. O número do botão de chat corresponde a uma aba que você pode abrir.',
      ],
    },
  },
  {
    id: '2026-10-22-tournament-round-clock',
    date: '2026-10-22',
    title: {
      en: 'Tournament rounds last 40 minutes',
      es: 'Las rondas del torneo duran 40 minutos',
      pt: 'As rodadas do torneio duram 40 minutos',
    },
    items: {
      en: [
        'Each round lasts 40 minutes, the official round length. The 5 minutes to open your duel count inside those 40. The clock is on the bracket and on the board.',
        'If the duel is still going when time runs out, the player with more Life Points advances. If Life Points are tied, the player with more cards left in the Deck advances.',
      ],
      es: [
        'Cada ronda dura 40 minutos, el tiempo oficial. Los 5 minutos para abrir tu duelo van dentro de esos 40. El reloj se ve en el cuadro y en el tablero.',
        'Si el duelo sigue cuando se acaba el tiempo, pasa quien tiene más Life Points. Si los Life Points empatan, pasa quien tiene más cartas en el Deck.',
      ],
      pt: [
        'Cada rodada dura 40 minutos, o tempo oficial. Os 5 minutos para abrir o duelo entram nesses 40. O relógio aparece na chave e no campo.',
        'Se o duelo continua quando o tempo acaba, avança quem tem mais Pontos de Vida. Se os Pontos de Vida empatam, avança quem tem mais cards no Deck.',
      ],
    },
  },
  {
    id: '2026-10-22-ghost-rare',
    date: '2026-10-22',
    title: {
      en: 'Ghost Rare',
      es: 'Ghost Rare',
      pt: 'Ghost Rare',
    },
    items: {
      en: [
        'Ghost Rare joins the foils. A card printed Ultra or Secret can come up Ghost as often as Ultra. The art washes to pale silver.',
        'Rare, Ultra, and Ghost reprint the card name. Rare is silver, Ultra is light gold, and Ghost is holographic silver.',
      ],
      es: [
        'Ghost Rare entra a los foils. Una carta impresa como Ultra o Secret puede salir Ghost con la misma frecuencia que Ultra. El dibujo se lava a plata pálida.',
        'Rare, Ultra y Ghost reimprimen el nombre. Rare queda en plata, Ultra en oro claro y Ghost en plata holográfica.',
      ],
      pt: [
        'Ghost Rare entra nos foils. Uma carta impressa como Ultra ou Secret pode sair Ghost com a mesma frequência que Ultra. A arte lava para um prata pálido.',
        'Rare, Ultra e Ghost reimprimem o nome. Rare fica em prata, Ultra em ouro claro e Ghost em prata holográfica.',
      ],
    },
    figures: [
      {
        item: 0,
        src: '/assets/changelog/2026-10-22-ghost-rare.png',
        alt: {
          en: 'Blue-Eyes White Dragon as Rare, Ultra Rare, and Ghost Rare',
          es: 'Blue-Eyes White Dragon en Rare, Ultra Rare y Ghost Rare',
          pt: 'Blue-Eyes White Dragon em Rare, Ultra Rare e Ghost Rare',
        },
      },
    ],
  },
  {
    id: '2026-10-22-lightning-tournaments',
    date: '2026-10-22',
    title: {
      en: 'Single-elimination tournaments',
      es: 'Torneos de eliminación directa',
      pt: 'Torneios eliminatórios',
    },
    items: {
      en: [
        'Eight single-elimination tournaments a day, three hours apart. Sign-up opens 30 minutes before. The time you see is your own. Sixteen seats. It starts with four duelists; fewer than that and it is called off.',
        'Home shows the live bracket with a Watch button, or the next tournament when none is running. History keeps the last 10, with each deck and its owner.',
        'When your duel is ready, only you and your opponent get a notice. You have 5 minutes to open it.',
        'When sign-up opens, anyone on the site gets one notice for that tournament.',
        'Finishing a duel pays a Master Pack. With 4 duelists, semifinals pay 20 coins, the finalist pays 30, and the champion pays 40. Each extra round adds 10: in a field of 16, the round of 16 pays 10, quarterfinals pay 20, semifinals pay 40, the finalist pays 50, and the champion pays 60. These duels do not move your league.',
      ],
      es: [
        'Hay ocho torneos de eliminación directa al día, cada tres horas. La inscripción abre 30 minutos antes. La hora que ves es la de tu reloj. Cupo de dieciséis. Arranca con cuatro duelistas; si hay menos, se cancela.',
        'El inicio muestra el cuadro en curso con un botón para espectar, o el próximo torneo si no hay ninguno. El historial guarda los últimos 10, con la baraja de cada jugador.',
        'Cuando tu duelo está listo, el aviso les llega solo a ti y a tu rival. Tienes 5 minutos para abrirlo.',
        'Cuando abre la inscripción, quien esté en el sitio recibe un aviso. Sale una vez por torneo.',
        'Terminar un duelo paga un Master Pack. Con 4 duelistas la semifinal paga 20 monedas, el finalista 30 y el campeón 40. Cada ronda de más suma 10: en un cuadro de 16, octavos pagan 10, cuartos 20, la semifinal 40, el finalista 50 y el campeón 60. Estos duelos no mueven tu liga.',
      ],
      pt: [
        'Há oito torneios eliminatórios por dia, a cada três horas. A inscrição abre 30 minutos antes. O horário que você vê é o do seu relógio. Dezesseis vagas. Começa com quatro duelistas; com menos, é cancelado.',
        'O início mostra a chave em andamento com um botão para assistir, ou o próximo torneio se não houver nenhum. O histórico guarda os últimos 10, com o baralho de cada jogador.',
        'Quando seu duelo fica pronto, o aviso chega só para você e para o rival. Você tem 5 minutos para abri-lo.',
        'Quando a inscrição abre, quem está no site recebe um aviso. Aparece uma vez por torneio.',
        'Terminar um duelo paga um Master Pack. Com 4 duelistas, a semifinal paga 20 moedas, o finalista 30 e o campeão 40. Cada rodada a mais soma 10: numa chave de 16, as oitavas pagam 10, as quartas 20, a semifinal 40, o finalista 50 e o campeão 60. Esses duelos não movem sua liga.',
      ],
    },
  },
  {
    id: '2026-10-22-friends',
    date: '2026-10-22',
    title: {
      en: 'Friends',
      es: 'Amigos',
      pt: 'Amigos',
    },
    items: {
      en: [
        'Add a duelist by their exact name. They accept or decline. You can remove them later. Friends can chat in their own tab on the chat button, and that tab closes with the X. Invite an online friend to a private room. That room does not show up in the public list.',
      ],
      es: [
        'Agrega a un duelista por su nombre exacto. Esa persona acepta o rechaza. Después puedes eliminarla. Los amigos chatean en su propia pestaña del botón de chat, y esa pestaña se cierra con la X. Invita a un amigo en línea a una sala privada. Esa sala no aparece en la lista pública.',
      ],
      pt: [
        'Adicione um duelista pelo nome exato. Essa pessoa aceita ou recusa. Depois você pode remover. Amigos conversam na própria aba do botão de chat, e essa aba fecha no X. Convide um amigo online para uma sala privada. Essa sala não aparece na lista pública.',
      ],
    },
  },
  {
    id: '2026-10-22-donate-coins',
    date: '2026-10-22',
    title: {
      en: 'Donate for coins',
      es: 'Apoyar da monedas',
      pt: 'Apoiar dá moedas',
    },
    items: {
      en: [
        'Donate opens four coin packs: $3, $5, $10, and $20. The coins land on the account you are signed in with. Donate another amount still lets you choose the sum, and that one does not add coins.',
      ],
      es: [
        'Apoyar abre cuatro paquetes de monedas: $3, $5, $10 y $20. Las monedas caen en la cuenta con la que entraste. Donar otro monto sigue dejando elegir la cantidad, y esa no da monedas.',
      ],
      pt: [
        'Apoiar abre quatro pacotes de moedas: $3, $5, $10 e $20. As moedas caem na conta em que você entrou. Doar outro valor continua deixando você escolher a quantia, e essa não dá moedas.',
      ],
    },
  },
  {
    id: '2026-10-22-lob-mrd-packs',
    date: '2026-10-22',
    title: {
      en: 'LOB and Metal Raiders packs',
      es: 'Sobres de LOB y Metal Raiders',
      pt: 'Pacotes de LOB e Metal Raiders',
    },
    items: {
      en: [
        'The shop sells Legend of Blue-Eyes and Metal Raiders for 20 coins. Each pack has 9 cards, with the 2002 odds: eight Commons and one Rare or better. A card that was Ultra Rare or Secret Rare in any of these sets can also come out Helios, Nightglass, or Stormwake. The Master Pack is no longer for sale. Ones you already have still open.',
      ],
      es: [
        'La tienda vende Legend of Blue-Eyes y Metal Raiders a 20 monedas. Cada sobre trae 9 cartas, con las probabilidades de 2002: ocho Common y una Rare o mejor. Una carta que fue Ultra Rare o Secret Rare en cualquiera de estos sets también puede salir Helios, Nightglass o Stormwake. El Master Pack ya no se vende. Los que ya tienes se siguen abriendo.',
      ],
      pt: [
        'A loja vende Legend of Blue-Eyes e Metal Raiders por 20 moedas. Cada pacote traz 9 cartas, com as chances de 2002: oito Common e uma Rare ou melhor. Uma carta que foi Ultra Rare ou Secret Rare em qualquer um desses sets também pode sair Helios, Nightglass ou Stormwake. O Master Pack não está mais à venda. Os que você já tem continuam abrindo.',
      ],
    },
  },
  {
    id: '2026-10-22-banlist-level-reward',
    date: '2026-10-22',
    title: {
      en: 'Banlist cards for new accounts',
      es: 'Cartas de la banlist para cuentas nuevas',
      pt: 'Cartas da banlist para contas novas',
    },
    items: {
      en: [
        'Accounts at level 1 through 5 can choose one banlist card now, then one more on each of the next four levels. That is five cards. A Common copy goes into your collection. Accounts already at level 6 or higher do not get this.',
      ],
      es: [
        'Las cuentas de nivel 1 a 5 eligen una carta de la banlist ahora, y otra en cada uno de los cuatro niveles siguientes. Son cinco cartas. Una copia Common entra a tu colección. Las cuentas que ya están en nivel 6 o más no entran.',
      ],
      pt: [
        'Contas do nível 1 ao 5 escolhem uma carta da banlist agora, e outra em cada um dos quatro níveis seguintes. São cinco cartas. Uma cópia Common entra na sua coleção. Contas que já estão no nível 6 ou mais não entram.',
      ],
    },
  },
  {
    id: '2026-10-22-board-sharp',
    date: '2026-10-22',
    title: {
      en: 'Cards on the field look sharp',
      es: 'Las cartas del campo se ven nítidas',
      pt: 'As cartas do campo ficam nítidas',
    },
    items: {
      en: [
        'On a phone or tablet the cards on the field look sharp again. The board stays in the mode that cannot turn white.',
      ],
      es: [
        'En un teléfono o una tableta, las cartas del campo se ven nítidas otra vez. El tablero sigue en el modo que no se pone blanco.',
      ],
      pt: [
        'No celular ou no tablet, as cartas do campo ficam nítidas de novo. O campo continua no modo que não fica branco.',
      ],
    },
  },
  {
    id: '2026-10-22-exodia-limbs',
    date: '2026-10-22',
    title: {
      en: 'Exodia faces you',
      es: 'Exodia te mira de frente',
      pt: 'Exodia olha para você',
    },
    items: {
      en: [
        'On the final screen, Exodia’s left arm and left leg sit on the right, facing you.',
      ],
      es: [
        'En la pantalla final, el brazo y la pierna izquierdos de Exodia quedan a la derecha, de frente a ti.',
      ],
      pt: [
        'Na tela final, o braço esquerdo e a perna esquerda de Exodia ficam à direita, de frente para você.',
      ],
    },
    figures: [
      {
        item: 0,
        src: '/assets/changelog/2026-10-22-exodia-limbs.png',
        alt: {
          en: 'Exodia assembled facing you, with the left arm and left leg on the right',
          es: 'Exodia armado de frente, con el brazo y la pierna izquierdos a la derecha',
          pt: 'Exodia montado de frente, com o braço esquerdo e a perna esquerda à direita',
        },
      },
    ],
  },
  {
    id: '2026-10-21-tap-set-trap',
    date: '2026-10-21',
    title: {
      en: 'The Set trap you tap',
      es: 'La trampa colocada que tocas',
      pt: 'A armadilha baixada que você toca',
    },
    items: {
      en: [
        'With a trap question open, tapping another Set trap flips that card. Waboku next to it stays Set.',
      ],
      es: [
        'Con el aviso de trampas abierto, tocar otra trampa colocada voltea esa carta. El Waboku de al lado sigue colocado.',
      ],
      pt: [
        'Com o aviso de armadilhas aberto, tocar outra armadilha baixada vira essa carta. O Waboku ao lado continua baixado.',
      ],
    },
  },
  {
    id: '2026-10-20-play-again-board',
    date: '2026-10-20',
    title: {
      en: 'Play again shows the board',
      es: 'Jugar de nuevo muestra el tablero',
      pt: 'Jogar de novo mostra o campo',
    },
    items: {
      en: [
        'Play again could leave a white rectangle over the playmat, with the gear and the card viewer still up. The board is drawn in the mode that cannot go white, and the effects layer no longer covers it.',
      ],
      es: [
        'Jugar de nuevo podía dejar un rectángulo blanco sobre el tapete, con el engranaje y el visor de cartas todavía visibles. El tablero se dibuja en el modo que no puede quedar en blanco, y la capa de efectos ya no lo tapa.',
      ],
      pt: [
        'Jogar de novo podia deixar um retângulo branco sobre o tapete, com a engrenagem e o visualizador de cartas ainda na tela. O campo é desenhado no modo que não pode ficar branco, e a camada de efeitos não o cobre mais.',
      ],
    },
  },
  {
    id: '2026-10-20-phone-canvas-board',
    date: '2026-10-20',
    title: {
      en: 'Phone board stays up',
      es: 'El tablero del teléfono se queda',
      pt: 'O campo do celular fica',
    },
    items: {
      en: [
        'On a phone the board is drawn without the graphics mode that was turning white or black, so the gear no longer sits over an empty screen. Play again does the same. The first duel from the lobby is unchanged. The playmat and cards show after the intro.',
      ],
      es: [
        'En un teléfono el tablero se dibuja sin el modo gráfico que se ponía blanco o negro, así que el engranaje ya no queda sobre una pantalla vacía. Jugar de nuevo hace lo mismo. El primer duelo desde el lobby no cambia. El tapete y las cartas aparecen después de la intro.',
      ],
      pt: [
        'No celular o campo é desenhado sem o modo gráfico que ficava branco ou preto, então a engrenagem não fica mais sobre uma tela vazia. Jogar de novo faz o mesmo. O primeiro duelo a partir do lobby não muda. O tapete e as cartas aparecem depois da intro.',
      ],
    },
  },
  {
    id: '2026-10-20-man-eater-empty',
    date: '2026-10-20',
    title: {
      en: 'The CPU keeps Man-Eater Bug set',
      es: 'La CPU deja a Man-Eater Bug boca abajo',
      pt: 'A CPU deixa Man-Eater Bug virado para baixo',
    },
    items: {
      en: [
        'The CPU no longer Flip Summons Man-Eater Bug or Hane-Hane when you have no monster on the field. That flip had to destroy one of its own monsters.',
      ],
      es: [
        'La CPU ya no hace Invocación de Volteo de Man-Eater Bug ni de Hane-Hane cuando no tienes ningún monstruo en el campo. Ese volteo tenía que destruir uno de sus propios monstruos.',
      ],
      pt: [
        'A CPU não faz mais Invocação por Flip de Man-Eater Bug nem de Hane-Hane quando você não tem nenhum monstro no campo. Esse flip tinha que destruir um dos monstros dela.',
      ],
    },
  },
  {
    id: '2026-10-20-dice-phone',
    date: '2026-10-20',
    title: {
      en: 'Dice no longer blank the board',
      es: 'Los dados ya no apagan el tablero',
      pt: 'Os dados não apagam mais o campo',
    },
    items: {
      en: [
        'On a phone, the dice roll could take the board with it, and the gear stayed up over a black screen. Play again also waits until the previous duel has let go of the screen.',
      ],
      es: [
        'En un teléfono, los dados podían llevarse el tablero, y el engranaje se quedaba sobre una pantalla negra. Jugar otra vez también espera a que el duelo anterior suelte la pantalla.',
      ],
      pt: [
        'No celular, os dados podiam levar o campo junto, e a engrenagem ficava sobre uma tela preta. Jogar de novo também espera o duelo anterior soltar a tela.',
      ],
    },
  },
  {
    id: '2026-10-20-ranked-one-seat',
    date: '2026-10-20',
    title: {
      en: 'One ranked duel at a time',
      es: 'Un duelo ranked a la vez',
      pt: 'Um duelo ranqueado por vez',
    },
    items: {
      en: [
        'A phone could enter two ranked duels at once and then show a black board. You stay in one duel. The board is created after the intro, so the dice roll no longer blanks it.',
      ],
      es: [
        'Un teléfono podía entrar en dos duelos ranked a la vez y después mostrar el tablero en negro. Te quedas en un solo duelo. El tablero se crea después de la intro, así que los dados ya no lo dejan en negro.',
      ],
      pt: [
        'Um celular podia entrar em dois duelos ranqueados ao mesmo tempo e depois mostrar o campo preto. Você fica em um só duelo. O campo é criado depois da intro, então os dados não o deixam mais preto.',
      ],
    },
  },
  {
    id: '2026-10-20-ranked-black-board',
    date: '2026-10-20',
    title: {
      en: 'Ranked board comes back',
      es: 'El tablero de ranked vuelve',
      pt: 'O campo ranqueado volta',
    },
    items: {
      en: [
        'A ranked duel could open on a black board after the intro, with the gear and the card viewer still up. The board paints again. Leaving that black screen no longer gives the win to your opponent. The dice roll shows their name.',
      ],
      es: [
        'Un duelo ranked podía abrir con el tablero en negro después de la intro, con el engranaje y el visor de cartas todavía visibles. El tablero se pinta de nuevo. Salir de esa pantalla negra ya no le da la victoria al rival. Los dados muestran su nombre.',
      ],
      pt: [
        'Um duelo ranqueado podia abrir com o campo preto depois da intro, com a engrenagem e o visualizador de cartas ainda na tela. O campo volta a aparecer. Sair dessa tela preta não entrega mais a vitória ao oponente. Os dados mostram o nome dele.',
      ],
    },
  },
  {
    id: '2026-10-20-banlist-emergency',
    date: '2026-10-20',
    title: {
      en: 'Tighter copy limits',
      es: 'Límites de copias más estrictos',
      pt: 'Limites de cópias mais estritos',
    },
    items: {
      en: [
        'Trap Hole, Swords of Revealing Light, Summoned Skull, Waboku, Witch of the Black Forest, and Sangan move from 2 to 1. Man-Eater Bug, Wall of Illusion, Fissure, La Jinn the Mystical Genie of the Lamp, Solemn Judgment, Magic Jammer, 7 Colored Fish, Seven Tools of the Bandit, Magician of Faith, Robbin\' Goblin, Tribute to the Doomed, and Jirai Gumo are now at 2. Soul Exchange and the Exodia pieces stay at 2. Cut the extra copies before you start a duel.',
      ],
      es: [
        'Trap Hole, Swords of Revealing Light, Summoned Skull, Waboku, Witch of the Black Forest y Sangan pasan de 2 a 1. Man-Eater Bug, Wall of Illusion, Fissure, La Jinn the Mystical Genie of the Lamp, Solemn Judgment, Magic Jammer, 7 Colored Fish, Seven Tools of the Bandit, Magician of Faith, Robbin\' Goblin, Tribute to the Doomed y Jirai Gumo quedan en 2. Soul Exchange y las piezas de Exodia siguen en 2. Baja las copias de más antes de empezar un duelo.',
      ],
      pt: [
        'Trap Hole, Swords of Revealing Light, Summoned Skull, Waboku, Witch of the Black Forest e Sangan passam de 2 para 1. Man-Eater Bug, Wall of Illusion, Fissure, La Jinn the Mystical Genie of the Lamp, Solemn Judgment, Magic Jammer, 7 Colored Fish, Seven Tools of the Bandit, Magician of Faith, Robbin\' Goblin, Tribute to the Doomed e Jirai Gumo ficam em 2. Soul Exchange e as peças de Exodia continuam em 2. Reduza as cópias a mais antes de começar um duelo.',
      ],
    },
  },
  {
    id: '2026-10-20-promo-recycle',
    date: '2026-10-20',
    title: {
      en: 'Promo copies can be recycled',
      es: 'Las copias promo se pueden reciclar',
      pt: 'As cópias promo podem ser recicladas',
    },
    items: {
      en: [
        'The promo Blue-Eyes you already own stays in your album. Extra copies of it can be recycled like any other card.',
      ],
      es: [
        'La Blue-Eyes promo que ya tienes se queda en tu álbum. Las copias de más se pueden reciclar como cualquier otra carta.',
      ],
      pt: [
        'A Blue-Eyes promo que você já tem continua no seu álbum. As cópias extras podem ser recicladas como qualquer outra carta.',
      ],
    },
  },
  {
    id: '2026-10-20-soul-exchange-tribute',
    date: '2026-10-20',
    title: {
      en: 'Soul Exchange tributes',
      es: 'Sacrificios de Soul Exchange',
      pt: 'Tributos de Soul Exchange',
    },
    items: {
      en: [
        'After **Soul Exchange**, **Cannon Soldier**, **Catapult Turtle**, and **The Little Swordsman of Aile** can Tribute the monster you selected.',
      ],
      es: [
        'Después de **Soul Exchange**, **Cannon Soldier**, **Catapult Turtle** y **The Little Swordsman of Aile** pueden sacrificar el monstruo que elegiste.',
      ],
      pt: [
        'Depois de **Soul Exchange**, **Cannon Soldier**, **Catapult Turtle** e **The Little Swordsman of Aile** podem tributar o monstro que você escolheu.',
      ],
    },
  },
  {
    id: '2026-10-20-ranked-mission-coins',
    date: '2026-10-20',
    title: {
      en: 'Ranked mission coins',
      es: 'Monedas de la clasificatoria',
      pt: 'Moedas da ranqueada',
    },
    items: {
      en: [
        'Collecting a ranked step now adds its 3 coins to the coin count. Those coins stay out of the count until you collect that step. Step 10 still pays packs only.',
      ],
      es: [
        'Recoger un paso de clasificatoria ahora suma sus 3 monedas al contador. Esas monedas quedan fuera del contador hasta que recojas ese paso. El paso 10 sigue dando solo sobres.',
      ],
      pt: [
        'Recolher um passo da ranqueada agora soma suas 3 moedas ao contador. Essas moedas ficam fora do contador até você recolher esse passo. O passo 10 continua dando só pacotes.',
      ],
    },
  },
  {
    id: '2026-10-20-covered-defense',
    date: '2026-10-20',
    title: {
      en: 'Covered monsters',
      es: 'Monstruos tapados',
      pt: 'Monstros cobertos',
    },
    items: {
      en: [
        'A Defense monster tucked under the one beside it can be chosen. That includes the Tribute for Horn of Heaven.',
      ],
      es: [
        'Un monstruo en Defensa que queda debajo del de al lado se puede elegir. Eso incluye el sacrificio de Horn of Heaven.',
      ],
      pt: [
        'Um monstro em Defesa que fica por baixo do vizinho pode ser escolhido. Isso inclui o Tributo de Horn of Heaven.',
      ],
    },
  },
  {
    id: '2026-10-20-race-coins',
    date: '2026-10-20',
    title: {
      en: 'Race goal coins',
      es: 'Monedas de la carrera',
      pt: 'Moedas da corrida',
    },
    items: {
      en: [
        'Collecting a Race goal now adds its 3 coins to the coin count. Those coins stay out of the count until you collect that goal.',
      ],
      es: [
        'Recoger una meta de Carrera ahora suma sus 3 monedas al contador. Esas monedas quedan fuera del contador hasta que recojas esa meta.',
      ],
      pt: [
        'Recolher uma meta da Corrida agora soma suas 3 moedas ao contador. Essas moedas ficam fora do contador até você recolher essa meta.',
      ],
    },
  },
  {
    id: '2026-10-20-solemn-damage-step',
    date: '2026-10-20',
    title: {
      en: 'Solemn Judgment before damage',
      es: 'Solemn Judgment antes del daño',
      pt: 'Solemn Judgment antes do dano',
    },
    items: {
      en: [
        'A Set Solemn Judgment or Seven Tools of the Bandit is asked when the opponent activates Reinforcements, Castle Walls, or Reverse Trap before damage. That includes an attack that just flipped a Set monster.',
      ],
      es: [
        'Un Solemn Judgment o Seven Tools of the Bandit colocado se pregunta cuando el rival activa Reinforcements, Castle Walls o Reverse Trap antes del daño. Eso incluye un ataque que acaba de voltear un monstruo colocado.',
      ],
      pt: [
        'Um Solemn Judgment ou Seven Tools of the Bandit baixado é perguntado quando o oponente ativa Reinforcements, Castle Walls ou Reverse Trap antes do dano. Isso inclui um ataque que acabou de virar um monstro baixado.',
      ],
    },
  },
  {
    id: '2026-10-20-deck-variety-poll',
    date: '2026-10-20',
    title: {
      en: 'A poll on 60-card decks',
      es: 'Una encuesta sobre barajas de 60',
      pt: 'Uma enquete sobre baralhos de 60',
    },
    items: {
      en: [
        'Home asked whether the minimum deck should go from 40 to 60, together with a stricter banlist, so decks have more variety. That poll is closed.',
      ],
      es: [
        'Inicio preguntó si el mínimo de la baraja debería pasar de 40 a 60, junto con una banlist más estricta, para que las barajas tengan más variedad. Esa encuesta ya cerró.',
      ],
      pt: [
        'O Início perguntou se o mínimo do baralho devia passar de 40 para 60, junto com uma banlist mais estrita, para os baralhos terem mais variedade. Essa enquete já fechou.',
      ],
    },
  },
  {
    id: '2026-10-20-banlist-permanent',
    date: '2026-10-20',
    title: {
      en: 'The banlist stays',
      es: 'La banlist se queda',
      pt: 'A banlist fica',
    },
    items: {
      en: [
        'The banlist is permanent. It is no longer a temporary list. It will change as the meta changes. The FAQ says so, and the daily reward limits are now separate questions.',
      ],
      es: [
        'La banlist es permanente. Ya no es una lista temporal. Va a ir cambiando según el meta. El FAQ lo explica, y los límites diarios de recompensa ahora son preguntas aparte.',
      ],
      pt: [
        'A banlist é permanente. Não é mais uma lista temporária. Vai mudando conforme o meta. O FAQ explica isso, e os limites diários de recompensa agora são perguntas separadas.',
      ],
    },
  },
  {
    id: '2026-10-20-faq-rewards',
    date: '2026-10-20',
    title: {
      en: 'Daily reward limits in the FAQ',
      es: 'Límites diarios en el FAQ',
      pt: 'Limites diários no FAQ',
    },
    items: {
      en: [
        'The FAQ now explains which rewards stop for the day. Ranked is the example: the ladder pays for the first 10 finished matches, and the waiting note pays 1 extra coin up to 20 times. Open it from the top bar or from Home.',
      ],
      es: [
        'El FAQ ahora explica qué recompensas se detienen al terminar el día. Clasificatoria es el ejemplo: la escalera paga por los primeros 10 duelos terminados, y el aviso de espera paga 1 moneda extra hasta 20 veces. Ábrelo desde la barra de arriba o desde Inicio.',
      ],
      pt: [
        'O FAQ agora explica quais recompensas param no fim do dia. A ranqueada é o exemplo: a escada paga pelas primeiras 10 partidas terminadas, e o aviso de espera paga 1 moeda extra até 20 vezes. Abra pela barra de cima ou pelo Início.',
      ],
    },
  },
  {
    id: '2026-10-20-recycle-coins',
    date: '2026-10-20',
    title: {
      en: 'Recycling pays more',
      es: 'Reciclar paga más',
      pt: 'Reciclar paga mais',
    },
    items: {
      en: [
        'Extra copies pay more when the print is higher. Common stays at **1** coin. Rare pays **3**, Super Rare **5**, Ultra Rare **8**, Helios Rare **12**, Nightglass Rare **18**, and Stormwake Rare **25**.',
      ],
      es: [
        'Las copias de más pagan más cuando la impresión es más alta. Common sigue en **1** moneda. Rare paga **3**, Super Rare **5**, Ultra Rare **8**, Helios Rare **12**, Nightglass Rare **18** y Stormwake Rare **25**.',
      ],
      pt: [
        'As cópias extras pagam mais quando a impressão é mais alta. Comum continua em **1** moeda. Raro paga **3**, Super Raro **5**, Ultra Raro **8**, Helios Rare **12**, Nightglass Rare **18** e Stormwake Rare **25**.',
      ],
    },
  },
  {
    id: '2026-10-19-fissure-tie',
    date: '2026-10-19',
    title: {
      en: 'Fissure asks on a tie',
      es: 'Fissure pregunta en un empate',
      pt: 'Fissure pergunta no empate',
    },
    items: {
      en: [
        'Fissure now lets you choose when two or more of your opponent\'s face-up monsters share the lowest ATK. One monster with the lowest ATK is still destroyed immediately.',
      ],
      es: [
        'Fissure ahora te deja elegir cuando dos o más monstruos boca arriba de tu rival empatan en el ATK más bajo. Si uno solo tiene el ATK más bajo, se destruye al instante.',
      ],
      pt: [
        'Fissure agora deixa você escolher quando dois ou mais monstros com a face para cima do seu oponente empatam no ATK mais baixo. Se só um tem o ATK mais baixo, ele é destruído na hora.',
      ],
    },
  },
  {
    id: '2026-10-19-reborn-graveyards',
    date: '2026-10-19',
    title: {
      en: 'Monster Reborn separates the Graveyards',
      es: 'Monster Reborn separa los Cementerios',
      pt: 'Monster Reborn separa os Cemitérios',
    },
    items: {
      en: [
        'Monster Reborn and Soul Release now show your Graveyard and your opponent’s side by side. Each card is marked Yours or Opponent. If that monster later returns to the hand, it goes back to its owner.',
      ],
      es: [
        'Monster Reborn y Soul Release ahora muestran tu Cementerio y el del rival, uno al lado del otro. Cada carta dice Tuya o Rival. Si ese monstruo vuelve a la mano, regresa a la de su dueño.',
      ],
      pt: [
        'Monster Reborn e Soul Release agora mostram o seu Cemitério e o do oponente, lado a lado. Cada card diz Seus ou Oponente. Se esse monstro voltar para a mão, volta para a do dono.',
      ],
    },
    figures: [
      {
        item: 0,
        src: '/assets/changelog/2026-10-19-reborn-graveyards.png',
        alt: {
          en: 'Monster Reborn lists your Graveyard on the left and the opponent Graveyard on the right',
          es: 'Monster Reborn lista tu Cementerio a la izquierda y el del rival a la derecha',
          pt: 'Monster Reborn lista o seu Cemitério à esquerda e o do oponente à direita',
        },
      },
    ],
  },
  {
    id: '2026-10-20-gy-to-hand',
    date: '2026-10-20',
    title: {
      en: 'Graveyard cards shown on the way back',
      es: 'La carta del Cementerio se queda a la vista',
      pt: 'O card do Cemitério fica à vista',
    },
    items: {
      en: [
        'Magician of Faith and Mask of Darkness show the Spell or Trap they return from the Graveyard. The picture stays until you close it, even with fast animations.',
      ],
      es: [
        'Magician of Faith y Mask of Darkness muestran la Mágica o la Trampa que devuelven del Cementerio. La imagen se queda hasta que la cierres, aunque las animaciones vayan rápido.',
      ],
      pt: [
        'Magician of Faith e Mask of Darkness mostram a Magia ou a Armadilha que volta do Cemitério. A imagem fica até você fechar, mesmo com animações rápidas.',
      ],
    },
  },
  {
    id: '2026-10-19-phone-duel-log',
    date: '2026-10-19',
    title: {
      en: 'Duel log on a phone',
      es: 'Registro del duelo en el teléfono',
      pt: 'Registro do duelo no celular',
    },
    items: {
      en: [
        'On a phone, the Log at the bottom of the duel settings menu now shows the plays. It no longer stops at the heading.',
      ],
      es: [
        'En el teléfono, el Log al final del menú de ajustes del duelo ahora muestra las jugadas. Ya no se queda solo en el título.',
      ],
      pt: [
        'No celular, o Log no fim do menu de ajustes do duelo agora mostra as jogadas. Ele não para mais só no título.',
      ],
    },
  },
  {
    id: '2026-10-19-unhappy-maiden',
    date: '2026-10-19',
    title: {
      en: 'The Unhappy Maiden ends the Battle Phase',
      es: 'The Unhappy Maiden termina la Battle Phase',
      pt: 'The Unhappy Maiden encerra a Battle Phase',
    },
    items: {
      en: [
        'When battle sends The Unhappy Maiden to the Graveyard, that Battle Phase ends. The turn continues in Main Phase 2. You can still Summon there if you have not used your Normal Summon.',
      ],
      es: [
        'Cuando el combate manda a The Unhappy Maiden al Cementerio, esa Battle Phase termina. El turno sigue en la Main Phase 2. Ahí puedes invocar si todavía no usaste tu Invocación Normal.',
      ],
      pt: [
        'Quando o combate manda The Unhappy Maiden ao Cemitério, essa Battle Phase termina. O turno continua na Main Phase 2. Você ainda pode invocar ali se não usou sua Invocação Normal.',
      ],
    },
  },
  {
    id: '2026-10-19-ranked-call-badge',
    date: '2026-10-19',
    title: {
      en: 'Rank and level on ranked prompts',
      es: 'Rango y nivel en los avisos de clasificatoria',
      pt: 'Ranque e nível nos avisos da ranqueada',
    },
    items: {
      en: [
        'While you duel the CPU, the note that someone is waiting for ranked now shows their league badge and account level next to their name.',
        'After 20 of those notes pay their coin, the note still appears. That match pays no extra coin.',
      ],
      es: [
        'Mientras juegas contra la CPU, el aviso de que alguien espera una clasificatoria ahora muestra su insignia de liga y su nivel de cuenta junto al nombre.',
        'Después de que 20 avisos paguen su moneda, el aviso sigue. Esa partida no paga la moneda extra.',
      ],
      pt: [
        'Enquanto você joga contra a CPU, o aviso de que alguém espera uma ranqueada agora mostra a insígnia de liga e o nível da conta ao lado do nome.',
        'Depois que 20 avisos pagarem a moeda, o aviso continua. Essa partida não paga a moeda extra.',
      ],
    },
  },
  {
    id: '2026-10-19-solemn-sangan',
    date: '2026-10-19',
    title: {
      en: 'Solemn Judgment and Sangan',
      es: 'Solemn Judgment y Sangan',
      pt: 'Solemn Judgment e Sangan',
    },
    items: {
      en: [
        'If Solemn Judgment or Horn of Heaven negates the summon of Sangan or Witch of the Black Forest, they do not search. That monster never entered the field. Trap Hole still destroys a summon that already succeeded, and the search still happens. A Sangan tributed for a Tribute Summon still searches, even when Solemn Judgment stops the monster that was coming in. The Immortal of Thunder does not deal 5000 on that negated summon, and Last Will does not count it. Sword of Deep-Seated still returns to the top of the Deck.',
      ],
      es: [
        'Si Solemn Judgment o Horn of Heaven niegan la invocación de Sangan o Witch of the Black Forest, no buscan. Ese monstruo nunca entró al campo. Trap Hole sigue destruyendo una invocación que ya fue exitosa, y la búsqueda sí ocurre. Un Sangan sacrificado para una Invocación por Sacrificio sí busca, aunque Solemn Judgment detenga al monstruo que iba a entrar. The Immortal of Thunder no inflige 5000 en esa invocación negada, y Last Will no la cuenta. Sword of Deep-Seated igual vuelve arriba de la baraja.',
      ],
      pt: [
        'Se Solemn Judgment ou Horn of Heaven negarem a invocação de Sangan ou Witch of the Black Forest, eles não buscam. Esse monstro nunca entrou em campo. Trap Hole continua destruindo uma invocação que já foi bem-sucedida, e a busca ainda acontece. Um Sangan tributado para uma Invocação por Tributo ainda busca, mesmo quando Solemn Judgment impede o monstro que ia entrar. The Immortal of Thunder não causa 5000 nessa invocação negada, e Last Will não a conta. Sword of Deep-Seated ainda volta para o topo do baralho.',
      ],
    },
  },
  {
    id: '2026-10-19-duelist-same-division',
    date: '2026-10-19',
    title: {
      en: 'Duelist wins in your division',
      es: 'Victorias de Duelista en tu división',
      pt: 'Vitórias de Duelista na sua divisão',
    },
    items: {
      en: [
        'A Duelist win against another Duelist in your division now pays at least **12** league points. A win against the same badge no longer stops at 4, 5, or 6. A loss in Duelist still costs **5**.',
      ],
      es: [
        'Una victoria de Duelista contra otro Duelista de tu división ahora paga al menos **12** puntos de liga. Ganar con la misma insignia ya no se queda en 4, 5 o 6. Una derrota en Duelista sigue costando **5**.',
      ],
      pt: [
        'Uma vitória de Duelista contra outro Duelista da sua divisão agora paga pelo menos **12** pontos de liga. Ganhar com a mesma insígnia não fica mais em 4, 5 ou 6. Uma derrota em Duelista continua custando **5**.',
      ],
    },
  },
  {
    id: '2026-10-19-mission-coins',
    date: '2026-10-19',
    title: {
      en: 'Daily mission coins',
      es: 'Monedas de las misiones diarias',
      pt: 'Moedas das missões diárias',
    },
    items: {
      en: [
        'Collect and Collect all now add the 3 coins printed on a daily mission. The coin count was only holding 1 back, so collecting several at once looked short. Coins from daily missions you have not collected yet stay out of the count until you collect them.',
      ],
      es: [
        'Recoger y Recoger todo ahora suman las 3 monedas que muestra la misión diaria. El contador solo apartaba 1, así que recoger varias a la vez parecía incompleto. Las monedas de misiones diarias que aún no recoges quedan fuera del contador hasta que las recojas.',
      ],
      pt: [
        'Recolher e Recolher tudo agora somam as 3 moedas que a missão diária mostra. O contador só guardava 1, então recolher várias de uma vez parecia incompleto. As moedas de missões diárias que você ainda não recolheu ficam fora do contador até você recolhê-las.',
      ],
    },
  },
  {
    id: '2026-10-19-clock-thirty',
    date: '2026-10-19',
    title: {
      en: 'More time on your turn',
      es: 'Más tiempo en tu turno',
      pt: 'Mais tempo no seu turno',
    },
    items: {
      en: [
        'In a duel against another person, your turn now adds **30 seconds** to the clock, up to **3:00**. The opponent\'s turn still adds **8 seconds**. The first turn stays at **2:00**. Reordering your hand does not add time.',
      ],
      es: [
        'En un duelo contra otra persona, tu turno ahora suma **30 segundos** al reloj, hasta **3:00**. El turno del rival sigue sumando **8 segundos**. El primer turno se queda en **2:00**. Reordenar la mano no suma tiempo.',
      ],
      pt: [
        'Num duelo contra outra pessoa, o seu turno agora soma **30 segundos** ao relógio, até **3:00**. O turno do oponente continua somando **8 segundos**. O primeiro turno fica em **2:00**. Reordenar a mão não soma tempo.',
      ],
    },
  },
  {
    id: '2026-10-19-offering-chain-order',
    date: '2026-10-19',
    title: {
      en: 'Chain order after Ultimate Offering',
      es: 'Orden de la cadena tras Ofrenda Final',
      pt: 'Ordem da corrente após Ultimate Offering',
    },
    items: {
      en: [
        'Tribute Summoning with Ultimate Offering during the opponent\'s Battle Phase no longer freezes the chain-order list. Confirm places the cards, and the duel continues.',
      ],
      es: [
        'Invocar por Sacrificio con Ofrenda Final durante la Battle Phase del rival ya no deja congelada la lista del orden de la cadena. Confirmar coloca las cartas, y el duelo sigue.',
      ],
      pt: [
        'A Invocação por Tributo com Ultimate Offering durante a Fase de Batalha do oponente não deixa mais a lista da ordem da corrente travada. Confirmar coloca as cartas, e o duelo continua.',
      ],
    },
  },
  {
    id: '2026-10-19-deck-list',
    date: '2026-10-19',
    title: {
      en: 'Deck list on one screen',
      es: 'Lista de la baraja en una pantalla',
      pt: 'Lista do baralho em uma tela',
    },
    items: {
      en: [
        'Deck Builder has a Deck list. Names shows each card with a larger picture. Cards shows only the pictures. Image saves that view so you can send it. Share still copies the list as text.',
      ],
      es: [
        'El constructor de barajas tiene Ver lista. Nombres muestra cada carta con una imagen más grande. Cartas muestra solo las imágenes. Imagen guarda esa vista para enviarla. Compartir sigue copiando la lista como texto.',
      ],
      pt: [
        'O construtor de baralhos tem Ver lista. Nomes mostra cada card com uma imagem maior. Cards mostra só as imagens. Imagem guarda essa vista para enviar. Compartilhar continua copiando a lista como texto.',
      ],
    },
  },
  {
    id: '2026-10-19-sangan-search',
    date: '2026-10-19',
    title: {
      en: 'Sangan searches 1500 ATK',
      es: 'Sangan busca 1500 ATK',
      pt: 'Sangan busca 1500 de ATK',
    },
    items: {
      en: [
        'Sangan adds 1 monster with 1500 or less ATK from your Deck to your hand.',
      ],
      es: [
        'Sangan añade a tu mano 1 monstruo con 1500 ATK o menos desde tu baraja.',
      ],
      pt: [
        'Sangan adiciona à sua mão 1 monstro com 1500 ou menos de ATK do seu baralho.',
      ],
    },
  },
  {
    id: '2026-10-19-yado-cancel',
    date: '2026-10-19',
    title: {
      en: 'Yado Karu can be declined',
      es: 'Puedes rechazar a Yado Karu',
      pt: 'Você pode recusar Yado Karu',
    },
    items: {
      en: [
        'When Yado Karu changes from Attack Position to Defense Position, Cancel returns no cards and the turn continues. The monster stays in Defense Position.',
      ],
      es: [
        'Cuando Yado Karu cambia de Posición de Ataque a Posición de Defensa, Cancelar no devuelve cartas y el turno sigue. El monstruo se queda en Posición de Defensa.',
      ],
      pt: [
        'Quando Yado Karu muda da Posição de Ataque para a Posição de Defesa, Cancelar não devolve cards e o turno continua. O monstro fica na Posição de Defesa.',
      ],
    },
  },
  {
    id: '2026-10-19-cancel-tribute',
    date: '2026-10-19',
    title: {
      en: 'Cancel a Tribute Summon',
      es: 'Cancelar una Invocación por Sacrificio',
      pt: 'Cancelar uma Invocação por Tributo',
    },
    items: {
      en: [
        'While you are choosing tributes, Cancel aborts that summon. A face-down monster you could Flip Summon, such as Magician of Faith, offers Flip Summon as well as Tribute.',
      ],
      es: [
        'Mientras eliges sacrificios, Cancelar aborta esa invocación. Un monstruo boca abajo que puedes Invocar por Volteo, como Magician of Faith, ofrece Invocación de Volteo además de Sacrificio.',
      ],
      pt: [
        'Enquanto você escolhe tributos, Cancelar aborta essa invocação. Um monstro virado para baixo que você pode Invocar por Flip, como Magician of Faith, oferece Invocação por Flip além de Tributo.',
      ],
    },
  },
  {
    id: '2026-10-19-puppeteer-horn',
    date: '2026-10-19',
    title: {
      en: 'Mysterious Puppeteer and a negated summon',
      es: 'Mysterious Puppeteer y una invocación negada',
      pt: 'Mysterious Puppeteer e uma invocação negada',
    },
    items: {
      en: [
        'If Horn of Heaven or Solemn Judgment negates a summon, Mysterious Puppeteer does not give 500 Life Points. That monster never entered the field. Trap Hole still destroys a summon that already succeeded, and those 500 Life Points still happen.',
      ],
      es: [
        'Si Horn of Heaven o Solemn Judgment niegan una invocación, Mysterious Puppeteer no da 500 Life Points. Ese monstruo nunca entró al campo. Trap Hole sigue destruyendo una invocación que ya fue exitosa, y esos 500 Life Points sí se ganan.',
      ],
      pt: [
        'Se Horn of Heaven ou Solemn Judgment negarem uma invocação, Mysterious Puppeteer não dá 500 Life Points. Esse monstro nunca entrou em campo. Trap Hole continua destruindo uma invocação que já foi bem-sucedida, e esses 500 Life Points ainda são ganhos.',
      ],
    },
  },
  {
    id: '2026-10-19-board-hears',
    date: '2026-10-19',
    title: {
      en: 'A stuck duel listens again',
      es: 'Un duelo trabado vuelve a escuchar',
      pt: 'Um duelo travado volta a ouvir',
    },
    items: {
      en: [
        'If the board stops taking clicks after the opening deal, it unlocks on its own. You do not have to leave and come back.',
        'A monster that failed to draw on the field shows up without waiting for an attack.',
      ],
      es: [
        'Si el campo deja de aceptar clics después del reparto, se desbloquea solo. No tienes que salir y volver a entrar.',
        'Un monstruo que no se dibujó en el campo aparece sin esperar a que lo ataquen.',
      ],
      pt: [
        'Se o campo para de aceitar cliques depois da distribuição, ele destrava sozinho. Você não precisa sair e entrar de novo.',
        'Um monstro que não foi desenhado no campo aparece sem esperar um ataque.',
      ],
    },
  },
  {
    id: '2026-10-19-direct-attack-menu',
    date: '2026-10-19',
    title: {
      en: 'Direct attack asks in plain words',
      es: 'El ataque directo pregunta en claro',
      pt: 'O ataque direto pergunta em palavras claras',
    },
    items: {
      en: [
        'A monster that can attack Life Points directly now asks with real buttons: attack directly, attack that monster, or choose one when there are several. A face-down monster is not named.',
      ],
      es: [
        'Un monstruo que puede atacar los Life Points directo ahora pregunta con botones de verdad: atacar directamente, atacar a ese monstruo, o elegir uno cuando hay varios. Un monstruo boca abajo no dice su nombre.',
      ],
      pt: [
        'Um monstro que pode atacar os Life Points direto agora pergunta com botões de verdade: atacar diretamente, atacar aquele monstro, ou escolher um quando há vários. Um monstro virado para baixo não mostra o nome.',
      ],
    },
  },
  {
    id: '2026-10-19-banlist-all-modes',
    date: '2026-10-19',
    title: {
      en: 'The banlist covers every duel',
      es: 'La banlist vale en todos los duelos',
      pt: 'A banlist vale em todos os duelos',
    },
    items: {
      en: [
        'The temporary banlist applies in ranked, the casual queue, private rooms, and versus the CPU, including your own deck. Cut the extra copies before you start.',
      ],
      es: [
        'La banlist temporal vale en clasificatoria, en la cola casual, en las salas privadas y contra la CPU, incluida tu baraja. Baja las copias de más antes de empezar.',
      ],
      pt: [
        'A banlist temporária vale no ranqueado, na fila casual, nas salas privadas e contra a CPU, inclusive o seu baralho. Reduza as cópias a mais antes de começar.',
      ],
    },
  },
  {
    id: '2026-10-19-cpu-master-banlist',
    date: '2026-10-19',
    title: {
      en: 'Master follows the banlist',
      es: 'Maestro sigue la banlist',
      pt: 'Mestre segue a banlist',
    },
    items: {
      en: [
        'CPU decks on every difficulty, including Master, follow the temporary banlist.',
      ],
      es: [
        'Las barajas de la CPU, en todas las dificultades y también en Maestro, siguen la banlist temporal.',
      ],
      pt: [
        'Os baralhos da CPU, em todas as dificuldades e também em Mestre, seguem a banlist temporária.',
      ],
    },
  },
  {
    id: '2026-10-19-waboku-heavy-storm',
    date: '2026-10-19',
    title: {
      en: 'Waboku chains to Heavy Storm',
      es: 'Waboku se encadena a Heavy Storm',
      pt: 'Waboku se encadeia a Heavy Storm',
    },
    items: {
      en: [
        'In **Minimal** and **Auto**, a Set **Waboku** can be chained when the card just played would destroy it. The protection still lasts this turn after **Heavy Storm** sends it to the Graveyard. **Reinforcements**, **Castle Walls**, and **Reverse Trap** get the same chance.',
      ],
      es: [
        'En **Mínimo** y **Auto**, un **Waboku** colocado se puede encadenar cuando la carta recién jugada lo destruiría. La protección sigue este turno aunque **Heavy Storm** lo mande al Cementerio. **Reinforcements**, **Castle Walls** y **Reverse Trap** tienen la misma oportunidad.',
      ],
      pt: [
        'Em **Mínimo** e **Auto**, um **Waboku** baixado pode ser encadeado quando a carta recém-jogada o destruiria. A proteção continua neste turno mesmo que **Heavy Storm** o mande para o Cemitério. **Reinforcements**, **Castle Walls** e **Reverse Trap** têm a mesma chance.',
      ],
    },
  },
  {
    id: '2026-10-19-robbin-goblin-attack',
    date: '2026-10-19',
    title: {
      en: 'Robbin\' Goblin on your attack',
      es: 'Robbin\' Goblin en tu ataque',
      pt: 'Robbin\' Goblin no seu ataque',
    },
    items: {
      en: [
        'With traps set to Auto, a Set Robbin\' Goblin can be activated when you declare an attack. Minimal still asks only at the start of your Battle Phase.',
      ],
      es: [
        'Con las trampas en Auto, un Robbin\' Goblin colocado se puede activar cuando declaras un ataque. Mínimo sigue preguntando solo al inicio de tu Battle Phase.',
      ],
      pt: [
        'Com as armadilhas em Auto, um Robbin\' Goblin baixado pode ser ativado quando você declara um ataque. Mínimo continua perguntando só no início da sua Battle Phase.',
      ],
    },
  },
  {
    id: '2026-10-07-chain-order-last-will',
    date: '2026-10-07',
    title: {
      en: 'Chain order can be confirmed',
      es: 'Ya se puede confirmar el orden de la cadena',
      pt: 'A ordem da corrente pode ser confirmada',
    },
    items: {
      en: [
        'Sangan and Witch of the Black Forest can be ordered after Last Will. Confirm places them, and cancel keeps the order already on screen.',
      ],
      es: [
        'Sangan y Bruja del Bosque Negro se pueden ordenar después de Última Voluntad. Confirmar las coloca, y cancelar deja el orden que ya está en pantalla.',
      ],
      pt: [
        'Sangan e Bruxa da Floresta Negra podem ser ordenados depois de Último Desejo. Confirmar coloca os dois, e cancelar deixa a ordem que já está na tela.',
      ],
    },
  },
  {
    id: '2026-10-07-ranked-reset-notice-off',
    date: '2026-10-07',
    title: {
      en: 'The ranked reset notice is gone',
      es: 'Ya no sale el aviso del reinicio',
      pt: 'O aviso do reset saiu',
    },
    items: {
      en: [
        'Home no longer shows the notice about resetting ranked matches.',
      ],
      es: [
        'Inicio ya no muestra el aviso sobre reiniciar las clasificatorias.',
      ],
      pt: [
        'O início não mostra mais o aviso sobre reiniciar as partidas ranqueadas.',
      ],
    },
  },
  {
    id: '2026-10-07-mrd-welcome-off',
    date: '2026-10-07',
    title: {
      en: 'The Metal Raiders welcome is gone',
      es: 'Ya no sale la bienvenida de Metal Raiders',
      pt: 'A boas-vindas de Metal Raiders saiu',
    },
    items: {
      en: [
        'The Metal Raiders welcome no longer opens when you enter.',
      ],
      es: [
        'La bienvenida de Metal Raiders ya no se abre al entrar.',
      ],
      pt: [
        'A boas-vindas de Metal Raiders não abre mais quando você entra.',
      ],
    },
  },
  {
    id: '2026-10-07-ranked-payout-7',
    date: '2026-10-07',
    title: {
      en: 'More coins for ranked',
      es: 'Más monedas en clasificatoria',
      pt: 'Mais moedas na ranqueada',
    },
    items: {
      en: [
        'A Quick Match win pays **7** coins. A finished loss or draw pays **5**. Surrendering still pays nothing.',
      ],
      es: [
        'Una victoria en clasificatoria paga **7** monedas. Una derrota o un empate terminados pagan **5**. Rendirse sigue sin pagar.',
      ],
      pt: [
        'Uma vitória na ranqueada paga **7** moedas. Uma derrota ou um empate terminados pagam **5**. Desistir continua sem pagar.',
      ],
    },
  },
  {
    id: '2026-10-07-mission-coins',
    date: '2026-10-07',
    title: {
      en: 'Daily missions pay 3',
      es: 'Las misiones diarias pagan 3',
      pt: 'As missões diárias pagam 3',
    },
    items: {
      en: [
        'Each daily mission, each bar step, and each queue-race goal pays **3** coins. Ranked steps 1 to 9 pay **3** coins too. Steps 6, 7, 8, and 9 are on the ladder now. Step **7** gives one shop pack and step **10** gives two.',
      ],
      es: [
        'Cada misión diaria, cada paso de la barra y cada meta de la carrera pagan **3** monedas. Los pasos 1 a 9 de clasificatoria también pagan **3**. Los pasos 6, 7, 8 y 9 ya están en la escalera. El paso **7** da un sobre de tienda y el paso **10** da dos.',
      ],
      pt: [
        'Cada missão diária, cada etapa da barra e cada meta da corrida pagam **3** moedas. As etapas 1 a 9 da ranqueada também pagam **3**. As etapas 6, 7, 8 e 9 já estão na escada. A etapa **7** dá um pacote da loja e a etapa **10** dá dois.',
      ],
    },
  },
  {
    id: '2026-10-07-ranked-payout',
    date: '2026-10-07',
    title: {
      en: 'Ranked win pays 5',
      es: 'La victoria clasificatoria paga 5',
      pt: 'A vitória ranqueada paga 5',
    },
    items: {
      en: [
        'A Quick Match win pays **5** coins. A finished loss or draw pays **3**. Surrendering still pays nothing.',
      ],
      es: [
        'Una victoria en clasificatoria paga **5** monedas. Una derrota o un empate terminados pagan **3**. Rendirse sigue sin pagar.',
      ],
      pt: [
        'Uma vitória na ranqueada paga **5** moedas. Uma derrota ou um empate terminados pagam **3**. Desistir continua sem pagar.',
      ],
    },
  },
  {
    id: '2026-10-06-ranked-payout',
    date: '2026-10-06',
    title: {
      en: 'More coins for ranked',
      es: 'Más monedas en clasificatoria',
      pt: 'Mais moedas na ranqueada',
    },
    items: {
      en: [
        'A Quick Match win pays **3** coins. A finished loss or draw pays **2**. Surrendering still pays nothing.',
      ],
      es: [
        'Una victoria en clasificatoria paga **3** monedas. Una derrota o un empate terminados pagan **2**. Rendirse sigue sin pagar.',
      ],
      pt: [
        'Uma vitória na ranqueada paga **3** moedas. Uma derrota ou um empate terminados pagam **2**. Desistir continua sem pagar.',
      ],
    },
  },
  {
    id: '2026-10-06-banlist-storm-offering',
    date: '2026-10-06',
    title: {
      en: 'Heavy Storm and Ultimate Offering at 1',
      es: 'Tormenta Fuerte y Ofrenda Final a 1',
      pt: 'Tempestade Pesada e Oferenda Derradeira em 1',
    },
    items: {
      en: [
        'Heavy Storm and Ultimate Offering move from 2 to 1 on the temporary PvP banlist. Ranked, private rooms, and CPU decks from Easy through Expert follow that cap. Master and your own deck versus the CPU can still run more.',
      ],
      es: [
        'Tormenta Fuerte y Ofrenda Final pasan de 2 a 1 en la banlist temporal de PvP. Clasificatoria, salas privadas y las barajas de la CPU de Fácil a Experto siguen ese tope. Maestro y tu baraja contra la CPU pueden llevar más.',
      ],
      pt: [
        'Tempestade Pesada e Oferenda Derradeira passam de 2 para 1 na banlist temporária de PvP. Ranqueada, salas privadas e os baralhos da CPU de Fácil até Especialista seguem esse limite. Mestre e seu baralho contra a CPU ainda podem levar mais.',
      ],
    },
  },
  {
    id: '2026-10-06-meta-season',
    date: '2026-10-06',
    title: {
      en: 'Popular counts this season',
      es: 'Populares cuenta esta temporada',
      pt: 'Populares conta esta temporada',
    },
    items: {
      en: [
        'Popular only counts decks brought since this season started. Duels from before the reset no longer show up there.',
      ],
      es: [
        'Populares solo cuenta las barajas llevadas desde que empezó esta temporada. Los duelos de antes del reinicio ya no aparecen ahí.',
      ],
      pt: [
        'Populares só conta os baralhos levados desde que esta temporada começou. Os duelos de antes do reset não aparecem mais lá.',
      ],
    },
  },
  {
    id: '2026-10-06-ranked-coins',
    date: '2026-10-06',
    title: {
      en: 'Ranked coins all day',
      es: 'Monedas de clasificatoria todo el día',
      pt: 'Moedas da ranqueada o dia todo',
    },
    items: {
      en: [
        'Every finished Quick Match pays **1** coin, whether you win, lose, or draw. There is no daily cap. Surrendering still pays nothing.',
      ],
      es: [
        'Cada clasificatoria terminada paga **1** moneda, ganes, pierdas o empates. No hay tope diario. Rendirse sigue sin pagar.',
      ],
      pt: [
        'Cada duelo ranqueado terminado paga **1** moeda, vitória, derrota ou empate. Não há limite diário. Desistir continua sem pagar.',
      ],
    },
  },
  {
    id: '2026-10-06-faq-decks',
    date: '2026-10-06',
    title: {
      en: 'Why decks and trunks reset',
      es: 'Por qué se reinician barajas y baúl',
      pt: 'Por que baralhos e baú reiniciam',
    },
    items: {
      en: [
        'The FAQ now explains why old decks and the trunk are erased when a new set arrives. Open it from the top bar or from Home.',
      ],
      es: [
        'El FAQ ahora explica por qué se borran las barajas viejas y el baúl cuando llega un set nuevo. Ábrelo desde la barra de arriba o desde Inicio.',
      ],
      pt: [
        'O FAQ agora explica por que os baralhos antigos e o baú são apagados quando chega um set novo. Abra pela barra de cima ou pelo Início.',
      ],
    },
  },
  {
    id: '2026-10-06-offering-pass',
    date: '2026-10-06',
    title: {
      en: 'Ultimate Offering pass',
      es: 'Pasar en Ofrenda Final',
      pt: 'Passar em Ultimate Offering',
    },
    items: {
      en: [
        '**No, pass** on Ultimate Offering no longer spends 500 LP. After you pay, you Normal Summon or Set that monster. The question comes back only once that summon happens.',
      ],
      es: [
        '**No, pasar** en Ofrenda Final ya no gasta 500 LP. Después de pagar, Invocas de Modo Normal o Colocas ese monstruo. La pregunta vuelve solo cuando esa invocación ocurre.',
      ],
      pt: [
        '**Não, passar** em Ultimate Offering não gasta mais 500 LP. Depois de pagar, você Invoca de Modo Normal ou Baixa esse monstro. A pergunta volta só quando essa invocação acontece.',
      ],
    },
  },
  {
    id: '2026-10-06-faith-mask',
    date: '2026-10-06',
    title: {
      en: 'Graveyard flips',
      es: 'Volteos del Cementerio',
      pt: 'Viradas do Cemitério',
    },
    items: {
      en: [
        '**Magician of Faith** adds 1 Spell from your Graveyard to your hand again. **Mask of Darkness** does the same for 1 Trap.',
        'Closing the peek of a card added to your hand no longer hides the next choice. Two **Sangan** searches in a row both ask for a card.',
      ],
      es: [
        '**Magician of Faith** vuelve a añadir 1 Mágica de tu Cementerio a tu mano. **Mask of Darkness** hace lo mismo con 1 Trampa.',
        'Cerrar el vistazo de una carta añadida a tu mano ya no esconde la siguiente elección. Dos búsquedas seguidas de **Sangan** piden carta las dos.',
      ],
      pt: [
        '**Magician of Faith** volta a adicionar 1 Magia do seu Cemitério à sua mão. **Mask of Darkness** faz o mesmo com 1 Armadilha.',
        'Fechar a prévia de uma carta adicionada à sua mão não esconde mais a próxima escolha. Duas buscas seguidas de **Sangan** pedem carta as duas.',
      ],
    },
  },
  {
    id: '2026-10-06-ai-coins',
    date: '2026-10-06',
    title: {
      en: 'Coins vs AI',
      es: 'Monedas vs IA',
      pt: 'Moedas contra a IA',
    },
    items: {
      en: [
        'While ranked is closed, every finished duel vs AI pays coins. There is no daily cap. Surrendering still pays nothing.',
      ],
      es: [
        'Mientras la clasificatoria está cerrada, cada duelo terminado contra la IA paga monedas. No hay tope diario. Rendirse sigue sin pagar.',
      ],
      pt: [
        'Enquanto a ranqueada está fechada, cada duelo terminado contra a IA paga moedas. Não há limite diário. Desistir continua sem pagar.',
      ],
    },
  },
  {
    id: '2026-10-06-barrel-heads',
    date: '2026-10-06',
    title: {
      en: 'Barrel Dragon',
      es: 'Barrel Dragon',
      pt: 'Barrel Dragon',
    },
    items: {
      en: [
        'Barrel Dragon destroys 1 opposing monster when 2 or 3 coin tosses land on heads.',
      ],
      es: [
        'Barrel Dragon destruye 1 monstruo del rival cuando 2 o 3 lanzamientos salen cara.',
      ],
      pt: [
        'Barrel Dragon destrói 1 monstro do oponente quando 2 ou 3 lançamentos dão cara.',
      ],
    },
  },
  {
    id: '2026-10-06-horn-minimal',
    date: '2026-10-06',
    title: {
      en: 'Horn of Heaven in Minimal',
      es: 'Horn of Heaven en Mínimo',
      pt: 'Horn of Heaven em Mínimo',
    },
    items: {
      en: [
        'With Traps set to Minimal, Horn of Heaven now asks when the opponent Normal Summons, Flip Summons, or Special Summons, if you control a monster to Tribute.',
      ],
      es: [
        'Con Trampas en Mínimo, Horn of Heaven ahora pregunta cuando el rival Invoca de modo Normal, por Volteo o Especial, si controlas un monstruo para sacrificar.',
      ],
      pt: [
        'Com Armadilhas em Mínimo, Horn of Heaven agora pergunta quando o oponente Invoca por Normal, Flip ou Especial, se você controlar um monstro para oferecer como Tributo.',
      ],
    },
  },
  {
    id: '2026-10-06-beta-chip-banlist',
    date: '2026-10-06',
    title: {
      en: 'Beta chips and a new PvP banlist',
      es: 'Chips de la beta y banlist nueva de PvP',
      pt: 'Chips da beta e banlist nova de PvP',
    },
    items: {
      en: [
        'Public profiles of duelists who finished the 5 placement duels in the beta show a Beta Player chip in the metal of that league: Duelist, Challenger, Champion, or King of Games.',
        'The temporary PvP banlist is back. Mirror Force is at 1. Trap Hole, Swords of Revealing Light, Sangan, Witch of the Black Forest, Heavy Storm, and each Exodia piece are at 2. La Jinn and Fissure are free at 3.',
      ],
      es: [
        'El perfil público de quien terminó las 5 de colocación en la beta muestra un chip de Beta Player con el metal de esa liga: Duelista, Retador, Campeón o Rey de los Juegos.',
        'Volvió la banlist temporal de PvP. Fuerza de Espejo queda a 1. Agujero Trampa, Espadas de la Luz Reveladora, Sangan, Bruja del Bosque Negro, Tormenta Fuerte y cada pieza de Exodia quedan a 2. La Jinn y Fisura quedan libres a 3.',
      ],
      pt: [
        'O perfil público de quem terminou as 5 de colocação na beta mostra um chip de Beta Player no metal dessa liga: Duelista, Desafiante, Campeão ou Rei dos Jogos.',
        'A banlist temporária de PvP voltou. Força do Espelho fica em 1. Buraco Armadilha, Espadas da Luz Reveladora, Sangan, Bruxa da Floresta Negra, Tempestade Pesada e cada peça de Exodia ficam em 2. La Jinn e Fissura ficam livres em 3.',
      ],
    },
  },
  {
    id: '2026-10-06-meta-detail',
    date: '2026-10-06',
    title: {
      en: 'Popular shows who travels together',
      es: 'Populares muestra con quién viaja cada carta',
      pt: 'Populares mostra com quem cada carta viaja',
    },
    items: {
      en: [
        'Each card shows whether its usage and win rate rose or fell against the previous 7 days, and how many decks run it at 1, 2, or 3 copies. Tap a card to see which cards share those decks. Top is Champion and King of Games in ranked only.',
      ],
      es: [
        'Cada carta muestra si su uso y sus victorias subieron o bajaron frente a los 7 días anteriores, y cuántas barajas la llevan a 1, 2 o 3 copias. Toca una carta para ver cuáles comparten esas barajas. Top es solo Campeón y Rey de los Juegos en clasificatoria.',
      ],
      pt: [
        'Cada carta mostra se o uso e as vitórias subiram ou caíram em relação aos 7 dias anteriores, e quantos baralhos a levam em 1, 2 ou 3 cópias. Toque uma carta para ver quais compartilham esses baralhos. Topo é só Campeão e Rei dos Jogos no ranqueado.',
      ],
    },
  },
  {
    id: '2026-10-06-popular-cards',
    date: '2026-10-06',
    title: {
      en: 'The most played cards are public',
      es: 'Las cartas más usadas ya son públicas',
      pt: 'As cartas mais usadas agora são públicas',
    },
    items: {
      en: [
        'Home has Popular: the cards duelists brought in the last 7 days, and how often those decks won. Filter by ranked, casual, or versus the CPU.',
      ],
      es: [
        'En inicio está Populares: las cartas que los duelistas llevaron en los últimos 7 días, y cuántas veces ganaron esas barajas. Filtra por clasificatoria, casual o contra la CPU.',
      ],
      pt: [
        'No início está Populares: as cartas que os duelistas levaram nos últimos 7 dias, e quantas vezes esses baralhos venceram. Filtre por ranqueado, casual ou contra a CPU.',
      ],
    },
  },
  {
    id: '2026-10-06-mrd-playtime-pack',
    date: '2026-10-06',
    afterReset: 'mrd',
    title: {
      en: 'Metal Raiders pack for the most played',
      es: 'Sobre de Metal Raiders para quienes más jugaron',
      pt: 'Pacote de Metal Raiders para quem mais jogou',
    },
    items: {
      en: [
        'The 50 accounts with the most time in finished duels get one special Metal Raiders pack. It has 5 cards from the set, and each one is Ultra Rare or a higher foil. Ties at 50th place are included. Claim it from the mailbox.',
      ],
      es: [
        'Las 50 cuentas con más tiempo en duelos terminados reciben un sobre especial de Metal Raiders. Trae 5 cartas del set, y cada una es Ultra Rare o un foil más alto. Si hay empate en el puesto 50, también entra. Reclámalo en el buzón.',
      ],
      pt: [
        'As 50 contas com mais tempo em duelos terminados recebem um pacote especial de Metal Raiders. Ele traz 5 cartas do set, e cada uma é Ultra Rare ou um foil mais alto. Se houver empate no 50º lugar, também entra. Resgate no correio.',
      ],
    },
  },
  {
    id: '2026-10-05-foil-phone',
    date: '2026-10-05',
    title: {
      en: 'Choose the foil shine',
      es: 'Elige el brillo del foil',
      pt: 'Escolha o brilho do foil',
    },
    items: {
      en: [
        'Settings has a Light foil shine for phones. Full keeps the usual sweep. Light asks less of a phone when the deck is full of foil, and the shine looks simpler.',
      ],
      es: [
        'En Ajustes hay un brillo de foil Ligero para el teléfono. Completo deja el destello de siempre. Ligero le pide menos al teléfono cuando la baraja está llena de foil, y el brillo se ve más simple.',
      ],
      pt: [
        'Em Ajustes há um brilho de foil Leve para o celular. Completo mantém o brilho de sempre. Leve pede menos do celular quando o baralho está cheio de foil, e o brilho fica mais simples.',
      ],
    },
  },
  {
    id: '2026-10-05-ladder-pages',
    date: '2026-10-05',
    title: {
      en: 'The ladders keep going',
      es: 'Las escaleras siguen',
      pt: 'As escadas continuam',
    },
    items: {
      en: [
        'Ranked, Level, and the vs AI win ladders now list every duelist, 50 at a time. Previous and Next sit under the list.',
      ],
      es: [
        'Clasificatoria, Nivel y las escaleras de victorias vs IA ahora muestran a todos los duelistas, de 50 en 50. Anterior y Siguiente quedan bajo la lista.',
      ],
      pt: [
        'Ranqueado, Nível e as escadas de vitórias contra a IA agora mostram todos os duelistas, de 50 em 50. Anterior e Próxima ficam abaixo da lista.',
      ],
    },
  },
  {
    id: '2026-10-05-tablet-set-peek',
    date: '2026-10-05',
    title: {
      en: 'Set cards pulse on tablets',
      es: 'Las cartas Colocadas destellan en tablet',
      pt: 'Cartas Colocadas piscam no tablet',
    },
    items: {
      en: [
        'On a tablet, your Set cards now pulse the same way they do on a phone, so you can see which one is which without tapping each card. On a computer, they still show when you hover them.',
      ],
      es: [
        'En una tablet, tus cartas Colocadas ahora destellan igual que en el teléfono, para que veas cuál es cuál sin tocar cada carta. En una computadora se siguen viendo al pasar el cursor.',
      ],
      pt: [
        'No tablet, suas cartas Colocadas agora piscam como no celular, para você ver qual é qual sem tocar em cada carta. No computador, elas continuam aparecendo ao passar o cursor.',
      ],
    },
  },
  {
    id: '2026-10-05-phone-switch',
    date: '2026-10-05',
    title: {
      en: 'Switching apps keeps the ranked duel',
      es: 'Cambiar de app no suelta el duelo clasificatorio',
      pt: 'Trocar de app não solta o duelo ranqueado',
    },
    items: {
      en: [
        'Leaving the app for a moment on a phone, to answer a message or change the music, keeps the ranked duel. It no longer says the match moved to another device, and it no longer shows a room code.',
      ],
      es: [
        'Salir un momento de la app en el teléfono, para contestar un mensaje o cambiar la música, mantiene el duelo clasificatorio. Ya no dice que la partida pasó a otro dispositivo, y ya no muestra un código de sala.',
      ],
      pt: [
        'Sair um instante do app no celular, para responder uma mensagem ou trocar a música, mantém o duelo ranqueado. Não diz mais que a partida foi para outro aparelho, e não mostra mais um código de sala.',
      ],
    },
  },
  {
    id: '2026-10-05-chat-mute',
    date: '2026-10-05',
    title: {
      en: 'Mute someone in chat',
      es: 'Silencia a alguien en el chat',
      pt: 'Silencie alguém no chat',
    },
    items: {
      en: [
        'Open a duelist’s public page and choose Mute. Their lines disappear for you only. In chat, /mute name does the same, and /unmute name brings them back.',
      ],
      es: [
        'Entra a la página pública de un duelista y elige Silenciar. Sus mensajes desaparecen solo para ti. En el chat, /mute nombre hace lo mismo, y /unmute nombre los vuelve a mostrar.',
      ],
      pt: [
        'Abra a página pública de um duelista e escolha Silenciar. As mensagens dele somem só para você. No chat, /mute nome faz o mesmo, e /unmute nome mostra de novo.',
      ],
    },
  },
  {
    id: '2026-10-05-waboku-chain',
    date: '2026-10-05',
    title: {
      en: 'Waboku chains to a battle boost',
      es: 'Waboku se encadena a un aumento de batalla',
      pt: 'Waboku se encadeia a um aumento de batalha',
    },
    items: {
      en: [
        'In **Minimal** and **Auto**, if the opponent activates **Reinforcements**, **Castle Walls**, or **Reverse Trap** before damage, the game asks for **Waboku** again so you can chain it.',
      ],
      es: [
        'En **Mínimo** y **Auto**, si el rival activa **Reinforcements**, **Castle Walls** o **Reverse Trap** antes del daño, el juego vuelve a preguntar por **Waboku** para que puedas encadenarla.',
      ],
      pt: [
        'Em **Mínimo** e **Auto**, se o oponente ativar **Reinforcements**, **Castle Walls** ou **Reverse Trap** antes do dano, o jogo pergunta de novo por **Waboku** para você encadear.',
      ],
    },
  },
  {
    id: '2026-10-05-tribute-end-phase',
    date: '2026-10-05',
    title: {
      en: 'Tribute Summon closes on End Phase',
      es: 'La Invocación por Sacrificio se cierra en la End Phase',
      pt: 'A Invocação por Tributo fecha na End Phase',
    },
    items: {
      en: [
        'Choosing which monster to Tribute and then clicking End Phase cancels that summon. The prompt no longer stays through your opponent\'s turn or your next one.',
      ],
      es: [
        'Si eliges a qué monstruo sacrificar y luego pulsas la End Phase, esa invocación se cancela. El aviso ya no se queda en el turno de tu rival ni en el tuyo siguiente.',
      ],
      pt: [
        'Se você escolhe qual monstro tributar e depois clica na End Phase, essa invocação é cancelada. O aviso não fica mais no turno do oponente nem no seu próximo.',
      ],
    },
  },
  {
    id: '2026-10-05-change-of-heart-click',
    date: '2026-10-05',
    title: {
      en: 'Change of Heart takes the click',
      es: 'Change of Heart recibe el clic',
      pt: 'Change of Heart recebe o clique',
    },
    items: {
      en: [
        'After you activate **Change of Heart**, the highlighted monster takes your click, including a Defense monster and a tap that lands on the edge of the card. The rest of the board answers again as soon as you pick.',
      ],
      es: [
        'Después de activar **Change of Heart**, el monstruo resaltado recibe el clic, también en Defensa y cuando el toque cae en el borde de la carta. El resto del tablero vuelve a responder en cuanto eliges.',
      ],
      pt: [
        'Depois de ativar **Change of Heart**, o monstro destacado recebe o clique, também em Defesa e quando o toque cai na borda da carta. O resto do tabuleiro volta a responder assim que você escolhe.',
      ],
    },
  },
  {
    id: '2026-10-05-soul-exchange-stack',
    date: '2026-10-05',
    title: {
      en: 'Two Soul Exchange, two Tributes',
      es: 'Dos Soul Exchange, dos sacrificios',
      pt: 'Dois Soul Exchange, dois Tributos',
    },
    items: {
      en: [
        'Activating **Soul Exchange** a second time keeps the first monster. You can Tribute both of your opponent\'s monsters for a Level 7 or higher card.',
      ],
      es: [
        'Activar **Soul Exchange** una segunda vez conserva el primer monstruo. Puedes sacrificar los dos monstruos del rival para una carta de Nivel 7 o más.',
      ],
      pt: [
        'Ativar **Soul Exchange** uma segunda vez mantém o primeiro monstro. Você pode tributar os dois monstros do oponente para uma carta de Nível 7 ou mais.',
      ],
    },
  },
  {
    id: '2026-10-05-pack-bought',
    date: '2026-10-05',
    title: {
      en: 'A shop pack shows up when you buy it',
      es: 'El sobre de tienda se nota al comprarlo',
      pt: 'O pacote da loja aparece ao comprar',
    },
    items: {
      en: [
        'Buying a shop pack now flashes **+1 pack** on the shop card, and the count you already have jumps. The pack is waiting under **Your packs**.',
      ],
      es: [
        'Al comprar un sobre de tienda, la tarjeta de la tienda muestra **+1 sobre** un momento y el conteo de los que ya tienes salta. El sobre queda en **Tus sobres**.',
      ],
      pt: [
        'Ao comprar um pacote da loja, o card da loja mostra **+1 pacote** por um instante e a quantidade que você já tem salta. O pacote fica em **Seus pacotes**.',
      ],
    },
  },
  {
    id: '2026-10-05-last-will-cancel',
    date: '2026-10-05',
    title: {
      en: 'Last Will can be cancelled',
      es: 'Last Will se puede cancelar',
      pt: 'Last Will pode ser cancelado',
    },
    items: {
      en: [
        'Canceling **Last Will** before you pick a monster no longer freezes the duel on a highlighted card. You can still Special Summon later this turn.',
      ],
      es: [
        'Cancelar **Last Will** antes de elegir un monstruo ya no deja el duelo trabado en una carta resaltada. Sigues pudiendo Invocar de Modo Especial después en este turno.',
      ],
      pt: [
        'Cancelar **Last Will** antes de escolher um monstro não trava mais o duelo numa carta destacada. Você ainda pode Invocar por Invocação-Especial depois neste turno.',
      ],
    },
  },
  {
    id: '2026-10-05-cpu-banlist',
    date: '2026-10-05',
    title: {
      en: 'The CPU follows the banlist',
      es: 'La CPU sigue la banlist',
      pt: 'A CPU segue a banlist',
    },
    items: {
      en: [
        'While a temporary banlist is active, CPU decks follow it from Easy through Expert. Master does not: those lists can run up to 3 of the limited cards. Your own deck versus the CPU can still run 3.',
      ],
      es: [
        'Mientras haya una banlist temporal activa, las barajas de la CPU la siguen desde Fácil hasta Experto. En Maestro no: ahí puede llevar hasta 3 copias de esas cartas. Tu baraja contra la CPU puede seguir con 3.',
      ],
      pt: [
        'Enquanto houver uma banlist temporária ativa, os baralhos da CPU a seguem de Fácil até Especialista. Em Mestre não: aí pode ter até 3 cópias dessas cartas. Seu baralho contra a CPU ainda pode ter 3.',
      ],
    },
  },
  {
    id: '2026-10-05-phase-double-click',
    date: '2026-10-05',
    title: {
      en: 'A phase click stays there',
      es: 'El clic de fase se queda ahí',
      pt: 'O clique de fase fica ali',
    },
    items: {
      en: [
        'Double-clicking a phase no longer skips to the one after it. You stop on the phase you clicked.',
      ],
      es: [
        'Un doble clic en una fase ya no te pasa a la siguiente. Te quedas en la fase que elegiste.',
      ],
      pt: [
        'Um clique duplo numa fase não avança mais para a seguinte. Você fica na fase que escolheu.',
      ],
    },
  },
  {
    id: '2026-10-05-username-chat',
    date: '2026-10-05',
    title: {
      en: 'Your name follows you in chat',
      es: 'Tu nombre te sigue en el chat',
      pt: 'Seu nome te segue no chat',
    },
    items: {
      en: [
        'Changing your name updates the lines you already sent, and the people you were tagged in. You can change it once every 30 days.',
      ],
      es: [
        'Al cambiar tu nombre, las líneas que ya enviaste y las menciones pasan a mostrarlo. Puedes cambiarlo una vez cada 30 días.',
      ],
      pt: [
        'Ao mudar seu nome, as linhas que você já enviou e as menções passam a mostrá-lo. Você pode mudá-lo uma vez a cada 30 dias.',
      ],
    },
  },
  {
    id: '2026-10-05-foil-case-pages',
    date: '2026-10-05',
    title: {
      en: 'Foil case turns the page',
      es: 'La vitrina de foils pasa de página',
      pt: 'A vitrine de foils vira a página',
    },
    items: {
      en: [
        'Picking a print for your foil case now moves through your album by pages, so later names are no longer cut off.',
      ],
      es: [
        'Al elegir una copia para la vitrina, el álbum se recorre por páginas. Los nombres que van más adelante ya no se cortan.',
      ],
      pt: [
        'Ao escolher uma cópia para a vitrine, o álbum passa de página. Os nomes mais adiante não ficam mais de fora.',
      ],
    },
  },
  {
    id: '2026-10-05-duel-replay',
    date: '2026-10-05',
    title: {
      en: 'Replay your last duels',
      es: 'Repite tus últimos duelos',
      pt: 'Repita seus últimos duelos',
    },
    items: {
      en: [
        'Your public page lists recent duels. The last 10 can be replayed, with pause, step, and speed.',
      ],
      es: [
        'Tu página pública lista los duelos recientes. Los últimos 10 se pueden repetir, con pausa, paso y velocidad.',
      ],
      pt: [
        'Sua página pública lista os duelos recentes. Os últimos 10 podem ser repetidos, com pausa, passo e velocidade.',
      ],
    },
  },
  {
    id: '2026-10-05-watch-duels',
    date: '2026-10-05',
    title: {
      en: 'Watch live duels',
      es: 'Ver duelos en vivo',
      pt: 'Assistir duelos ao vivo',
    },
    items: {
      en: [
        'Open **Watch duels** from Home or the lobby to spectate a ranked match or a private room. Hands and face-down cards stay hidden.',
      ],
      es: [
        'Abre **Ver duelos** desde el inicio o el lobby para espectar una partida clasificatoria o una sala privada. Las manos y las cartas boca abajo quedan ocultas.',
      ],
      pt: [
        'Abra **Assistir duelos** na página inicial ou no lobby para assistir uma partida ranqueada ou uma sala privada. As mãos e as cartas viradas para baixo ficam ocultas.',
      ],
    },
  },
  {
    id: '2026-10-05-end-phase-pass',
    date: '2026-10-05',
    title: {
      en: 'End Phase passes in one click',
      es: 'La End Phase pasa en un clic',
      pt: 'A End Phase passa em um clique',
    },
    items: {
      en: [
        'One click on End Phase waits for your opponent\'s response, then passes the turn.',
      ],
      es: [
        'Un clic en la End Phase espera la respuesta de tu oponente y luego pasa el turno.',
      ],
      pt: [
        'Um clique na End Phase espera a resposta do oponente e depois passa o turno.',
      ],
    },
  },
  {
    id: '2026-10-05-offering-minimal',
    date: '2026-10-05',
    title: {
      en: 'Ultimate Offering asks in Minimal',
      es: 'Ultimate Offering pregunta en Mínimo',
      pt: 'Ultimate Offering pergunta no Mínimo',
    },
    items: {
      en: [
        'In **Minimal**, a face-down **Ultimate Offering** now asks during the opponent\'s Battle Phase: once when that phase starts, and again when they declare an attack. Activating it still does not pay 500 LP until you choose to.',
      ],
      es: [
        'En **Mínimo**, **Ultimate Offering** boca abajo ahora pregunta en la Battle Phase del rival: una vez al empezar esa fase, y otra vez cuando declara un ataque. Activarla todavía no cobra 500 LP hasta que eliges pagar.',
      ],
      pt: [
        'No **Mínimo**, **Ultimate Offering** virada para baixo agora pergunta na Fase de Batalha do oponente: uma vez no começo dessa fase, e de novo quando ele declara um ataque. Ativar ainda não cobra 500 LP até você escolher pagar.',
      ],
    },
  },
  {
    id: '2026-10-05-coin-rewards',
    date: '2026-10-05',
    title: {
      en: 'Coins are lower, and level keeps going',
      es: 'Las monedas bajaron, y el nivel sigue',
      pt: 'As moedas baixaram, e o nível continua',
    },
    items: {
      en: [
        'A Master Pack costs 20 coins. Duel coins are lower. Casual pays no coins.',
        'Against the CPU, the first 6 finished duels of the day pay coins. After that, only XP.',
        'In ranked, the first 15 finished duels of the day pay the full amount. Later duels pay less, down to 1 coin.',
        'Every 3 CPU duels, playing one ranked match pays 5 coins, once a day.',
        'The note that someone is waiting for ranked pays at most 3 times a day. You can turn those notes off in Settings.',
        'The queue wait only has the Dark Magician Girl run.',
        'Each level gives one Master Pack. There is no level cap. Level goes back to 1 when the season rotates.',
      ],
      es: [
        'Un Master Pack cuesta 20 monedas. Las monedas de los duelos bajaron. Casual no paga monedas.',
        'Contra la IA, los primeros 6 duelos terminados del día pagan monedas. Después solo dan XP.',
        'En clasificatoria, los primeros 15 duelos terminados del día pagan el monto completo. Luego baja hasta 1 moneda.',
        'Cada 3 duelos contra la IA, jugar una clasificatoria paga 5 monedas, una vez al día.',
        'El aviso de que alguien espera una clasificatoria paga como máximo 3 veces al día. Puedes apagar esos avisos en Configuración.',
        'La espera de la cola solo tiene la carrera de la Maga Oscura.',
        'Cada nivel da un Master Pack. El nivel no tiene tope. Vuelve a 1 cuando rota la temporada.',
      ],
      pt: [
        'Um Master Pack custa 20 moedas. As moedas dos duelos baixaram. O casual não paga moedas.',
        'Contra a CPU, os primeiros 6 duelos terminados do dia pagam moedas. Depois, só XP.',
        'Na ranqueada, os primeiros 15 duelos terminados do dia pagam o valor cheio. Depois baixa até 1 moeda.',
        'A cada 3 duelos contra a CPU, jogar uma ranqueada paga 5 moedas, uma vez por dia.',
        'O aviso de que alguém espera uma ranqueada paga no máximo 3 vezes por dia. Você pode desligar esses avisos em Configurações.',
        'A espera da fila só tem a corrida da Dark Magician Girl.',
        'Cada nível dá um Master Pack. Não há teto de nível. Ele volta a 1 quando a temporada gira.',
      ],
    },
  },
  {
    id: '2026-10-05-pvp-streak',
    date: '2026-10-05',
    title: {
      en: 'Win and loss streaks',
      es: 'Rachas de victorias y derrotas',
      pt: 'Sequências de vitórias e derrotas',
    },
    items: {
      en: [
        'Before a PvP duel, the duelist cards show a green chip for a win streak and a red chip for a loss streak. It appears from two in a row. A draw clears it.',
      ],
      es: [
        'Antes de un duelo PvP, la presentación de los duelistas muestra un chip verde por racha de victorias y uno rojo por racha de derrotas. Aparece desde dos seguidas. Un empate la corta.',
      ],
      pt: [
        'Antes de um duelo PvP, os cartões dos duelistas mostram um chip verde para uma sequência de vitórias e um vermelho para uma sequência de derrotas. Aparece a partir de duas seguidas. Um empate zera a sequência.',
      ],
    },
  },
  {
    id: '2026-10-05-trap-modes',
    date: '2026-10-05',
    title: {
      en: 'Counter Traps in Auto and Minimal',
      es: 'Counter Trap en Auto y Mínimo',
      pt: 'Counter Trap em Auto e Mínimo',
    },
    items: {
      en: [
        'Auto and Minimal now ask for Solemn Judgment, Magic Jammer, Seven Tools of the Bandit, and Horn of Heaven only when the opponent activates or summons something those cards can negate. All still offers them against your own cards. Robbin\' Goblin no longer pauses those modes; flip it by tapping the Set card.',
      ],
      es: [
        'Auto y Mínimo ahora preguntan por Solemn Judgment, Magic Jammer, Seven Tools of the Bandit y Horn of Heaven solo cuando el rival activa o invoca algo que esas cartas pueden negar. Todo sigue ofreciéndolas contra tus propias cartas. Robbin\' Goblin ya no detiene esos modos; voltéalo tocando la carta colocada.',
      ],
      pt: [
        'Auto e Mínimo agora perguntam por Solemn Judgment, Magic Jammer, Seven Tools of the Bandit e Horn of Heaven só quando o oponente ativa ou invoca algo que essas cartas podem negar. Tudo continua oferecendo elas contra as suas próprias cartas. Robbin\' Goblin não pausa mais esses modos; vire tocando a carta baixada.',
      ],
    },
  },
  {
    id: '2026-10-05-season-rewards',
    date: '2026-10-05',
    title: {
      en: 'Season rewards',
      es: 'Premios de temporada',
      pt: 'Prêmios de temporada',
    },
    items: {
      en: [
        'When a season ends, your final league pays shop boosters: Duelist 2, Challenger 5, Champion 7, King of Games 10. Placement matches do not count.',
        'Starting next season, every account gets one Metal Raiders booster the first time you sign in. It holds 5 cards from that set.',
        'The top 10 of each CPU ladder (Hard, Expert, and Master), including ties for 10th, get 1 Master Pack when the season closes. Only that season’s wins count.',
      ],
      es: [
        'Al cerrar una temporada, tu liga final paga sobres de tienda: Duelista 2, Retador 5, Campeón 7 y Rey de los Juegos 10. La colocación no cuenta.',
        'Desde la próxima temporada, cada cuenta recibe un sobre de Metal Raiders la primera vez que entra. Trae 5 cartas de ese set.',
        'El top 10 de cada escalera contra la CPU (Difícil, Experto y Maestro), incluidos los empates en el puesto 10, recibe 1 Master Pack al cerrar la temporada. Solo cuentan las victorias de esa temporada.',
      ],
      pt: [
        'Quando uma temporada fecha, sua liga final paga pacotes da loja: Duelista 2, Desafiante 5, Campeão 7 e Rei dos Jogos 10. A colocação não conta.',
        'A partir da próxima temporada, cada conta ganha um pacote de Metal Raiders na primeira vez que você entra. Ele traz 5 cartas desse set.',
        'O top 10 de cada escada contra a CPU (Difícil, Especialista e Mestre), incluindo empates no 10º lugar, recebe 1 Master Pack quando a temporada fecha. Só contam as vitórias dessa temporada.',
      ],
    },
  },
  {
    id: '2026-10-05-faq',
    date: '2026-10-05',
    title: {
      en: 'Frequently asked questions',
      es: 'Preguntas frecuentes',
      pt: 'Perguntas frequentes',
    },
    items: {
      en: [
        'A new FAQ page answers what the reset keeps and what it clears. Open it from the top bar or from Home.',
        'It also says the temporary banlist only applies in PvP and resets with the new set.',
      ],
      es: [
        'Una página nueva de preguntas frecuentes responde qué se queda y qué se borra con el reset. Ábrela desde la barra de arriba o desde Inicio.',
        'También aclara que la banlist temporal solo aplica en PvP y que se reinicia con el set nuevo.',
      ],
      pt: [
        'Uma página nova de perguntas frequentes responde o que fica e o que some no reset. Abra pela barra de cima ou pelo Início.',
        'Também deixa claro que a banlist temporária só vale no PvP e que é reiniciada com o set novo.',
      ],
    },
  },
  {
    id: '2026-10-05-league-cosmetics',
    date: '2026-10-05',
    title: {
      en: 'League rewards',
      es: 'Premios de liga',
      pt: 'Prêmios de liga',
    },
    items: {
      en: [
        'Each ranked league has its own sleeve, playmat, and frame. Reach a league to unlock its set and every set below it. They stay yours after a demotion or a new season.',
        'Duelists who already reached a league get those sets right away. Find them under League rewards in your profile.',
      ],
      es: [
        'Cada liga clasificatoria tiene su propia funda, tapete y marco. Llega a una liga para desbloquear su set y todos los de abajo. Son tuyos aunque bajes o empiece otra temporada.',
        'Si ya llegaste a una liga, esos sets ya están desbloqueados. Búscalos en Premios de liga dentro de tu perfil.',
      ],
      pt: [
        'Cada liga ranqueada tem seu próprio protetor, tapete e moldura. Chegue a uma liga para desbloquear o conjunto dela e todos os de baixo. Eles continuam seus mesmo se você cair ou começar outra temporada.',
        'Se você já chegou a uma liga, esses conjuntos já estão liberados. Procure em Prêmios de liga no seu perfil.',
      ],
    },
  },
  {
    id: '2026-10-05-collection-missions',
    date: '2026-10-05',
    title: {
      en: 'Collection missions',
      es: 'Misiones de colección',
      pt: 'Missões de coleção',
    },
    items: {
      en: [
        'Missions has a Collection tab. Collecting cards, sets, types, attributes, and rarities pays coins. The hardest steps also give a shop pack. Progress counts the cards you already own.',
      ],
      es: [
        'Misiones tiene una pestaña Colección. Coleccionar cartas, sets, tipos, atributos y rarezas paga monedas. Los peldaños más difíciles también dan un sobre de tienda. El progreso cuenta las cartas que ya tienes.',
      ],
      pt: [
        'Missões tem uma aba Coleção. Colecionar cartas, sets, tipos, atributos e raridades paga moedas. As etapas mais difíceis também dão um pacote da loja. O progresso conta as cartas que você já tem.',
      ],
    },
  },
  {
    id: '2026-09-27-ranked-call-ignore',
    date: '2026-09-27',
    title: {
      en: 'Ignore stays in the CPU duel',
      es: 'Ignorar te deja en el duelo contra la CPU',
      pt: 'Ignorar te deixa no duelo contra a CPU',
    },
    items: {
      en: [
        'Tapping Ignore on the “someone is waiting for ranked” note during a CPU duel only closes that note: it does not start the ranked duel and it does not play the board under the note.',
      ],
      es: [
        'Tocar Ignorar en el aviso de “alguien espera una partida ranqueada” durante un duelo contra la CPU solo cierra ese aviso: no inicia el duelo ranqueado ni juega el tablero debajo del aviso.',
      ],
      pt: [
        'Tocar Ignorar no aviso de “alguém espera uma partida ranqueada” durante um duelo contra a CPU só fecha esse aviso: não inicia o duelo ranqueado e não joga o tabuleiro embaixo do aviso.',
      ],
    },
  },
  {
    id: '2026-09-27-queue-star',
    date: '2026-09-27',
    title: {
      en: 'The star stays in the queue',
      es: 'La estrella se queda en la cola',
      pt: 'A estrela fica na fila',
    },
    items: {
      en: [
        'Daily missions that ask you to tap the star no longer show a star on Missions. Tap it while you wait in the Quick Match or casual queue. Each tap still counts toward the mission.',
      ],
      es: [
        'Las misiones diarias que piden tocar la estrella ya no muestran una estrella en Misiones. Tócala mientras esperas en la cola de partida rápida o casual. Cada toque sigue contando para la misión.',
      ],
      pt: [
        'As missões diárias que pedem para tocar a estrela não mostram mais uma estrela em Missões. Toque nela enquanto espera na fila da partida rápida ou casual. Cada toque continua contando para a missão.',
      ],
    },
  },

  {
    id: '2026-09-27-play-time',
    date: '2026-09-27',
    title: {
      en: 'Time played on your page',
      es: 'Tiempo jugado en tu página',
      pt: 'Tempo jogado na sua página',
    },
    items: {
      en: [
        'Your public page now shows how long your finished duels have lasted, including matches already on record.',
      ],
      es: [
        'Tu página pública ahora muestra cuánto han durado tus duelos terminados, incluidos los que ya estaban registrados.',
      ],
      pt: [
        'Sua página pública agora mostra quanto duraram seus duelos terminados, inclusive os que já estavam registrados.',
      ],
    },
  },
  {
    id: '2026-09-26-same-league-lp',
    date: '2026-09-26',
    title: {
      en: 'Even points inside a league',
      es: 'Puntos parejos dentro de una liga',
      pt: 'Pontos iguais dentro de uma liga',
    },
    items: {
      en: [
        'A Quick Match between two players in the same league now moves 20 league points each way. Champion I against Champion II is the same league, so the division no longer shrinks that result. Playing that rival again the same day pays the full points.',
      ],
      es: [
        'Una partida rápida entre dos jugadores de la misma liga ahora mueve 20 puntos de liga para cada uno. Champion I contra Champion II es la misma liga, así que la división ya no recorta ese resultado. Volver a jugar contra ese rival el mismo día paga los puntos completos.',
      ],
      pt: [
        'Uma partida rápida entre dois jogadores da mesma liga agora move 20 pontos de liga para cada um. Champion I contra Champion II é a mesma liga, então a divisão não reduz mais esse resultado. Jogar de novo contra esse rival no mesmo dia paga os pontos completos.',
      ],
    },
  },
  {
    id: '2026-09-26-casual-queue',
    date: '2026-09-26',
    title: {
      en: 'Casual queue',
      es: 'Cola casual',
      pt: 'Fila casual',
    },
    items: {
      en: [
        'A casual queue sits next to Quick Match. It pairs you with anyone, your league does not change, and a win pays 1 coin.',
      ],
      es: [
        'Hay una cola casual junto a la partida rápida. Te empareja con cualquiera, tu liga no se mueve y una victoria paga 1 moneda.',
      ],
      pt: [
        'Há uma fila casual ao lado da partida rápida. Ela te emparelha com qualquer pessoa, sua liga não muda e uma vitória paga 1 moeda.',
      ],
    },
  },
  {
    id: '2026-09-26-ranked-lp',
    date: '2026-09-26',
    title: {
      en: 'More points for a win below your rank',
      es: 'Más puntos al ganar contra un rango más bajo',
      pt: 'Mais pontos ao ganhar de um rank mais baixo',
    },
    items: {
      en: [
        'A Quick Match win against someone several divisions below pays more league points. Beating someone far below no longer stops at 1 or 2. A loss against someone that far below has a cap, so one upset does not wipe the session.',
      ],
      es: [
        'Si ganas una partida rápida contra alguien que está varias divisiones por debajo, subes más puntos de liga. Ganar contra alguien muy por debajo ya no se queda en 1 o 2 puntos. Si pierdes contra alguien tan abajo, la baja tiene un tope, así que una sorpresa no borra la sesión.',
      ],
      pt: [
        'Se você ganha uma partida rápida contra alguém várias divisões abaixo, você sobe mais pontos de liga. Ganhar de alguém muito abaixo não fica mais em 1 ou 2 pontos. Se você perde contra alguém tão abaixo, a queda tem um limite, então uma surpresa não apaga a sessão.',
      ],
    },
  },
  {
    id: '2026-09-26-chat-badge',
    date: '2026-09-26',
    title: {
      en: 'Chat number while you are here',
      es: 'Número del chat mientras estás aquí',
      pt: 'Número do chat enquanto você está aqui',
    },
    items: {
      en: [
        'The chat number stays while you use the site. It clears only when that chat is open on the latest line. If you scrolled up, Latest shows how many new lines arrived.',
      ],
      es: [
        'El número del chat sigue ahí mientras usas el sitio. Se borra solo cuando ese chat está abierto en la última línea. Si subiste en el historial, Al final muestra cuántos mensajes nuevos llegaron.',
      ],
      pt: [
        'O número do chat continua lá enquanto você usa o site. Ele some só quando esse chat está aberto na última linha. Se você subiu no histórico, Ao fim mostra quantas mensagens novas chegaram.',
      ],
    },
  },
  {
    id: '2026-09-26-mailbox',
    date: '2026-09-26',
    title: {
      en: 'Mailbox',
      es: 'Buzón',
      pt: 'Correio',
    },
    items: {
      en: [
        'The mailbox in the top bar holds news to review and messages from the team. A reward in a message waits there until you claim it.',
      ],
      es: [
        'El buzón de la barra de arriba guarda las novedades por revisar y los mensajes del equipo. Una recompensa en un mensaje espera ahí hasta que la reclamas.',
      ],
      pt: [
        'O correio na barra de cima guarda as novidades para ver e as mensagens da equipe. Uma recompensa numa mensagem espera lá até você resgatá-la.',
      ],
    },
  },
  {
    id: '2026-09-26-pvp-clock',
    date: '2026-09-26',
    title: {
      en: 'Shorter PvP clock',
      es: 'Reloj de PvP más corto',
      pt: 'Relógio de PvP mais curto',
    },
    items: {
      en: [
        'In a duel against another person, the clock starts at **2:00** and runs while you can act. At zero, you lose.',
        'When the turn changes, your turn adds **15 seconds** and the opponent\'s turn adds **8 seconds**, up to **3:00**. Reordering your hand does not add time.',
        'When that number changes, it grows and settles, the same way Life Points do.',
      ],
      es: [
        'En un duelo contra otra persona, el reloj empieza en **2:00** y corre mientras puedes jugar. En cero, pierdes.',
        'Al cambiar el turno, el tuyo suma **15 segundos** y el del rival suma **8 segundos**, hasta **3:00**. Reordenar la mano no suma tiempo.',
        'Cuando ese número cambia, crece y vuelve a su tamaño, igual que los Life Points.',
      ],
      pt: [
        'Num duelo contra outra pessoa, o relógio começa em **2:00** e corre enquanto você pode jogar. Em zero, você perde.',
        'Quando o turno muda, o seu soma **15 segundos** e o do oponente soma **8 segundos**, até **3:00**. Reordenar a mão não soma tempo.',
        'Quando esse número muda, ele cresce e volta ao tamanho, como os Pontos de Vida.',
      ],
    },
  },
  {
    id: '2026-09-26-target-button',
    date: '2026-09-26',
    title: {
      en: 'One highlighted card has a button',
      es: 'Una carta resaltada tiene un botón',
      pt: 'Um card destacado tem um botão',
    },
    items: {
      en: [
        'When a Spell, Trap, or Flip effect needs exactly one card on the field, a button confirms it. Enter does the same. You can still click the card, including the glow around a sideways monster.',
      ],
      es: [
        'Cuando una Mágica, una Trampa o un efecto de volteo pide exactamente una carta en el campo, un botón la confirma. Enter hace lo mismo. Sigues pudiendo tocar la carta, incluido el brillo alrededor de un monstruo de lado.',
      ],
      pt: [
        'Quando uma Magia, uma Armadilha ou um efeito de virar pede exatamente um card no campo, um botão confirma esse card. Enter faz o mesmo. Você ainda pode tocar o card, inclusive o brilho em volta de um monstro de lado.',
      ],
    },
  },
  {
    id: '2026-09-26-pack-captions',
    date: '2026-09-26',
    title: {
      en: 'Pack opening stays quiet',
      es: 'Abrir un sobre ya no narra lo obvio',
      pt: 'Abrir um pacote não narra mais o óbvio',
    },
    items: {
      en: [
        'While the cards slide into the binder, and on the summary after, the line that said they went into your collection is gone.',
      ],
      es: [
        'Mientras las cartas entran al álbum, y en el resumen de después, ya no aparece la línea que decía que fueron a tu colección.',
      ],
      pt: [
        'Enquanto os cards entram no fichário, e no resumo depois, a linha que dizia que foram para a sua coleção não aparece mais.',
      ],
    },
  },
  {
    id: '2026-09-26-trap-tap',
    date: '2026-09-26',
    title: {
      en: 'A trap question on the phone',
      es: 'La pregunta de una trampa en el teléfono',
      pt: 'A pergunta de uma armadilha no celular',
    },
    items: {
      en: [
        'On a phone, while you are looking at the opponent\'s field, the trap question sits at the top. It no longer covers the summoned monster.',
        'A finger that was already down when that question appears does not activate the trap. Lift and tap the button if you want it.',
      ],
      es: [
        'En el teléfono, mientras miras el campo del rival, la pregunta de la trampa queda arriba. Ya no tapa al monstruo invocado.',
        'Un dedo que ya estaba apoyado cuando aparece esa pregunta no activa la trampa. Levanta y toca el botón si la quieres.',
      ],
      pt: [
        'No celular, enquanto você olha o campo do oponente, a pergunta da armadilha fica em cima. Ela não cobre mais o monstro invocado.',
        'Um dedo que já estava na tela quando essa pergunta aparece não ativa a armadilha. Solte e toque o botão se quiser ativá-la.',
      ],
    },
  },
  {
    id: '2026-09-25-armed-ninja',
    date: '2026-09-25',
    title: {
      en: 'Armed Ninja does not stall an empty field',
      es: 'Armed Ninja ya no traba un campo vacío',
      pt: 'Armed Ninja não trava mais um campo vazio',
    },
    items: {
      en: [
        '**Armed Ninja** no longer asks you to pick a target when the Spell that flipped it has already gone to the Graveyard and nothing else is a Spell or a Set card. The turn continues.',
      ],
      es: [
        '**Armed Ninja** ya no te pide un objetivo cuando la Mágica que lo volteó ya se fue al Cementerio y no queda otra Mágica ni una carta boca abajo. El turno sigue.',
      ],
      pt: [
        '**Armed Ninja** não pede mais um alvo quando a Magia que o virou já foi para o Cemitério e não resta outra Magia nem um card baixado. O turno continua.',
      ],
    },
  },
  {
    id: '2026-09-25-deck-undo',
    date: '2026-09-25',
    title: {
      en: 'Undo in the deck builder',
      es: 'Deshacer en el constructor',
      pt: 'Desfazer no construtor',
    },
    items: {
      en: [
        'The deck builder has an Undo button. It puts back the last card you removed, and that card flashes so you can see which one it was. It also steps back an add, a sort, a clear, or a rarity swap. Ctrl+Z does the same.',
      ],
      es: [
        'El constructor tiene un botón Deshacer. Devuelve la última carta que quitaste, y esa carta parpadea para que veas cuál era. También retrocede un agregado, un orden, un vaciado o un cambio de rareza. Ctrl+Z hace lo mismo.',
      ],
      pt: [
        'O construtor tem um botão Desfazer. Devolve o último card que você tirou, e esse card pisca para você ver qual era. Também volta uma adição, uma ordenação, uma limpeza ou uma troca de raridade. Ctrl+Z faz o mesmo.',
      ],
    },
  },
  {
    id: '2026-09-25-collection-sort',
    date: '2026-09-25',
    title: {
      en: 'Card Collection keeps your sort',
      es: 'La colección recuerda el orden',
      pt: 'A coleção lembra a ordem',
    },
    items: {
      en: [
        'The Card Collection in the deck builder remembers how you sorted it. Leave it on Obtained (newest) and it stays that way when you come back. Reset filters no longer changes the sort.',
      ],
      es: [
        'La colección de cartas del constructor recuerda cómo la ordenaste. Si la dejas en Adquisición (reciente), sigue así cuando vuelves. Quitar filtros ya no cambia el orden.',
      ],
      pt: [
        'A coleção de cards no construtor lembra a ordem que você escolheu. Se deixar em Obtido (mais novo), continua assim quando você volta. Limpar filtros não muda mais a ordem.',
      ],
    },
  },
  {
    id: '2026-09-25-last-will',
    date: '2026-09-25',
    title: {
      en: 'Last Will remembers this turn',
      es: 'Last Will recuerda este turno',
      pt: 'Last Will lembra este turno',
    },
    items: {
      en: [
        '**Last Will** counts a monster you controlled that already went to your ' +
          'Graveyard this turn. Each copy can Special Summon once.',
        'Cancel the summon and you can still use it later this turn. It still ends ' +
          'when your turn ends. A monster that goes to the opponent\'s Graveyard does not count.',
      ],
      es: [
        '**Last Will** cuenta un monstruo que controlabas y que ya fue a tu ' +
          'Cementerio en este turno. Cada copia puede Invocar de Modo Especial una vez.',
        'Si cancelas la Invocación, puedes usarla después en este turno. Sigue ' +
          'terminando cuando termina tu turno. Un monstruo que va al Cementerio del rival no cuenta.',
      ],
      pt: [
        '**Last Will** conta um monstro que você controlava e que já foi ao seu ' +
          'Cemitério neste turno. Cada cópia pode Invocar por Invocação-Especial uma vez.',
        'Se você cancelar a Invocação, ainda pode usá-la depois neste turno. Ela ' +
          'continua terminando quando o seu turno termina. Um monstro que vai ao Cemitério do oponente não conta.',
      ],
    },
  },
  {
    id: '2026-09-25-best-rarities',
    date: '2026-09-25',
    title: {
      en: 'Best rarities in the deck builder',
      es: 'Mejores rarezas en el constructor',
      pt: 'Melhores raridades no construtor',
    },
    items: {
      en: [
        'The deck builder has a Best rarities button. It swaps copies already in the deck for the rarest prints you own of that same card. It does not add or remove cards.',
      ],
      es: [
        'El constructor tiene un botón Mejores rarezas. Cambia las copias que ya están en la baraja por las impresiones más raras que tienes de esa misma carta. No agrega ni quita cartas.',
      ],
      pt: [
        'O construtor tem um botão Melhores raridades. Troca as cópias que já estão no baralho pelas impressões mais raras que você tem desse mesmo card. Não adiciona nem remove cards.',
      ],
    },
  },
  {
    id: '2026-09-25-target-hover',
    date: '2026-09-25',
    title: {
      en: 'Picking a monster no longer freezes the duel',
      es: 'Elegir un monstruo ya no congela el duelo',
      pt: 'Escolher um monstro não trava mais o duelo',
    },
    items: {
      en: [
        'Activating a card that asks for a monster no longer freezes the duel when the cursor is resting on a card in your hand. Change of Heart and Dragon Treasure were the ones that did this.',
      ],
      es: [
        'Activar una carta que pide un monstruo ya no congela el duelo cuando el cursor está sobre una carta de tu mano. Pasaba con Change of Heart y Dragon Treasure.',
      ],
      pt: [
        'Ativar uma carta que pede um monstro não trava mais o duelo quando o cursor está sobre uma carta da sua mão. Acontecia com Change of Heart e Dragon Treasure.',
      ],
    },
  },
  {
    id: '2026-09-25-just-desserts',
    date: '2026-09-25',
    title: {
      en: 'Just Desserts asks at Battle Phase',
      es: 'Just Desserts pregunta en la Battle Phase',
      pt: 'Just Desserts pergunta na Fase de Batalha',
    },
    items: {
      en: [
        'With Traps set to Minimal, Just Desserts asks once when the opponent\'s Battle Phase starts, if they control a monster. Passing leaves it Set. Waboku and the other battle traps still ask when an attack is declared.',
      ],
      es: [
        'Con Trampas en Mínimo, Just Desserts pregunta una vez cuando empieza la Battle Phase del rival, si controla un monstruo. Pasar la deja Colocada. Waboku y las otras trampas de batalla siguen preguntando cuando se declara un ataque.',
      ],
      pt: [
        'Com Armadilhas em Mínimo, Just Desserts pergunta uma vez quando começa a Fase de Batalha do oponente, se ele controlar um monstro. Passar a deixa Baixada. Waboku e as outras armadilhas de batalha continuam perguntando quando um ataque é declarado.',
      ],
    },
  },
  {
    id: '2026-09-25-duel-resume',
    date: '2026-09-25',
    title: {
      en: 'A duel survives an update',
      es: 'Un duelo sobrevive una actualización',
      pt: 'Um duelo sobrevive a uma atualização',
    },
    items: {
      en: [
        'If the server updates in the middle of a duel, the match stays. When the connection comes back, you return to the same board. The clock does not run while the server is down. If nobody is back within a minute of the server returning, the duel is surrendered as usual.',
      ],
      es: [
        'Si el servidor se actualiza en medio de un duelo, la partida sigue. Cuando vuelve la conexión, entras al mismo tablero. El reloj no corre mientras el servidor está caído. Si nadie regresa en el minuto después de que el servidor vuelve, el duelo se rinde como siempre.',
      ],
      pt: [
        'Se o servidor atualizar no meio de um duelo, a partida continua. Quando a conexão voltar, você entra no mesmo tabuleiro. O relógio não corre enquanto o servidor está fora. Se ninguém voltar no minuto depois que o servidor retorna, o duelo é abandonado como sempre.',
      ],
    },
  },
  {
    id: '2026-09-25-duel-rotate',
    date: '2026-09-25',
    title: {
      en: 'The duel turns the phone',
      es: 'El duelo gira el celular',
      pt: 'O duelo gira o celular',
    },
    items: {
      en: [
        'On an Android phone, including the installed app, entering a duel turns the screen sideways again. If it stays upright, tap the board once.',
      ],
      es: [
        'En un celular Android, también con la app instalada, entrar a un duelo vuelve a poner la pantalla de lado. Si se queda vertical, toca el tablero una vez.',
      ],
      pt: [
        'No celular Android, inclusive no app instalado, entrar num duelo volta a deitar a tela. Se ela ficar em pé, toque o tabuleiro uma vez.',
      ],
    },
  },
  {
    id: '2026-09-25-queue-run',
    date: '2026-09-25',
    title: {
      en: 'A run while you wait',
      es: 'Una carrera mientras esperas',
      pt: 'Uma corrida enquanto você espera',
    },
    items: {
      en: [
        'While you wait for a ranked match, the game is picked at random: tap the star, or Dark Magician Girl runs and jumps face-down cards. Each card you clear rolls the same chance as the small star. When it hits, a coin shines and drops into your wallet. On a phone, the button to leave the queue sits off to the side.',
      ],
      es: [
        'Mientras buscas una clasificatoria, el juego sale al azar: o tocas la estrella, o la Maga Oscura corre y salta cartas boca abajo. Cada carta que pasas tiene la misma probabilidad que la estrellita. Si pega, una moneda brilla y entra a tu monedero. En el celular, el botón para salir de la cola queda a un lado.',
      ],
      pt: [
        'Enquanto você espera uma ranqueada, o jogo sai no acaso: ou você toca a estrela, ou a Dark Magician Girl corre e pula cards virados para baixo. Cada card que você passa tem a mesma chance da estrelinha. Se der certo, uma moeda brilha e entra na carteira. No celular, o botão para sair da fila fica de lado.',
      ],
    },
    figures: [
      {
        item: 0,
        src: '/assets/changelog/2026-09-25-queue-run.png',
        alt: {
          en: 'Dark Magician Girl jumping a face-down card while waiting in the ranked queue',
          es: 'La Maga Oscura saltando una carta boca abajo mientras espera en la clasificatoria',
          pt: 'A Dark Magician Girl pulando um card virado para baixo enquanto espera na ranqueada',
        },
      },
    ],
  },
  {
    id: '2026-09-25-pack-bulk',
    date: '2026-09-25',
    title: {
      en: 'Buy and open packs in batches',
      es: 'Compra y abre sobres de a varios',
      pt: 'Compre e abra pacotes em lote',
    },
    items: {
      en: [
        'In the shop, Buy 3, Buy 5, and Buy 10 show up when you have the coins. Opening adds Open 3 next to Open 5 and Open 10, on your packs and after a batch.',
      ],
      es: [
        'En la tienda, Comprar 3, Comprar 5 y Comprar 10 aparecen cuando tienes las monedas. Al abrir, Abrir 3 queda junto a Abrir 5 y Abrir 10, en tus sobres y al terminar un lote.',
      ],
      pt: [
        'Na loja, Comprar 3, Comprar 5 e Comprar 10 aparecem quando você tem as moedas. Ao abrir, Abrir 3 fica junto de Abrir 5 e Abrir 10, nos seus pacotes e ao terminar um lote.',
      ],
    },
  },
  {
    id: '2026-09-25-chat-scroll',
    date: '2026-09-25',
    title: {
      en: 'Chat stays where you are reading',
      es: 'El chat se queda donde estás leyendo',
      pt: 'O chat fica onde você está lendo',
    },
    items: {
      en: [
        'Scrolling up in chat no longer jumps you to the newest line when someone else writes. A Latest button brings you back down. Your own message, opening the panel, and switching Global or Duel still land on the latest line.',
      ],
      es: [
        'Subir en el chat ya no te lleva al mensaje más nuevo cuando escribe otra persona. El botón Al final te regresa abajo. Tu propio mensaje, abrir el panel y cambiar entre Global y Duelo siguen dejando a la vista la última línea.',
      ],
      pt: [
        'Subir no chat não te leva mais à mensagem mais nova quando outra pessoa escreve. O botão Ao fim te traz de volta para baixo. Sua própria mensagem, abrir o painel e trocar entre Global e Duelo continuam mostrando a última linha.',
      ],
    },
  },
  {
    id: '2026-09-25-pack-claim-toast',
    date: '2026-09-25',
    title: {
      en: 'Mission claim waits out a pack opening',
      es: 'Reclamar misiones espera a que termines el sobre',
      pt: 'Resgatar missões espera o pacote terminar',
    },
    items: {
      en: [
        'The claim balloon no longer sits on top of a pack you are opening. It waits until you close the opening, so leaving for Missions cannot interrupt the first pack of a batch. The cards are already in your collection before the first pack appears.',
      ],
      es: [
        'El globo de reclamar ya no se pone encima de un sobre que estás abriendo. Espera a que cierres la apertura, así ir a Misiones no corta el primero de un lote. Las cartas ya están en tu colección antes de que aparezca el primer sobre.',
      ],
      pt: [
        'O balão de resgatar não fica mais por cima de um pacote que você está abrindo. Ele espera você fechar a abertura, então ir a Missões não corta o primeiro de um lote. As cartas já estão na sua coleção antes do primeiro pacote aparecer.',
      ],
    },
  },
  {
    id: '2026-09-24-chat-profile',
    date: '2026-09-24',
    title: {
      en: 'Names in chat open a profile',
      es: 'Los nombres del chat abren un perfil',
      pt: 'Os nomes no chat abrem um perfil',
    },
    items: {
      en: [
        'In chat, the name above a message and a name tagged with @ open that duelist’s public page.',
      ],
      es: [
        'En el chat, el nombre sobre un mensaje y un nombre etiquetado con @ abren la página pública de ese duelista.',
      ],
      pt: [
        'No chat, o nome acima de uma mensagem e um nome marcado com @ abrem a página pública desse duelista.',
      ],
    },
  },
  {
    id: '2026-09-24-mobile-cancel',
    date: '2026-09-24',
    title: {
      en: 'Cancel on the phone',
      es: 'Cancelar en el celular',
      pt: 'Cancelar no celular',
    },
    items: {
      en: [
        'While you are picking tributes or an attack target, Cancel sits under the buttons on the left. The “Esc to cancel” line no longer shows on the phone.',
      ],
      es: [
        'Mientras eliges sacrificios o a quién atacar, Cancelar queda debajo de los botones de la izquierda. En el celular ya no dice “Esc para cancelar”.',
      ],
      pt: [
        'Enquanto você escolhe tributos ou quem atacar, Cancelar fica embaixo dos botões da esquerda. No celular não aparece mais “Esc para cancelar”.',
      ],
    },
  },
  {
    id: '2026-09-24-donate',
    date: '2026-09-24',
    title: {
      en: 'A way to support the duel',
      es: 'Una forma de apoyar el duelo',
      pt: 'Um jeito de apoiar o duelo',
    },
    items: {
      en: [
        'Donate sits on the home screen, on the sign-in page, and at the bottom of the phone menu. It opens PayPal, and afterward Dark Magician Girl blows you a kiss.',
      ],
      es: [
        'Apoyar está en el inicio, en el inicio de sesión y al fondo del menú del celular. Abre PayPal y, al terminar, la Maga Oscura te manda un beso.',
      ],
      pt: [
        'Apoiar está no início, na entrada da conta e no fim do menu do celular. Ele abre o PayPal e, no fim, a Pequena Maga Negra manda um beijo.',
      ],
    },
  },
  {
    id: '2026-09-24-repeat-lp',
    date: '2026-09-24',
    title: {
      en: 'Full points when the ladder is small',
      es: 'Puntos completos cuando hay poca gente',
      pt: 'Pontos completos quando a tabela está pequena',
    },
    items: {
      en: [
        'While few duelists are playing Quick Match, facing the same rival again the same day gives full league points. A win no longer lands on 0. The half-points cut for a repeat match comes back once more people are on the ladder, and even then a win still gives at least 1 point.',
      ],
      es: [
        'Mientras haya poca gente en partidas rápidas, volver a jugar contra el mismo rival el mismo día da los puntos de liga completos. Una victoria ya no queda en 0. El recorte a la mitad vuelve cuando haya más duelistas en la clasificación, y aun así una victoria da al menos 1 punto.',
      ],
      pt: [
        'Enquanto houver pouca gente em partidas rápidas, jogar de novo contra o mesmo rival no mesmo dia dá os pontos de liga completos. Uma vitória não fica mais em 0. O corte pela metade volta quando houver mais duelistas na classificação, e mesmo assim uma vitória dá pelo menos 1 ponto.',
      ],
    },
  },
  {
    id: '2026-09-24-equip-target',
    date: '2026-09-24',
    title: {
      en: 'Equip spells can finish their target',
      es: 'Las cartas de equipo pueden elegir objetivo',
      pt: 'Magias de Equipamento conseguem escolher o alvo',
    },
    items: {
      en: [
        'Activating an Equip no longer gets stuck when the spell lands under the monster. Tap the highlighted monster, including its lower edge.',
      ],
      es: [
        'Activar un Equipo ya no deja el duelo trabado cuando la carta queda debajo del monstruo. Toca el monstruo resaltado, también en el borde de abajo.',
      ],
      pt: [
        'Ativar um Equipamento não trava mais o duelo quando a carta fica embaixo do monstro. Toque o monstro destacado, inclusive na borda de baixo.',
      ],
    },
  },
  {
    id: '2026-09-24-defense-target',
    date: '2026-09-24',
    title: {
      en: 'Defense monsters can be selected',
      es: 'Los monstruos en defensa se pueden elegir',
      pt: 'Monstros em defesa podem ser escolhidos',
    },
    items: {
      en: [
        'A monster in Defense, including one in the left zone next to the Field card, can be chosen as a target. **Stop Defense**, **Soul Exchange**, **Change of Heart**, and **Man-Eater Bug** accept that tap.',
      ],
      es: [
        'Un monstruo en Defensa, incluido el de la zona izquierda junto a la carta de Campo, se puede elegir como objetivo. **Stop Defense**, **Soul Exchange**, **Change of Heart** y **Man-Eater Bug** aceptan ese toque.',
      ],
      pt: [
        'Um monstro em Defesa, inclusive o da zona esquerda ao lado da carta de Campo, pode ser escolhido como alvo. **Stop Defense**, **Soul Exchange**, **Change of Heart** e **Man-Eater Bug** aceitam esse toque.',
      ],
    },
  },
  {
    id: '2026-09-24-chat-button-move',
    date: '2026-09-24',
    title: {
      en: 'Move the chat button',
      es: 'Mueve el botón del chat',
      pt: 'Mova o botão do chat',
    },
    items: {
      en: [
        'Drag the chat button and drop it where you want. It stays there, including over a duel. Arrow keys move it while it is focused, and Home puts it back in the corner.',
      ],
      es: [
        'Arrastra el botón del chat y suéltalo donde quieras. Se queda ahí, también sobre un duelo. Las flechas lo mueven cuando está enfocado, y Inicio lo devuelve a la esquina.',
      ],
      pt: [
        'Arraste o botão do chat e solte onde quiser. Ele fica lá, inclusive sobre um duelo. As setas movem quando ele está em foco, e Home devolve para o canto.',
      ],
    },
  },
  {
    id: '2026-09-24-soul-exchange-leave',
    date: '2026-09-24',
    title: {
      en: 'Soul Exchange and leaving a duel',
      es: 'Soul Exchange y salir de un duelo',
      pt: 'Soul Exchange e sair de um duelo',
    },
    items: {
      en: [
        'Tributing with **Soul Exchange** on a full field no longer removes the opponent\'s monster when the summon cannot happen. That monster stays until a zone is free, or you tribute one of yours.',
        '**Back** during a PvP duel asks before it ends the match. Your opponent still wins if you leave.',
      ],
      es: [
        'Sacrificar con **Soul Exchange** con el campo lleno ya no manda el monstruo del rival al cementerio si la invocación no puede hacerse. Ese monstruo se queda hasta que haya una zona libre, o sacrifiques uno de los tuyos.',
        '**Atrás** durante un duelo PvP pregunta antes de terminar la partida. Tu rival igual gana si sales.',
      ],
      pt: [
        'Tributar com **Soul Exchange** com o campo cheio não manda mais o monstro do oponente ao cemitério se a invocação não pode acontecer. Esse monstro fica até haver uma zona livre, ou você tributar um dos seus.',
        '**Voltar** durante um duelo PvP pergunta antes de encerrar a partida. Seu oponente ainda vence se você sair.',
      ],
    },
  },
  {
    id: '2026-09-24-fusion-materials',
    date: '2026-09-24',
    title: {
      en: 'Fusion materials in the card text',
      es: 'Materiales de fusión en el texto',
      pt: 'Materiais de fusão no texto',
    },
    items: {
      en: [
        'On a Fusion Monster, each material name in the text turns green when you have that monster and red when you do not. In Deck Builder that checks your main deck. In a duel it checks your hand and your monsters on the field.',
        'The Fusion Monster\'s own name turns green when every material is there.',
      ],
      es: [
        'En un monstruo de Fusión, cada nombre de material en el texto se pone verde si tienes ese monstruo y rojo si no. En Baraja eso mira tu baraja principal. En un duelo mira tu mano y tus monstruos en el campo.',
        'El nombre del monstruo de Fusión se pone verde cuando están todos los materiales.',
      ],
      pt: [
        'Em um monstro de Fusão, cada nome de material no texto fica verde quando você tem esse monstro e vermelho quando não. No Construtor de baralho isso olha seu baralho principal. Em um duelo olha sua mão e seus monstros no campo.',
        'O nome do monstro de Fusão fica verde quando todos os materiais estão lá.',
      ],
    },
  },
  {
    id: '2026-09-24-flip-self-target',
    date: '2026-09-24',
    title: {
      en: 'A destroyed flip cannot choose itself',
      es: 'Un volteo destruido no puede elegirse',
      pt: 'Um flip destruído não pode se escolher',
    },
    items: {
      en: [
        'If the attack that flips Hane-Hane or Man-Eater Bug also destroys it, that monster cannot choose itself. If it stays on the field, it still can.',
      ],
      es: [
        'Si el ataque que voltea a Hane-Hane o a Man-Eater Bug también lo destruye, ese monstruo no puede elegirse a sí mismo. Si se queda en el campo, sí puede.',
      ],
      pt: [
        'Se o ataque que vira Hane-Hane ou Man-Eater Bug também o destrói, esse monstro não pode escolher a si mesmo. Se ele fica no campo, ainda pode.',
      ],
    },
  },
  {
    id: '2026-09-24-chat-mentions',
    date: '2026-09-24',
    title: {
      en: 'Tag someone in chat with @',
      es: 'Etiqueta a alguien en el chat con @',
      pt: 'Marque alguém no chat com @',
    },
    items: {
      en: [
        'Type @ in chat and pick a duelist who is online. Names that are not connected stay as plain text.',
      ],
      es: [
        'Escribe @ en el chat y elige a un duelista que esté en línea. Si no está conectado, el nombre queda como texto.',
      ],
      pt: [
        'Digite @ no chat e escolha um duelista que esteja online. Se não estiver conectado, o nome fica como texto.',
      ],
    },
  },
  {
    id: '2026-09-24-star-tap',
    date: '2026-09-24',
    title: {
      en: 'Tap the star on Missions',
      es: 'Toca la estrella en Misiones',
      pt: 'Toque a estrela em Missões',
    },
    items: {
      en: [
        'Some daily missions ask you to tap a star on the Missions page. You do not need to queue for ranked. Finishing it pays 1 coin, like any other daily mission.',
      ],
      es: [
        'Algunas misiones diarias piden tocar una estrella en Misiones. No hace falta buscar una clasificatoria. Al terminarla recibes 1 moneda, como en cualquier otra misión diaria.',
      ],
      pt: [
        'Algumas missões diárias pedem para tocar uma estrela em Missões. Não precisa entrar na fila da ranqueada. Ao terminar, você ganha 1 moeda, como em qualquer outra missão diária.',
      ],
    },
  },
  {
    id: '2026-09-24-pvp-chat-button',
    date: '2026-09-24',
    title: {
      en: 'The chat button in a phone duel',
      es: 'El botón del chat en un duelo en el teléfono',
      pt: 'O botão do chat em um duelo no telefone',
    },
    items: {
      en: [
        'In a duel against another player, the chat button on a phone sits on the right edge, the same as against the CPU.',
      ],
      es: [
        'En un duelo contra otro jugador, el botón del chat en el teléfono queda en el borde derecho, igual que contra la CPU.',
      ],
      pt: [
        'Em um duelo contra outro jogador, o botão do chat no telefone fica na borda direita, igual contra a CPU.',
      ],
    },
  },
  {
    id: '2026-09-24-last-call',
    date: '2026-09-24',
    title: {
      en: 'Last Call',
      es: 'Última llamada',
      pt: 'Última chamada',
    },
    items: {
      en: [
        'For two weeks, finished PVP ranked matches fill a list on Missions. Versus the CPU does not count, and neither do private rooms or leaving a match.',
        '3 matches give an avatar frame, 8 a card sleeve, 14 a playmat, and 20 a promo pack. The pack uses the Legend of Blue Eyes cover and opens like the others. Inside is one Blue-Eyes White Dragon in Ultra Rare, Helios, Nightglass, or Stormwake.',
        'The promo Blue-Eyes card stays after resets. The list freezes when the two weeks end.',
      ],
      es: [
        'Durante dos semanas, las partidas clasificatorias PVP terminadas llenan una lista en Misiones. Contra la CPU no cuenta, y tampoco las salas privadas ni dejar la partida.',
        '3 partidas dan un marco de avatar, 8 una funda, 14 un tapete y 20 un sobre promo. El sobre usa la portada de Legend of Blue Eyes y se abre como los demás. Adentro hay un Blue-Eyes White Dragon en Ultra Rare, Helios, Nightglass o Stormwake.',
        'La carta promo Blue-Eyes se mantiene después de los resets. La lista se congela cuando terminan las dos semanas.',
      ],
      pt: [
        'Por duas semanas, as partidas ranqueadas PVP terminadas enchem uma lista em Missões. Contra a CPU não conta, e nem as salas privadas nem sair da partida.',
        '3 partidas dão uma moldura de avatar, 8 uma capa, 14 um tapete e 20 um pacote promo. O pacote usa a capa de Legend of Blue Eyes e abre como os outros. Dentro há um Blue-Eyes White Dragon em Ultra Rare, Helios, Nightglass ou Stormwake.',
        'A carta promo Blue-Eyes permanece depois dos resets. A lista congela quando as duas semanas acabam.',
      ],
    },
  },
  {
    id: '2026-09-24-ranked-call-coins',
    date: '2026-09-24',
    title: {
      en: 'Three coins for answering ranked',
      es: 'Tres monedas por entrar a la clasificatoria',
      pt: 'Três moedas por entrar na ranqueada',
    },
    items: {
      en: [
        'The note that someone is waiting for ranked shows a coin **×3** on **Duel**. You collect those **3** coins when you finish that Quick Match. Leaving it pays nothing.',
      ],
      es: [
        'El aviso de que alguien espera una clasificatoria muestra la moneda **×3** en **Duelo**. Cobras esas **3** monedas cuando terminas esa partida. Si la dejas, no se pagan.',
      ],
      pt: [
        'O aviso de que alguém espera uma ranqueada mostra a moeda **×3** em **Duelo**. Você recebe essas **3** moedas quando termina essa partida. Se sair, não paga.',
      ],
    },
  },
  {
    id: '2026-09-23-ranked-call',
    date: '2026-09-23',
    title: {
      en: 'Someone is waiting for ranked',
      es: 'Alguien espera una clasificatoria',
      pt: 'Alguém espera uma ranqueada',
    },
    items: {
      en: [
        'While you duel the CPU, a small note appears when someone is waiting for ranked. **Duel** leaves the CPU match and starts a ranked duel with them. If someone else takes that player first, the note disappears and your CPU duel keeps going. **Ignore** hides that person until they queue again.',
      ],
      es: [
        'Mientras juegas contra la CPU, aparece un aviso pequeño si alguien espera una clasificatoria. **Duelo** deja la partida contra la CPU y empieza una clasificatoria contra esa persona. Si otro se la lleva primero, el aviso desaparece y tu duelo contra la CPU sigue. **Ignorar** oculta a esa persona hasta que vuelva a entrar en cola.',
      ],
      pt: [
        'Enquanto você duela contra a CPU, aparece um aviso pequeno se alguém espera uma ranqueada. **Duelo** deixa a partida contra a CPU e começa uma ranqueada contra essa pessoa. Se outra pessoa chegar primeiro, o aviso some e seu duelo contra a CPU continua. **Ignorar** esconde essa pessoa até ela entrar na fila de novo.',
      ],
    },
  },
  {
    id: '2026-09-23-ranked-payout',
    date: '2026-09-23',
    title: {
      en: 'Ranked pays more than vs AI',
      es: 'La clasificatoria paga más que vs IA',
      pt: 'A ranqueada paga mais do que vs IA',
    },
    items: {
      en: [
        'A Quick Match win pays **15** coins and **48** XP. A finished loss or draw pays **10** coins and **30** XP. That is more than beating the CPU on Master. Surrender still pays nothing.',
      ],
      es: [
        'Una victoria en partida rápida paga **15** monedas y **48** de XP. Una derrota o un empate terminados pagan **10** monedas y **30** de XP. Es más que ganarle a la CPU en Maestro. Rendirse sigue sin pagar.',
      ],
      pt: [
        'Uma vitória em partida rápida paga **15** moedas e **48** de XP. Uma derrota ou um empate terminados pagam **10** moedas e **30** de XP. É mais do que vencer a CPU em Mestre. Desistir continua sem pagar.',
      ],
    },
  },
  {
    id: '2026-09-23-minimal-motion',
    date: '2026-09-23',
    title: {
      en: 'A lower animation level',
      es: 'Un nivel de animación más bajo',
      pt: 'Um nível de animação mais baixo',
    },
    items: {
      en: [
        'Settings and Duel settings now have a third animation level, **Minimal**. It keeps the Reduced cuts and also skips 3D card flights, the field background, and animated foil. The board draws fewer frames. Cards appear in place. It stays on this device.',
      ],
      es: [
        'Ajustes y Ajustes del duelo ahora tienen un tercer nivel, **Mínimas**. Conserva los cortes de Reducidas y además quita los vuelos 3D, el fondo de campo y el foil animado. El tablero dibuja menos cuadros. Las cartas aparecen en su sitio. Se queda en este dispositivo.',
      ],
      pt: [
        'Ajustes e Ajustes do duelo agora têm um terceiro nível, **Mínimas**. Mantém os cortes de Reduzidas e também tira os voos 3D, o fundo de campo e o foil animado. O tabuleiro desenha menos quadros. Os cards aparecem no lugar. Fica neste aparelho.',
      ],
    },
  },
  {
    id: '2026-09-23-chat-size-mobile',
    date: '2026-09-23',
    title: {
      en: 'A larger chat on your phone',
      es: 'Un chat más grande en el teléfono',
      pt: 'Um chat maior no telefone',
    },
    items: {
      en: [
        'On a phone, the chat button grows it in two steps: first to a medium size, then to full screen. It fits the height, width, and safe area of the screen, including over a duel.',
      ],
      es: [
        'En el teléfono, el botón del chat lo agranda en dos pasos: primero a un tamaño medio y después a pantalla completa. Se acomoda al alto, al ancho y a la zona segura de la pantalla, también encima de un duelo.',
      ],
      pt: [
        'No telefone, o botão do chat aumenta em dois passos: primeiro para um tamanho médio e depois para a tela cheia. Ele se ajusta à altura, à largura e à área segura da tela, também por cima de um duelo.',
      ],
    },
  },
  {
    id: '2026-09-23-global-chat-restore',
    date: '2026-09-23',
    title: {
      en: 'Global chat keeps the last lines',
      es: 'El chat global conserva los últimos mensajes',
      pt: 'O chat global guarda as últimas mensagens',
    },
    items: {
      en: [
        'After an update, global chat opens with the last 10 messages still there.',
      ],
      es: [
        'Después de una actualización, el chat global abre con los últimos 10 mensajes todavía ahí.',
      ],
      pt: [
        'Depois de uma atualização, o chat global abre com as últimas 10 mensagens ainda lá.',
      ],
    },
  },
  {
    id: '2026-09-23-collection-poll',
    date: '2026-09-23',
    title: {
      en: 'A vote on resetting the collection',
      es: 'Una votación sobre reiniciar la colección',
      pt: 'Uma votação sobre reiniciar a coleção',
    },
    items: {
      en: [
        'Under the Metal Raiders notice on Home, you can vote **yes** or **no** on resetting the collection when a new set arrives. There is also a box for the rewards you want for the players who finish on top after the reset. Those rewards can be cosmetics or cards. Once you have voted and sent a suggestion, the poll folds up. Tap it to open it again.',
      ],
      es: [
        'Debajo del aviso de Metal Raiders, en Inicio, puedes votar **sí** o **no** a reiniciar la colección cuando llegue un set nuevo. También hay una caja para las recompensas que quieres para quienes queden arriba después del reinicio. Pueden ser cosméticas o cartas. Cuando ya votaste y enviaste una sugerencia, la encuesta se pliega. Tócala para abrirla otra vez.',
      ],
      pt: [
        'Abaixo do aviso de Metal Raiders, no Início, você pode votar **sim** ou **não** para reiniciar a coleção quando chegar um set novo. Também há uma caixa para as recompensas que você quer para quem ficar no topo depois do reinício. Podem ser cosméticas ou cartas. Quando você já votou e enviou uma sugestão, a enquete se recolhe. Toque nela para abrir de novo.',
      ],
    },
  },
  {
    id: '2026-09-23-trap-minimal',
    date: '2026-09-23',
    title: {
      en: 'Fewer trap questions',
      es: 'Menos preguntas de trampas',
      pt: 'Menos perguntas de armadilhas',
    },
    items: {
      en: [
        '**Minimal** is the starting choice. It asks only when a trap answers the play: **Trap Hole** on a summon, or **Waboku** and the battle boosts on an attack. It skips the question at the start of the Battle Phase, and it does not ask again before damage if the monster was already face-up.',
      ],
      es: [
        '**Mínimo** es el que viene elegido. Solo pregunta cuando una trampa responde a la jugada: **Trap Hole** en una invocación, o **Waboku** y los aumentos de batalla en un ataque. No pregunta al entrar a la Battle Phase, y no vuelve a preguntar antes del daño si el monstruo ya estaba boca arriba.',
      ],
      pt: [
        '**Mínimo** é o que já vem escolhido. Só pergunta quando uma armadilha responde à jogada: **Trap Hole** numa invocação, ou **Waboku** e os aumentos de batalha num ataque. Não pergunta no começo da Fase de Batalha, e não pergunta de novo antes do dano se o monstro já estava virado para cima.',
      ],
    },
  },
  {
    id: '2026-09-23-lower-rank-win',
    date: '2026-09-23',
    title: {
      en: 'A win against a lower rank pays less',
      es: 'Ganar contra un rango más bajo sube menos',
      pt: 'Ganhar de um rank mais baixo sobe menos',
    },
    items: {
      en: [
        'If you win a Quick Match against someone several divisions below you, you gain fewer league points. The further below they are, the smaller the gain. A win against someone at your rank, or above, stays the same.',
      ],
      es: [
        'Si ganas una partida rápida contra alguien que está varias divisiones por debajo, subes menos puntos de liga. Cuanto más abajo esté, menor es la subida. Ganar contra alguien de tu rango, o más alto, sigue igual.',
      ],
      pt: [
        'Se você ganha uma partida rápida contra alguém várias divisões abaixo, você sobe menos pontos de liga. Quanto mais abaixo a pessoa estiver, menor é a subida. Ganhar de alguém do seu rank, ou mais alto, continua igual.',
      ],
    },
  },
  {
    id: '2026-09-23-bug-reward',
    date: '2026-09-23',
    title: {
      en: 'A coin for a bug report',
      es: 'Una moneda por reportar un error',
      pt: 'Uma moeda por reportar um bug',
    },
    items: {
      en: [
        'Report a bug and we give you 1 coin a day. If the bug was real, we give you a booster. It shows up the next time you come back.',
      ],
      es: [
        'Si reportas un error, te damos 1 moneda al día. Si el error era real, te damos un sobre. Aparece la próxima vez que entras.',
      ],
      pt: [
        'Se você reportar um bug, damos 1 moeda por dia. Se o bug era real, damos um pacote. Ele aparece na próxima vez que você entra.',
      ],
    },
  },
  {
    id: '2026-09-23-reconnect',
    date: '2026-09-23',
    title: {
      en: 'A dropped connection no longer freezes the duel',
      es: 'Una conexión caída ya no traba el duelo',
      pt: 'Uma conexão caída não trava mais o duelo',
    },
    items: {
      en: [
        'If the link drops during a ranked duel, the board says it is reconnecting. ' +
          'The match resumes when the connection comes back, instead of stopping on a raw error.',
      ],
      es: [
        'Si se cae la conexión en un duelo clasificatorio, el tablero avisa que está ' +
          'reconectando. La partida sigue cuando vuelve el enlace, en lugar de quedarse en un error crudo.',
      ],
      pt: [
        'Se a conexão cair num duelo ranqueado, o tabuleiro avisa que está reconectando. ' +
          'A partida continua quando o enlace volta, em vez de parar num erro cru.',
      ],
    },
  },
  {
    id: '2026-09-23-deploy-coins',
    date: '2026-09-23',
    title: {
      en: 'Coins from a cut duel arrive when you return',
      es: 'Las monedas de un duelo cortado llegan al volver',
      pt: 'As moedas de um duelo interrompido chegam ao voltar',
    },
    items: {
      en: [
        'If an update cuts a duel, both duelists still receive the coins. They show up, with the note, the next time you are back in.',
      ],
      es: [
        'Si una actualización corta un duelo, los dos duelistas igual reciben las monedas. Aparecen, con el aviso, la próxima vez que entras.',
      ],
      pt: [
        'Se uma atualização interrompe um duelo, os dois duelistas ainda recebem as moedas. Elas aparecem, com o aviso, na próxima vez que você entra.',
      ],
    },
  },
  {
    id: '2026-09-23-offering-pay-button',
    date: '2026-09-23',
    title: {
      en: 'Pay Ultimate Offering from the prompt',
      es: 'Paga Ofrenda Final desde el aviso',
      pt: 'Pague Ultimate Offering no aviso',
    },
    items: {
      en: [
        'After **Ultimate Offering** flips during the opponent\'s attack, the prompt shows **Pay 500 LP** on the left and **No, pass** on the right. The same pair sits side by side when it is already face-up.',
      ],
      es: [
        'Cuando **Ofrenda Final** se activa en el ataque del rival, el aviso muestra **Pagar 500 LP** a la izquierda y **No, pasar** a la derecha. El mismo par queda lado a lado si ya está boca arriba.',
      ],
      pt: [
        'Quando **Ultimate Offering** ativa no ataque do oponente, o aviso mostra **Pagar 500 LP** à esquerda e **Não, passar** à direita. O mesmo par fica lado a lado se já estiver virada para cima.',
      ],
    },
  },
  {
    id: '2026-09-23-higher-rank-loss',
    date: '2026-09-23',
    title: {
      en: 'A loss to a higher rank costs less',
      es: 'Perder contra un rango más alto baja menos',
      pt: 'Perder para um rank mais alto desce menos',
    },
    items: {
      en: [
        'If you lose a Quick Match to someone several divisions above you, you drop fewer league points. The further above they are, the smaller the drop. A loss to someone at your rank, or below, stays the same.',
      ],
      es: [
        'Si pierdes una partida rápida contra alguien que está varias divisiones por encima, bajas menos puntos de liga. Cuanto más arriba esté, menor es la baja. Perder contra alguien de tu rango, o más bajo, sigue igual.',
      ],
      pt: [
        'Se você perde uma partida rápida para alguém várias divisões acima, você desce menos pontos de liga. Quanto mais acima a pessoa estiver, menor é a queda. Perder para alguém do seu rank, ou mais baixo, continua igual.',
      ],
    },
  },
  {
    id: '2026-09-23-deck-sync',
    date: '2026-09-23',
    title: {
      en: 'Decks stay in sync',
      es: 'Las barajas se quedan sincronizadas',
      pt: 'Os baralhos ficam sincronizados',
    },
    items: {
      en: [
        'Deleting or editing a deck on your phone sticks when you open the site on a computer. An old tab can no longer bring the previous decks back.',
      ],
      es: [
        'Si borras o editas una baraja en el celular, el navegador muestra esa misma lista. Una pestaña vieja ya no puede traer de vuelta las barajas anteriores.',
      ],
      pt: [
        'Se você apaga ou edita um baralho no celular, o navegador mostra essa mesma lista. Uma aba antiga não traz mais os baralhos anteriores.',
      ],
    },
  },
  {
    id: '2026-09-23-waiting-block',
    date: '2026-09-23',
    title: {
      en: 'A blocked play says when you are waiting',
      es: 'Una jugada bloqueada dice cuándo estás esperando',
      pt: 'Uma jogada bloqueada diz quando você está esperando',
    },
    items: {
      en: [
        'If you try a play while your opponent is deciding, the notice says you are waiting. It still does not name the Battle Step or a Set card.',
      ],
      es: [
        'Si intentas una jugada mientras tu rival decide, el aviso dice que estás esperando. Sigue sin nombrar el Battle Step ni una carta colocada.',
      ],
      pt: [
        'Se você tenta uma jogada enquanto seu oponente decide, o aviso diz que você está esperando. Continua sem nomear o Battle Step nem uma carta Colocada.',
      ],
    },
  },
  {
    id: '2026-09-23-waiting-line',
    date: '2026-09-23',
    title: {
      en: 'The wait sits with the player',
      es: 'La espera queda con el jugador',
      pt: 'A espera fica com o jogador',
    },
    items: {
      en: [
        'While someone still has to answer, the waiting line sits under that duelist. It is a bit larger on desktop, and bigger on a phone so it is easy to see.',
      ],
      es: [
        'Mientras alguien todavía tiene que responder, el aviso de espera queda debajo de ese duelista. En el escritorio es un poco más grande, y en el celular se lee fácil.',
      ],
      pt: [
        'Enquanto alguém ainda tem que responder, o aviso de espera fica embaixo desse duelista. No computador ele fica um pouco maior, e no celular dá para ler fácil.',
      ],
    },
  },
  {
    id: '2026-09-23-ranked-loss',
    date: '2026-09-23',
    title: {
      en: 'A ranked loss pays more',
      es: 'Una derrota clasificatoria paga más',
      pt: 'Uma derrota ranqueada paga mais',
    },
    items: {
      en: [
        'A finished Quick Match loss pays **4** coins. Surrender still pays nothing.',
      ],
      es: [
        'Una derrota en partida rápida terminada paga **4** monedas. Rendirse sigue sin pagar.',
      ],
      pt: [
        'Uma derrota em partida rápida terminada paga **4** moedas. Desistir continua sem pagar.',
      ],
    },
  },
  {
    id: '2026-09-23-ranked-missions',
    date: '2026-09-23',
    title: {
      en: 'Ranked missions',
      es: 'Misiones de ranked',
      pt: 'Missões ranqueadas',
    },
    items: {
      en: [
        'Missions has a **Ranked** tab. Each Quick Match you finish today fills one step. Steps 1 through 9 pay that many coins, and step 10 gives a shop pack.',
        'Leaving the match does not fill your step. If you stay and your opponent leaves, you still get the step.',
      ],
      es: [
        'Misiones tiene una pestaña **Clasificatoria**. Cada partida clasificatoria que terminas hoy completa un paso. Los pasos 1 a 9 pagan esa cantidad de monedas, y el paso 10 da un sobre de tienda.',
        'Si abandonas la partida, tu paso no cuenta. Si te quedas y tu rival se va, el paso sí es tuyo.',
      ],
      pt: [
        'Missões tem uma aba **Ranqueado**. Cada partida ranqueada que você termina hoje completa uma etapa. As etapas 1 a 9 pagam essa quantidade de moedas, e a etapa 10 dá um pacote da loja.',
        'Se você abandonar a partida, sua etapa não conta. Se você ficar e o rival sair, a etapa é sua.',
      ],
    },
  },
  {
    id: '2026-09-23-queue-wait',
    date: '2026-09-23',
    title: {
      en: 'Something to do while you wait',
      es: 'Algo que hacer mientras esperas',
      pt: 'Algo para fazer enquanto espera',
    },
    items: {
      en: [
        'Quick Match now says when the search widens: close ratings first, then a wider gap, then anyone still waiting.',
        'Tap the star while you wait. A small star sometimes drops a coin, up to 3 a day.',
      ],
      es: [
        'Partida rápida ahora dice cuándo se amplía la búsqueda: primero ratings cercanos, luego una diferencia más grande, y después quien siga esperando.',
        'Toca la estrella mientras esperas. Una estrella chica a veces suelta una moneda, hasta 3 al día.',
      ],
      pt: [
        'A Partida rápida agora diz quando a busca amplia: primeiro ratings próximos, depois uma diferença maior, e então quem ainda estiver esperando.',
        'Toque a estrela enquanto espera. Uma estrela pequena às vezes solta uma moeda, até 3 por dia.',
      ],
    },
  },
  {
    id: '2026-09-23-metal-raiders',
    date: '2026-09-23',
    title: {
      en: 'Metal Raiders is next',
      es: 'Lo que sigue es Metal Raiders',
      pt: 'O próximo é Metal Raiders',
    },
    items: {
      en: [
        'The home now says it up front: once the current set of cards is stable, the next set of cards is **Metal Raiders**. Those cards join the **185** already in the game.',
        'Every new expansion resets the cards you obtained, your coins, your league, and your level. Your collection does not reset. The ladder starts over so players with more and better cards do not lock the top, new players can climb, and decks stay varied.',
        'Players who helped improve the game, and those who played the most, will receive rewards. The ranked top and the vs CPU top also get cosmetic rewards and packs.',
      ],
      es: [
        'El inicio ahora lo dice de frente: cuando el set de cartas actual esté estable, el siguiente set de cartas es **Metal Raiders**. Esas cartas se suman a las **185** que ya existen.',
        'Cada expansión nueva reinicia las cartas que obtuviste, tus monedas, tu liga y tu nivel. Tu colección no se reinicia. La escalera vuelve a empezar para que quienes ya tienen más y mejores cartas no acaparen el top, para que los jugadores nuevos puedan escalar y para que haya más variedad en las barajas.',
        'Quienes ayudaron a mejorar el juego, y quienes más jugaron, van a recibir recompensas. El top de la clasificatoria y el top contra la CPU también reciben recompensas cosméticas y sobres.',
      ],
      pt: [
        'O início agora diz de cara: quando o set de cartas atual estiver estável, o próximo set de cartas é **Metal Raiders**. Essas cartas se somam às **185** que já existem.',
        'Cada expansão nova reinicia as cartas que você obteve, suas moedas, sua liga e seu nível. Sua coleção não é reiniciada. A escada recomeça para que quem já tem mais e melhores cartas não fique com o topo, para os jogadores novos poderem subir e para haver mais variedade nos baralhos.',
        'Quem ajudou a melhorar o jogo, e quem mais jogou, vai receber recompensas. O topo do ranqueado e o topo contra a CPU também recebem recompensas cosméticas e pacotes.',
      ],
    },
    figures: [
      {
        item: 0,
        src: '/assets/changelog/2026-09-23-metal-raiders.webp',
        alt: {
          en: 'Home notice for Metal Raiders and the reset on each new set of cards',
          es: 'Aviso del inicio sobre Metal Raiders y el reinicio en cada set de cartas',
          pt: 'Aviso do início sobre Metal Raiders e o reinício em cada set de cartas',
        },
      },
    ],
  },
  {
    id: '2026-09-23-offering-legal-summon',
    date: '2026-09-23',
    title: {
      en: 'Ultimate Offering only if you can summon',
      es: 'Ofrenda Final solo si puedes invocar',
      pt: 'Ultimate Offering só se você puder invocar',
    },
    items: {
      en: [
        '**Ultimate Offering** no longer takes 500 LP when you cannot Normal Summon or Tribute Summon. A full field with only a Level 4 in hand, or a Level 5 or higher with no tribute, does not offer the payment.',
      ],
      es: [
        '**Ofrenda Final** ya no cobra 500 LP cuando no puedes Invocar de Modo Normal o por Sacrificio. Un campo lleno con solo un nivel 4 en la mano, o un nivel 5 o más sin sacrificio, no ofrece el pago.',
      ],
      pt: [
        '**Ultimate Offering** não cobra mais 500 LP quando você não pode Invocar de Modo Normal ou por Tributo. Um campo cheio só com um Nível 4 na mão, ou um Nível 5 ou mais sem tributo, não oferece o pagamento.',
      ],
    },
  },
  {
    id: '2026-09-23-faster-queue',
    date: '2026-09-23',
    title: {
      en: 'Quicker Quick Match',
      es: 'Partida rápida, más rápida',
      pt: 'Partida rápida, mais rápida',
    },
    items: {
      en: [
        'Quick Match widens sooner. After 15 seconds it accepts a larger rating gap, and after 30 seconds it pairs with anyone still waiting.',
      ],
      es: [
        'Partida rápida se abre antes. A los 15 segundos acepta una diferencia de rating más grande, y a los 30 se empareja con quien siga esperando.',
      ],
      pt: [
        'A Partida rápida abre mais cedo. Aos 15 segundos aceita uma diferença de rating maior, e aos 30 emparelha com quem ainda estiver esperando.',
      ],
    },
  },
  {
    id: '2026-09-23-prompt-keys',
    date: '2026-09-23',
    title: {
      en: 'Enter activates, Esc passes',
      es: 'Enter activa, Esc pasa',
      pt: 'Enter ativa, Esc passa',
    },
    items: {
      en: [
        'On a duel prompt, **Enter** activates and **Esc** chooses **No, pass**. If several cards can activate, **Enter** takes the first one. **Esc** also cancels a card menu. A menu with more than one option still needs a click.',
      ],
      es: [
        'En un aviso del duelo, **Enter** activa y **Esc** elige **No, pasar**. Si varias cartas pueden activarse, **Enter** toma la primera. **Esc** también cancela el menú de una carta. Un menú con más de una opción sigue pidiendo un clic.',
      ],
      pt: [
        'Num aviso do duelo, **Enter** ativa e **Esc** escolhe **Não, passar**. Se vários cards podem ativar, **Enter** pega o primeiro. **Esc** também cancela o menu de um card. Um menu com mais de uma opção ainda pede um clique.',
      ],
    },
  },
  {
    id: '2026-09-23-reduce-animations',
    date: '2026-09-23',
    title: {
      en: 'Reduce animations',
      es: 'Reducir animaciones',
      pt: 'Reduzir animações',
    },
    items: {
      en: [
        'Settings and Duel settings now have **Reduce animations**. It cuts particles, screen shake, and field effects on this device. Cards still move. It does not change animation speed, and it does not follow your account to another device.',
      ],
      es: [
        'Ajustes y Ajustes del duelo ahora tienen **Reducir animaciones**. Corta partículas, temblores de pantalla y efectos de campo en este dispositivo. Las cartas siguen moviéndose. No cambia la velocidad de animación y no viaja con tu cuenta a otro aparato.',
      ],
      pt: [
        'Ajustes e Ajustes do duelo agora têm **Reduzir animações**. Corta partículas, tremores de tela e efeitos de campo neste aparelho. Os cards continuam se movendo. Não muda a velocidade da animação e não vai com a sua conta para outro aparelho.',
      ],
    },
  },
  {
    id: '2026-09-23-bgm-mix',
    date: '2026-09-23',
    title: {
      en: 'Your music keeps playing',
      es: 'Tu música sigue sonando',
      pt: 'Sua música continua tocando',
    },
    items: {
      en: [
        'Game music no longer pauses Spotify or other apps. Both can play at once. Lower the music slider if you only want your own playlist.',
      ],
      es: [
        'La música del juego ya no pausa Spotify ni otras apps. Las dos pueden sonar a la vez. Baja el control de música si solo quieres tu propia lista.',
      ],
      pt: [
        'A música do jogo não pausa mais o Spotify nem outros apps. As duas podem tocar ao mesmo tempo. Baixe o volume da música se você quiser só a sua playlist.',
      ],
    },
  },
  {
    id: '2026-09-23-trap-hole-activate',
    date: '2026-09-23',
    title: {
      en: 'Trap Hole activates on the summon',
      es: 'Trap Hole se activa en la invocación',
      pt: 'Trap Hole ativa na invocação',
    },
    items: {
      en: [
        '**Trap Hole** destroys the summoned monster as soon as you activate it. You no longer have to click that monster afterwards.',
        'On a **Flip Summon**, **Trap Hole** can still destroy that monster, but it does not cancel the Flip Effect. If the effect needs a target, you choose it first, and it still resolves when that target is still legal.',
      ],
      es: [
        '**Trap Hole** destruye al monstruo invocado en cuanto la activas. Ya no tienes que tocar ese monstruo después.',
        'En una **Invocación de Volteo**, **Trap Hole** puede destruir a ese monstruo, pero no cancela el efecto de volteo. Si el efecto pide un objetivo, lo eliges primero, y se resuelve si ese objetivo sigue siendo válido.',
      ],
      pt: [
        '**Trap Hole** destrói o monstro invocado assim que você a ativa. Você não precisa mais tocar nesse monstro depois.',
        'Numa **Invocação por Flip**, **Trap Hole** pode destruir esse monstro, mas não cancela o Efeito de Virar. Se o efeito pede um alvo, você o escolhe primeiro, e ele resolve se esse alvo ainda for válido.',
      ],
    },
  },
  {
    id: '2026-09-23-ranked-queue',
    date: '2026-09-23',
    title: {
      en: 'Queue size and a coin for ranked',
      es: 'Cola visible y monedas por clasificatoria',
      pt: 'Fila visível e moedas pela ranqueada',
    },
    items: {
      en: [
        'The ranked buttons show how many duelists are waiting in the Quick Match queue.',
        'Every 3 finished duels vs AI, you are offered **5** coins just for playing one ranked match. Surrendering or leaving that match does not pay them.',
      ],
      es: [
        'Los botones de clasificatoria muestran cuántos duelistas esperan en la cola de la partida rápida.',
        'Cada 3 duelos terminados contra la IA, te ofrecemos **5** monedas solo por jugar una clasificatoria. Rendirse o salir de esa partida no las paga.',
      ],
      pt: [
        'Os botões de ranqueada mostram quantos duelistas esperam na fila da partida rápida.',
        'A cada 3 duelos concluídos contra a IA, oferecemos **5** moedas só por jogar uma ranqueada. Desistir ou sair dessa partida não paga.',
      ],
    },
  },
  {
    id: '2026-09-23-offering-attack-summon',
    date: '2026-09-23',
    title: {
      en: 'Ultimate Offering summon during an attack',
      es: 'Ofrenda Final invoca durante un ataque',
      pt: 'Ultimate Offering invoca durante um ataque',
    },
    items: {
      en: [
        'Paying **Ultimate Offering** when the opponent declares an attack now lets you Normal Summon or Set from your hand before damage. The attack replays if the number of monsters you control changes.',
      ],
      es: [
        'Pagar **Ofrenda Final** cuando el rival declara un ataque ahora te deja Invocar de Modo Normal o Colocar desde la mano antes del daño. El ataque se repite si cambia cuántos monstruos controlas.',
      ],
      pt: [
        'Pagar **Ultimate Offering** quando o oponente declara um ataque agora deixa você Invocar de Modo Normal ou Baixar da mão antes do dano. O ataque se repete se mudar quantos monstros você controla.',
      ],
    },
  },
  {
    id: '2026-09-22-duel-roster',
    date: '2026-09-22',
    title: {
      en: 'See who you are dueling',
      es: 'Mira contra quién juegas',
      pt: 'Veja contra quem você joga',
    },
    items: {
      en: [
        'After the VS intro in a duel against another player, both duelists appear together: rank, level, ladder places, and PvP win rate. Tap to skip.',
      ],
      es: [
        'Después de la intro VS en un duelo contra otro jugador, los dos duelistas aparecen a la vez: rango, nivel, puestos y porcentaje de victorias en PvP. Toca para saltar.',
      ],
      pt: [
        'Depois da intro VS em um duelo contra outro jogador, os dois duelistas aparecem ao mesmo tempo: rank, nível, lugares e taxa de vitória no PvP. Toque para pular.',
      ],
    },
  },
  {
    id: '2026-09-22-rank-medal',
    date: '2026-09-22',
    title: {
      en: 'League medals in online duels',
      es: 'Medallas de liga en duelos en línea',
      pt: 'Medalhas de liga em duelos online',
    },
    items: {
      en: [
        'In an online duel, the league medal of each duelist sits under their portrait.',
      ],
      es: [
        'En un duelo en línea, la medalla de liga de cada duelista queda bajo su retrato.',
      ],
      pt: [
        'Em um duelo online, a medalha de liga de cada duelista fica sob o retrato.',
      ],
    },
  },
  {
    id: '2026-09-22-deploy-reward',
    date: '2026-09-22',
    title: {
      en: 'Coins when an update cuts a duel',
      es: 'Monedas si una actualización corta un duelo',
      pt: 'Moedas se uma atualização interrompe um duelo',
    },
    items: {
      en: [
        'If an update cuts a duel short, both duelists receive coins and a note that explains why.',
      ],
      es: [
        'Si una actualización corta un duelo, los dos duelistas reciben monedas y un aviso que explica por qué.',
      ],
      pt: [
        'Se uma atualização interrompe um duelo, os dois duelistas recebem moedas e um aviso explicando o porquê.',
      ],
    },
  },
  {
    id: '2026-09-22-last-will-turn',
    date: '2026-09-22',
    title: {
      en: 'Last Will ends with your turn',
      es: 'Last Will termina con tu turno',
      pt: 'Last Will termina com o seu turno',
    },
    items: {
      en: [
        '**Last Will** lasts only the turn you activate it. A monster sent to the ' +
          'Graveyard on the opponent\'s turn, such as by **Dark Hole**, does not ' +
          'Special Summon from the Deck.',
      ],
      es: [
        '**Last Will** dura solo el turno en que la activas. Si un monstruo tuyo va ' +
          'al Cementerio en el turno del rival, por ejemplo con **Dark Hole**, no ' +
          'invocas de Modo Especial desde la baraja.',
      ],
      pt: [
        '**Last Will** dura apenas o turno em que você a ativa. Se um monstro seu vai ' +
          'ao Cemitério no turno do oponente, por exemplo com **Dark Hole**, você não ' +
          'Invoca por Invocação-Especial do baralho.',
      ],
    },
  },
  {
    id: '2026-09-22-trap-prompt',
    date: '2026-09-22',
    title: {
      en: 'The trap question stays on screen',
      es: 'La pregunta de la trampa se queda en pantalla',
      pt: 'A pergunta da armadilha fica na tela',
    },
    items: {
      en: [
        'Entering the Battle Phase with a Set trap such as **Reinforcements** keeps ' +
          'the question on screen until you activate it or pass. If that question is ' +
          'missing, the phase button passes it so the duel can continue.',
      ],
      es: [
        'Entrar a la Battle Phase con una trampa colocada, como **Reinforcements**, ' +
          'deja la pregunta en pantalla hasta que la actives o pases. Si esa pregunta ' +
          'no aparece, el botón de fase la pasa para que el duelo pueda seguir.',
      ],
      pt: [
        'Entrar na Battle Phase com uma armadilha colocada, como **Reinforcements**, ' +
          'mantém a pergunta na tela até você ativá-la ou passar. Se essa pergunta ' +
          'não aparecer, o botão de fase passa por ela para o duelo continuar.',
      ],
    },
  },
  {
    id: '2026-09-22-queue-timer',
    date: '2026-09-22',
    title: {
      en: 'Queue timer',
      es: 'Reloj en la cola',
      pt: 'Relógio na fila',
    },
    items: {
      en: [
        'Quick Match shows how long you have been waiting, so the search does not look frozen.',
      ],
      es: [
        'Partida rápida muestra cuánto llevas esperando, para que la búsqueda no parezca trabada.',
      ],
      pt: [
        'A partida rápida mostra há quanto tempo você está esperando, para a busca não parecer travada.',
      ],
    },
  },
  {
    id: '2026-09-22-discord',
    date: '2026-09-22',
    title: {
      en: 'Discord',
      es: 'Discord',
      pt: 'Discord',
    },
    items: {
      en: [
        'A **Discord** button on the home screen and the sign-in pages opens the ' +
          'Dueling Chaos server.',
      ],
      es: [
        'Un botón de **Discord** en el inicio y en las pantallas de cuenta abre el ' +
          'servidor de Dueling Chaos.',
      ],
      pt: [
        'Um botão de **Discord** na tela inicial e nas telas de conta abre o servidor ' +
          'do Dueling Chaos.',
      ],
    },
  },
  {
    id: '2026-09-22-ai-coins',
    date: '2026-09-22',
    title: {
      en: 'More coins vs AI',
      es: 'Más monedas vs IA',
      pt: 'Mais moedas vs IA',
    },
    items: {
      en: [
        'A **Normal** win pays **1** coin, **Hard** **4**, **Expert** **5**, and ' +
          '**Master** **6**. A real loss pays **3**. An Easy win still pays no coins. ' +
          'Surrender still pays nothing.',
      ],
      es: [
        'Ganar en **Normal** paga **1** moneda, en **Difícil** **4**, en **Experto** ' +
          '**5** y en **Maestro** **6**. Una derrota real paga **3**. Una victoria en ' +
          'Fácil sigue sin monedas. Rendirse sigue sin pagar.',
      ],
      pt: [
        'Uma vitória em **Normal** paga **1** moeda, **Difícil** **4**, ' +
          '**Especialista** **5** e **Mestre** **6**. Uma derrota de verdade paga **3**. ' +
          'Uma vitória em Fácil ainda não paga moedas. Desistir ainda não paga nada.',
      ],
    },
  },
  {
    id: '2026-09-22-pvp-rewards',
    date: '2026-09-22',
    title: {
      en: 'Bigger PvP rewards',
      es: 'Más recompensa en PvP',
      pt: 'Mais recompensa no PvP',
    },
    items: {
      en: [
        'A PvP win pays **7** coins. A real loss pays **3**. Surrender still pays nothing.',
        'Climbing into a higher league — Duelist to Challenger, and so on — grants ' +
          '**2** shop packs. A division step inside the same league does not.',
      ],
      es: [
        'Una victoria PvP paga **7** monedas. Una derrota real paga **3**. Rendirse ' +
          'sigue sin pagar.',
        'Subir de liga — de Duelista a Retador, y así — da **2** sobres de tienda. ' +
          'Pasar de división dentro de la misma liga no.',
      ],
      pt: [
        'Uma vitória PvP paga **7** moedas. Uma derrota de verdade paga **3**. Desistir ' +
          'ainda não paga nada.',
        'Subir de liga — de Duelista para Desafiante, e assim por diante — dá **2** ' +
          'pacotes da loja. Um passo de divisão dentro da mesma liga não.',
      ],
    },
  },
  {
    id: '2026-09-22-chat-input-mobile',
    date: '2026-09-22',
    title: {
      en: 'The chat box on a phone',
      es: 'La caja del chat en el teléfono',
      pt: 'A caixa do chat no telefone',
    },
    items: {
      en: [
        'On a short screen, the line where you type a chat message stays fully on screen.',
      ],
      es: [
        'En una pantalla baja, la línea donde escribes un mensaje del chat se ve completa.',
      ],
      pt: [
        'Em uma tela baixa, a linha onde você digita uma mensagem do chat fica toda na tela.',
      ],
    },
  },
  {
    id: '2026-09-22-leave-forfeit',
    date: '2026-09-22',
    title: {
      en: 'Leaving a PvP duel',
      es: 'Salir de un duelo PvP',
      pt: 'Sair de um duelo PvP',
    },
    items: {
      en: [
        'Pressing **Back** during a PvP duel ends it. Your opponent wins right away.',
        'A short drop or a refresh still gives you half a minute to come back.',
      ],
      es: [
        'Pulsar **Atrás** durante un duelo PvP lo termina. Tu rival gana al instante.',
        'Una caída breve o un refresh sigue dándote medio minuto para volver.',
      ],
      pt: [
        'Pressionar **Voltar** durante um duelo PvP o encerra. Seu oponente vence na hora.',
        'Uma queda curta ou um refresh ainda dá meio minuto para você voltar.',
      ],
    },
  },
  {
    id: '2026-09-22-fast-win-turn',
    date: '2026-09-22',
    title: {
      en: 'Fast wins follow the Turn number',
      es: 'Las victorias rápidas siguen el número de Turno',
      pt: 'Vitórias rápidas seguem o número do Turno',
    },
    items: {
      en: [
        'Winning a duel in **8 turns or fewer** now uses the **Turn** number on the ' +
          'duel screen. A win on Turn 5 through 8 counts.',
      ],
      es: [
        'Ganar un duelo en **8 turnos o menos** ahora usa el número de **Turno** de la ' +
          'pantalla. Una victoria en el Turno 5, 6, 7 u 8 cuenta.',
      ],
      pt: [
        'Vencer um duelo em **8 turnos ou menos** agora usa o número de **Turno** da ' +
          'tela do duelo. Uma vitória no Turno 5 até 8 conta.',
      ],
    },
  },
  {
    id: '2026-09-22-dragon-capture-jar',
    date: '2026-09-22',
    title: {
      en: 'Dragon Capture Jar no longer stalls the turn',
      es: 'Dragon Capture Jar ya no traba el turno',
      pt: 'Dragon Capture Jar não trava mais o turno',
    },
    items: {
      en: [
        'Setting **Dragon Capture Jar** face-down no longer stops the duel. The game ' +
          'only asks you to flip it when your opponent has a face-up Dragon. You can ' +
          'still flip it yourself any time after the turn you Set it.',
      ],
      es: [
        'Colocar **Dragon Capture Jar** boca abajo ya no detiene el duelo. El juego ' +
          'solo pregunta si la volteas cuando tu rival tiene un Dragón boca arriba. ' +
          'Sigues pudiendo voltearla tú mismo después del turno en que la colocaste.',
      ],
      pt: [
        'Colocar **Dragon Capture Jar** com a face para baixo não para mais o duelo. O ' +
          'jogo só pergunta se você a vira quando o oponente tem um Dragão com a face ' +
          'para cima. Você ainda pode virá-la você mesmo a qualquer momento depois do ' +
          'turno em que a Colocou.',
      ],
    },
  },
  {
    id: '2026-09-22-dice-tap',
    date: '2026-09-22',
    title: {
      en: 'Dice spin, then roll on tap',
      es: 'Los dados giran y se tiran al tocar',
      pt: 'Os dados giram e rolam ao toque',
    },
    items: {
      en: [
        'Before the duel, the dice spin on their own. Tap the screen to roll, or wait ' +
          'and they roll by themselves.',
      ],
      es: [
        'Antes del duelo, los dados giran solos. Toca la pantalla para tirarlos, o ' +
          'espera y se tiran solos.',
      ],
      pt: [
        'Antes do duelo, os dados giram sozinhos. Toque a tela para rolá-los, ou espere ' +
          'e eles rolam sozinhos.',
      ],
    },
  },
  {
    id: '2026-09-22-home-top-players',
    date: '2026-09-22',
    title: {
      en: 'Home card for every ladder',
      es: 'La tarjeta del inicio reúne las escaleras',
      pt: 'O card inicial reúne as ladders',
    },
    items: {
      en: [
        'The home card that opened **Ranked** is now **Top players**. It still leads to ' +
          'the leagues, the level ladder, and the vs AI win ladders.',
      ],
      es: [
        'La tarjeta del inicio que decía **Clasificatoria** ahora dice **Mejores ' +
          'jugadores**. Sigue llevando a las ligas, al nivel y a las victorias vs IA.',
      ],
      pt: [
        'O card da tela inicial que abria **Ranqueada** agora diz **Melhores ' +
          'jogadores**. Continua levando às ligas, à ladder de nível e às ladders de ' +
          'vitórias vs IA.',
      ],
    },
  },
  {
    id: '2026-09-22-waboku-pass',
    date: '2026-09-22',
    title: {
      en: 'Declining Waboku no longer sticks the Battle Phase',
      es: 'Rechazar Waboku ya no traba la Battle Phase',
      pt: 'Recusar Waboku não trava mais a Battle Phase',
    },
    items: {
      en: [
        'Passing on **Waboku** (or any other Set trap) as the Battle Phase starts no ' +
          'longer freezes the phase button. You can move on to Main Phase 2.',
      ],
      es: [
        'Pasar de **Waboku** (o de cualquier otra trampa colocada) al entrar a la ' +
          'Battle Phase ya no congela el botón de fase. Puedes seguir a la Main Phase 2.',
      ],
      pt: [
        'Passar de **Waboku** (ou de qualquer outra armadilha Colocada) ao entrar na ' +
          'Battle Phase não congela mais o botão de fase. Você pode seguir para a Main ' +
          'Phase 2.',
      ],
    },
  },
  {
    id: '2026-09-22-ladder-portraits',
    date: '2026-09-22',
    title: {
      en: 'Portraits on the ladders',
      es: 'Retratos en las escaleras',
      pt: 'Retratos nas ladders',
    },
    items: {
      en: [
        'Every row on **Ranked**, **Level**, and the vs AI win ladders shows that ' +
          'duelist\'s portrait and frame.',
      ],
      es: [
        'Cada fila de **Clasificatoria**, **Nivel** y las escaleras de victorias vs IA ' +
          'muestra el retrato y el marco de ese duelista.',
      ],
      pt: [
        'Cada linha de **Ranqueada**, **Nível** e das ladders de vitórias vs IA mostra ' +
          'o retrato e a moldura daquele duelista.',
      ],
    },
  },
  {
    id: '2026-09-22-ai-win-ladders',
    date: '2026-09-22',
    title: {
      en: 'Win ladders from Hard up',
      es: 'Escaleras de victorias desde Difícil',
      pt: 'Ladders de vitórias a partir de Difícil',
    },
    items: {
      en: [
        '**Ranked** adds a ladder for each CPU difficulty from Hard up. **Hard**, ' +
          '**Expert**, and **Master** show who has the most finished wins on that ' +
          'difficulty.',
      ],
      es: [
        '**Clasificatoria** suma una escalera por cada dificultad de la CPU desde ' +
          'Difícil. **Difícil**, **Experto** y **Maestro** muestran quién tiene más ' +
          'victorias terminadas en esa dificultad.',
      ],
      pt: [
        '**Ranqueada** adiciona uma ladder para cada dificuldade da CPU a partir de ' +
          'Difícil. **Difícil**, **Especialista** e **Mestre** mostram quem tem mais ' +
          'vitórias concluídas naquela dificuldade.',
      ],
    },
  },
  {
    id: '2026-09-22-level-ladder',
    date: '2026-09-22',
    title: {
      en: 'Level ladder',
      es: 'Top de nivel',
      pt: 'Ladder de nível',
    },
    items: {
      en: [
        '**Ranked** has a second ladder: the accounts with the highest level on the ' +
          'server. Open it and switch to **Level**. Vs AI counts toward that ladder too.',
      ],
      es: [
        '**Clasificatoria** tiene una segunda escalera: las cuentas con más nivel en el ' +
          'servidor. Ábrela y cambia a **Nivel**. Vs IA también cuenta para esa escalera.',
      ],
      pt: [
        '**Ranqueada** tem uma segunda ladder: as contas com o nível mais alto no ' +
          'servidor. Abra e mude para **Nível**. Vs IA também conta para essa ladder.',
      ],
    },
  },
  {
    id: '2026-09-22-device-switch',
    date: '2026-09-22',
    title: {
      en: 'Switch a PvP duel from phone to PC',
      es: 'Pasar un duelo PvP del teléfono a la PC',
      pt: 'Passar um duelo PvP do telefone para o PC',
    },
    items: {
      en: [
        'If you leave a **PvP** duel on your phone and open **Duel** on the PC with the ' +
          'same account, that duel comes with you. Your opponent is not left in a match ' +
          'that no longer accepts plays.',
        'A short drop no longer ends the duel on the spot. You have half a minute to ' +
          'come back before it counts as a surrender.',
      ],
      es: [
        'Si sales de un duelo **PvP** en el teléfono y abres **Duelo** en la PC con la ' +
          'misma cuenta, ese duelo te sigue. Tu rival no se queda en una partida que ya ' +
          'no acepta jugadas.',
        'Una caída breve ya no termina el duelo al instante. Tienes medio minuto para ' +
          'volver antes de que cuente como rendición.',
      ],
      pt: [
        'Se você sair de um duelo **PvP** no telefone e abrir **Duelo** no PC com a ' +
          'mesma conta, esse duelo vai com você. Seu oponente não fica em uma partida que ' +
          'já não aceita jogadas.',
        'Uma queda curta não encerra mais o duelo na hora. Você tem meio minuto para ' +
          'voltar antes que conte como desistência.',
      ],
    },
  },
  {
    id: '2026-09-22-hidden-trap-hints',
    date: '2026-09-22',
    title: {
      en: 'Failed plays no longer hint at Set cards',
      es: 'Las jugadas fallidas ya no delatan cartas colocadas',
      pt: 'Jogadas falhas não delatam mais cartas Colocadas',
    },
    items: {
      en: [
        'While your opponent is deciding, the duel no longer says “Not in the Battle ' +
          'Step” or “No response.” Those lines revealed what kind of Set card they were ' +
          'holding.',
      ],
      es: [
        'Mientras tu rival decide, el duelo ya no dice “No estás en el Battle Step” ni ' +
          '“Sin respuesta.” Esas líneas revelaban qué tipo de carta tenía colocada.',
      ],
      pt: [
        'Enquanto seu oponente decide, o duelo não diz mais “Not in the Battle Step” ' +
          'nem “No response.” Essas linhas revelavam que tipo de carta Colocada ele ' +
          'tinha.',
      ],
    },
  },
  {
    id: '2026-09-22-duel-mode-pick',
    date: '2026-09-22',
    title: {
      en: 'Duel asks how you want to play',
      es: 'Duelo pregunta cómo quieres jugar',
      pt: 'Duelo pergunta como você quer jogar',
    },
    items: {
      en: [
        'The **Duel** button in the menu asks whether you want to play against the AI ' +
          'or start a ranked online match.',
      ],
      es: [
        'El botón **Duelo** del menú pregunta si quieres jugar contra la IA o empezar ' +
          'una clasificatoria en línea.',
      ],
      pt: [
        'O botão **Duelo** no menu pergunta se você quer jogar contra a IA ou começar ' +
          'uma partida ranqueada online.',
      ],
    },
  },
  {
    id: '2026-09-22-soul-exchange-end',
    date: '2026-09-22',
    title: {
      en: 'Soul Exchange no longer sticks on End Phase',
      es: 'Intercambio de Almas ya no se queda en la End Phase',
      pt: 'Soul Exchange não trava mais na End Phase',
    },
    items: {
      en: [
        'After **Soul Exchange**, ending your Main Phase passes the turn. The duel no ' +
          'longer sits on your End Phase.',
      ],
      es: [
        'Después de **Soul Exchange**, al terminar tu Main Phase el turno pasa. El ' +
          'duelo ya no se queda en tu End Phase.',
      ],
      pt: [
        'Depois de **Soul Exchange**, ao terminar sua Main Phase o turno passa. O duelo ' +
          'não fica mais parado na sua End Phase.',
      ],
    },
  },
  {
    id: '2026-09-22-deck-save-top',
    date: '2026-09-22',
    title: {
      en: 'Save the deck from the top',
      es: 'Guarda la baraja desde arriba',
      pt: 'Salve o baralho pelo topo',
    },
    items: {
      en: [
        'In the deck editor, **Save** sits at the top. On a phone, rename the deck from ' +
          'the deck list. On a larger screen, the name field stays next to Save.',
      ],
      es: [
        'En el editor, **Guardar** queda arriba. En el teléfono, el nombre se cambia en ' +
          'la lista de barajas. En una pantalla grande, el campo del nombre sigue junto a ' +
          'Guardar.',
      ],
      pt: [
        'No editor de baralho, **Salvar** fica no topo. No telefone, renomeie o baralho ' +
          'na lista de baralhos. Em uma tela maior, o campo do nome continua ao lado de ' +
          'Salvar.',
      ],
    },
  },
]

/**
 * Formats a release day for the current language (month name, not ISO).
 */
export function formatChangelogDate(iso: string, locale: AppLocale): string {
  const [year, month, day] = iso.split('-').map(Number)
  const date = new Date(Date.UTC(year, (month ?? 1) - 1, day ?? 1))
  return date.toLocaleDateString(intlLocale(locale), {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  })
}

/** How long a release stays "new" for the desktop dialog and the pending dot. */
export const CHANGELOG_FRESH_DAYS = 3

/** Releases duelists see. Admin-panel notes stay out. */
export function playerChangelogReleases(): ChangelogRelease[] {
  let done: readonly string[] = []
  try {
    done = useSiteStore().resetDone
  } catch {
    done = []
  }
  return CHANGELOG_RELEASES.filter((release) => {
    if (release.ops) return false
    if (release.afterReset && !done.includes(release.afterReset)) return false
    return true
  })
}

/** Whole calendar days since `iso` (YYYY-MM-DD), in the player's local date. */
export function changelogAgeDays(iso: string, today = new Date()): number {
  const [year, month, day] = iso.split('-').map(Number)
  if (!year || !month || !day) return Number.POSITIVE_INFINITY
  const release = Date.UTC(year, month - 1, day)
  const now = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate())
  return Math.round((now - release) / 86_400_000)
}

/** Player notes from the last {@link CHANGELOG_FRESH_DAYS} calendar days. */
export function freshChangelogReleases(today = new Date()): ChangelogRelease[] {
  return playerChangelogReleases().filter((release) => {
    const age = changelogAgeDays(release.date, today)
    return age >= 0 && age < CHANGELOG_FRESH_DAYS
  })
}

/**
 * Fresh notes newer than `seenId` (newest first). A missing id means none of
 * them have been opened on the news page yet.
 */
export function unseenFreshReleases(
  seenId: string | null,
  today = new Date(),
): ChangelogRelease[] {
  const fresh = freshChangelogReleases(today)
  if (!seenId) return fresh
  const all = playerChangelogReleases()
  const seenAt = all.findIndex((release) => release.id === seenId)
  if (seenAt < 0) return fresh
  const newer = new Set(all.slice(0, seenAt).map((release) => release.id))
  return fresh.filter((release) => newer.has(release.id))
}

export interface ChangelogTextPart {
  bold: boolean
  text: string
}

/** Splits `**bold**` markers. The source text is never treated as HTML. */
export function changelogSegments(text: string): ChangelogTextPart[] {
  const parts: ChangelogTextPart[] = []
  const pattern = /\*\*([^*]+)\*\*/g
  let last = 0
  let match: RegExpExecArray | null
  while ((match = pattern.exec(text))) {
    if (match.index > last) {
      parts.push({ bold: false, text: text.slice(last, match.index) })
    }
    parts.push({ bold: true, text: match[1] ?? '' })
    last = match.index + match[0].length
  }
  if (last < text.length) parts.push({ bold: false, text: text.slice(last) })
  return parts
}
